<template>
<div class="chapter">
<div class="inner_page">
<h1>The crime at Vanderlynden's</h1>
<p class="pcalibre drop-cap">HIGH up in the pale Flemish sky aeroplanes
were wheeling and darting like
bright-coloured insects, catching from
one moment to another the glint of sun on
metallic body or translucent wing. To any pilot
or observer who had opportunity or gift for mere
speculation, the sight that lay spread out below
might have appeared wonderful. From far away
on the seaboard with its coming and going of
ships, there led rail, road, and wire, and by these
three came material, human material, and human
thought, up to that point just behind the battle-line
where in dumps, camps (dumps of men) and
Head-quarters (dumps of brains) they eddied a
little, before streaming forward again, more slowly
and covertly, by night, or below ground, up to
the battle itself. There they were lost in that
gap in life—that barren lane where the Irresistible
Force dashing against the Immovable Post ground
such a fine powder, that of material, very little,
of men, very few, and of thought, nothing came
splashing back.</p>
<p class="calibre3">But pilots and observers were too busy, adding
to the Black Carnival, or saving their own skins
from those puffs of Death that kept following them
up and down the sky, to take any such a remote
view; and even had they been interested in<span id="Page_2" title="[2]"></span>
it, they could not have lifted the roof off the Mairie
of the village—almost town—of Haagedoorne,
and have seen, sitting in the Mayor’s parlour, a
man of middle size and middle class, a phenomenon
in that place, that had been shocked in its
village dignity so many times in those few months.
For first it had been turned from one of those
haunts of Peace, of small slow-moving officialdom,
into the “Q.” office of Divisional Head-quarters.
It had become inhabited by two or three English
Staff Officers, their maps and papers, their orderlies
and clerks, policemen and servants; and
now, last of all, there was added to them this
quiet, absorbed young man—whose face and hair,
figure and clothes had all those half-tones of
moderate appropriateness of men who work indoors
and do not expect too much. A young man
who had neither red tabs nor long boots about
him—and who seemed to have so much to do.</p>
<p class="calibre3">The old walls stared. The Mairie of Haagedoorne,
half wine-shop, half beadle’s office, had
seen soldiers in its four hundred years, had been
built for Spanish ones, and had seen them replaced
by French and Dutch, English and Hessians, in
bright uniforms and with a certain soldierly idleness
and noise. This fellow had none of it.
Sat there with his nose well down, applying
himself to maps and papers, occasionally speaking
deferentially to Colonel Birchin, who, a proper
soldier, his left breast bright with medals, his
face blank and slightly bored with breeding,<span id="Page_3" title="[3]"></span>
would nod or shake his head. This was all part of
the fact that this War was not as other wars. It
was too wide and deep, as if the foundations of
life had come adrift on some subterranean sea,
and the whole fabric were swaying; it had none
of the decent intervals, and proper limits, allowing
men to shut up for the winter and to carry on
their trade all the time.</p>
<p class="calibre3">The dun-coloured person attached to Divisional
Staff, whose name was Stephen Doughty Dormer,
indulged in none of these reflections. He just
got on with it. He was deep in his job when an
exclamation from his temporary Chief made him
look up. The Colonel was sitting back in his
chair (iron-bottomed, officers, for the use of),
his beautiful legs in their faultless casings stretched
out beneath his army table. He was holding at
arm’s-length a blue printed form, filled up in
pen and ink.</p>
<p class="calibre3">Dormer knew it well. It was the official form
on which Belgian or French civilians were
instructed to make their claim for damages caused
by the troops billeted on them.</p>
<p class="calibre3">The Colonel’s mouth hung open, his eyeglass
had dropped down.</p>
<p class="calibre3">“You speak this—er—language?”</p>
<p class="calibre3">“Yessah!” (with a prayer it might not be
Portuguese). “What language, sir?”</p>
<p class="calibre3">“This is—er—French.”</p>
<p class="calibre3">Yes, he could speak French, and hastened to
look. Dormer was a clerk in a bank. Like so<span id="Page_4" title="[4]"></span>
many of that species, he had had a grandmother
with views as to the improvement of his position
in the world, and she had insisted that he should
learn the French language. Why she desired
this was never discovered, unless it was that she
considered it a genteel accomplishment, for she
dated from the days when society was composed
of two sorts of people, gentle and simple. She
belonged to the former category and was in no
danger of allowing any of her descendants to
lapse. As she paid for the extra tuition involved,
her arguments were irrefutable, and the boy
intended for no more romantic a career than is
afforded by a branch office in a market town, had,
in 1900, a fair knowledge of the tongue of Voltaire
and Hugo.</p>
<p class="calibre3">He hardly reflected upon the matter again
until, in the midst of a European War, he found
that that War was being conducted in a country
where French was the chief language, and that
familiar-sounding words and phrases assailed his
ear on every side. This was of considerable
service to him, enabled him to add to his own
and his brother officers’ comfort; but he never
boasted of it, having a profound uncertainty, after
years of clerkdom, about anything so foreign and
out of office hours. The legend of his peculiar
ability persisted, however; and when after more
than a year of incredible fatigues and nastiness,
his neat methods and perfect amenity to orders
were rewarded by the unofficial job of helping<span id="Page_5" title="[5]"></span>
in the A. and Q. office of a division, he found his
legend there before him. It was therefore with
a sigh, and a mental ejaculation equivalent to
“Spare me these useless laurels,” that he got up
and went over to his Chief’s table, to be confronted
by the sentence:</p>
<p class="calibre3">“<i class="calibre9">Esquinté une vierge chez moi!</i>”</p>
<p class="calibre3">“What’s <i class="calibre9">Esquinté</i>? It’s not in Cassells’
Dictionary!”</p>
<p class="calibre3">“I should say—knocked asquint, sir!
Spoiled, ruined; they often say it, if the troops
go into the crops.”</p>
<p class="calibre3">“Well, how does it read, then? Knock
asquint; no, that won’t do; ruined, you say.
Ruined a Virgin in my house. This sounds like
a nice business, with the French in their present
mood!”</p>
<p class="calibre3">Dormer simply could not believe it and asked:</p>
<p class="calibre3">“May I see the claim?”</p>
<p class="calibre3">“Certainly. Come here. Stop me wherever
I go wrong.”</p>
<p class="calibre3">He knew more French than Dormer gave him
credit for. He read the blue form, printed
question and pen-and-ink reply to the end. It
went like this:</p>
<p class="calibre3">Q. When was the damage committed?</p>
<p class="calibre3">A. Last Thursday.</p>
<p class="calibre3">Q. What troops were responsible? Give the
number and name of the English detachment.</p>
<p class="calibre3">A. A soldier of the 469 Trench Mortar
Battery (T.M.B.).</p>
<p class="calibre3"><span id="Page_6" title="[6]"></span>Q. Were you present and did you see the
damage done?</p>
<p class="calibre3">A. No, but my daughter knows all about it.</p>
<p class="calibre3">Q. In what conditions was the damage done?</p>
<p class="calibre3">A. He broke the window (<i class="calibre9">vitrage</i>). She called
out to him, but he replied with oaths.</p>
<p class="calibre3">Q. Can you prove responsibility (<i class="calibre9">a</i>) by
witness?</p>
<p class="calibre3">A. My daughter.</p>
<p class="calibre3">Q. (<i class="calibre9">b</i>) By <i class="calibre9">procès-verbal</i>.</p>
<p class="calibre3">A. They insulted the Mayor when he came
to do it!</p>
<p class="calibre3">Q. (<i class="calibre9">c</i>) By admission of the culprits.</p>
<p class="calibre3">A. Not necessary. It is visible.</p>
<p class="calibre3">Q. Did you complain to the officer commanding
troops?</p>
<p class="calibre3">A. He would not listen.</p>
<p class="calibre3">And so on.</p>
<p class="calibre3">Deposed and sealed at the Mairie of Hondebecq,
Nord, as the claim for compensation of
Mr. Vanderlynden, cultivator, 64 years old, by
us Swingadow, Achille, Mayor.</p>
<p class="calibre3">“What do you say to that?” asked the Chief.</p>
<p class="calibre3">Dormer had a good deal to say, but kept it
down. “I can’t believe it, sir. I know the
billet. I remember Miss Vanderlynden. She’s
as strong as a man and much more determined
than most. It’s a mistake of some sort!”</p>
<p class="calibre3">“Pretty circumstantial mistake, isn’t it?
Look at this covering letter received with it.”</p>
<p class="calibre3">He held out a memorandum headed: “Grand<span id="Page_7" title="[7]"></span>
Quartier General,” in French, to the effect that
one desired it might be given appropriate attention.
And another from a department of English
General Head-quarters with “Passed to you,
please.”</p>
<p class="calibre3">“The French have had their knife into us for
some time. This’ll be a nice case for them to
take up. We must make an arrest at once.
Sergeant!”</p>
<p class="calibre3">That Sergeant was a famous London Architect.
He came to the door of the ante-room in
which he worked.</p>
<p class="calibre3">“In what Corps Area is Hondebecq?”</p>
<p class="calibre3">The Sergeant spotted it in a moment, on the
big map pinned up on the wall.</p>
<p class="calibre3">“Very well, wire them to take this up, and
make an arrest.”</p>
<p class="calibre3">“There is just one point I should like to put,
sir!”</p>
<p class="calibre3">As Dormer said it, he felt it to be “cheek.”
His Chief turned upon him the eyeglass of a
regular officer who found it rather difficult to
imagine how a junior temporary officer could put
a point. But Dormer had seen two Courts-Martial,
and the thought of some poor brute
hauled out of a trench, and marched about for no
better purpose than that, kept him firm.</p>
<p class="calibre3">“If an arrest is made, you will have to go on
with the proceedings.”</p>
<p class="calibre3">“Naturally.”</p>
<p class="calibre3">“Then you will need a statement from the<span id="Page_8" title="[8]"></span>
victim. If we had that first, we should know the
truth!”</p>
<p class="calibre3">“Well, you’d better go and get it, as you know
the people. You can see Corps and insist on an
arrest. But, most important of all, try what a
little money can do. He says a thousand francs.
Well, you must see what he will come down
to.”</p>
<p class="calibre3">Outside Divisional Head-quarters, Dormer
turned to the right, to go to his billet, but a
military policeman, stepping out from the shelter
of the buildings, saluted.</p>
<p class="calibre3">“They’re shelling that way, sir!”</p>
<p class="calibre3">It gave Dormer a queer familiar feeling in the
pit of the stomach. Shelling, the daily routine
of that War. But being a very punctilious temporary
officer, and taking his almost non-existent
position in Divisional Staff very seriously, he
pulled himself together.</p>
<p class="calibre3">“Oh, well, they’d have hit me long ago, if
they could!” He passed on, followed by a
smile. He said those things because he felt
them to be good for the morale of the troops.
Sure enough, he had not gone many yards before
the air was rent by a familiar tearing sound,
followed by the usual bump and roar. It was
well in front of him, and to the left, and he went
on reassured. A few yards farther on, close to
the side street where he was billeted over a
pork-butcher’s shop, he noticed people coming
out of their houses and shops to stare, while one<span id="Page_9" title="[9]"></span>
elderly woman, rounder than any artist would dare
to portray, asked him:</p>
<p class="calibre3">“O Monsieur, is the bombard finished?” in
the Anglo-Flemish which years of billeting were
beginning to teach the inhabitants of the town.
But the centre of excitement was farther on,
where the little street of houses petered out
between small, highly cultivated fields. Here
the first shell had fallen right upon one of those
limbers that were to be found being driven
up some obscure street at any hour of the day
or night. Two dazed drivers had succeeded in
cutting loose and quieting the mules. A horse
lay dead in the gutter. Against the bank leaned
the Corporal, his face out of sight, as if in the
midst of a hearty laugh. It needed only a
glance, however, to see that there was no head
upon the shoulders. It was just one of those
daily disagreeable scenes which to Dormer had
been so utterly strange all his life, and so familiar
for the last year. Dormer made no fuss, but took
charge. He knew well enough that the drivers
would stand and look at each other. He sent
one of them for a burial party from the nearest
Field Ambulance, saw that the other tied up the
mules and made a bundle of the dead man’s
effects—paybook, knife, money, letters—the pitiful
little handkerchief-ful of all that remains for
a soldier’s loved ones—while he himself pushed
his way into the orderly room of the nearest
formation, that showed any signs of telephone<span id="Page_10" title="[10]"></span>
wires. He had not many yards to go, for the
camps lay along each side of that Flemish lane,
as close as houses in a street.</p>
<p class="calibre3">He was soon inside an Armstrong hut, with
the field telephone at his disposal, and while
waiting to be given the orderly room of the
Brigade Transport to which the casualty belonged,
he happened to close his eyes. The effect was
so striking that he immediately opened them
again. There, on the underside of his eyelids,
was the headless body he had just left. Curiously
enough, it did not lie against the bank, as he
had seen it, but seemed to swim towards him,
arms above his head, gesticulating. Once his
eyes were open again, of course it disappeared.</p>
<p class="calibre3">About him was nothing more wonderful than
the interior of an Armstrong hut Orderly Room,
an army table, an army chair. Some one’s bed
and bath shoved in a corner. Outside, trampled
mud, mule-lines, cinder tracks, Holland elms,
flat, stodgy Flanders all desecrated with War.
He got the number he wanted, told the Brigade
to fetch their broken limber, gave his rank and
job, and put up the telephone. The impression
he had had was so strong, however, that walking
back along the cinder path, he closed his eyes
again. Yes, it was still there, quite plain, the
details of the khaki uniform all correct and clear
cut, spurred boots and bandolier, but no head, and
the arms raised aloft, exhorting or threatening.</p>
<p class="calibre3">If he went on like this he would have to see a<span id="Page_11" title="[11]"></span>
Medical Officer, and they would send him down
to the Base, and he would find his job filled up,
and have to go elsewhere and start all over fresh,
trying to do something that was not desperately
boring or wholly useless. He had been doing too
much, going up at night for “stunts,” and working
in Q. office all day. He would have to slack
off a bit.</p>
<p class="calibre3">By the time he got back to Divisional H.Q.
the car stood ready. The feelings of one who,
having been hauled out of the infantry, had then
to return to the Forward Areas, were curiously
mixed. Of course no one wanted to be shelled
or bombed, to live where the comforts of life
were unpurchasable, and the ordinary means of
locomotion out of use. And yet—and yet—there
was a curious feeling of going home. That
great rowdy wood and canvas and corrugated
iron town, miles deep and nearly a hundred
miles long, was where one belonged. That
atmosphere of obvious jokes and equally obvious
death, disinfectant, tobacco, mules, and wood
smoke had become one’s life, one’s right and
natural environment.</p>
<p class="calibre3">His companion on this joyless ride was Major
Stevenage, the A.P.M. of the Division, an ex-cavalry
officer of the regular army, in appearance
and mentality a darker and grimmer edition of
Colonel Birchin.</p>
<p class="calibre3">Dormer showed him the Vanderlynden dossier
as they bowled along. He surveyed it with the<span id="Page_12" title="[12]"></span>
weariness of a professional to whom an amateur
exhibits a “masterpiece.”</p>
<p class="calibre3">“Colonel Birchin thinks it’s rape, does he?”</p>
<p class="calibre3">“Yes!”</p>
<p class="calibre3">“He’s wrong, of course. Q. office always
are! What do you think it is yourself?”</p>
<p class="calibre3">“A nasty snag. What happened doesn’t
matter. You and I could settle it for forty
francs. But the French have got hold of it.
It’s become official.”</p>
<p class="calibre3">“What do you suggest?” Major Stevenage
put in his monocle.</p>
<p class="calibre3">“We must go and see the Maire, and get
it withdrawn. Let’s see. Hondebecq? It’s
the Communal Secretary Blanquart we must see.
Shrewd fellow and all on our side. These schoolmasters
hate the peasants.”</p>
<p class="calibre3">Dormer knew the area well. Hondebecq
was the typical village of French Flanders.
That is to say, it was a cluster of cottages in
which <i class="calibre9">rentiers</i>—peasants who had scraped a few
savings out of the surrounding fields—lived on
about forty pounds a year English; in its centre,
a paved <i class="calibre9">grand’ place</i> held a few modest shops, a
huge high-shouldered church, carefully refaced
with red brick, and a big, rambling “Estaminet
de la Mairie,” next to the village school.</p>
<p class="calibre3">It was here that they found Blanquart, Communal
Secretary, schoolmaster, land surveyor,
poor man’s lawyer, Heaven only knows what
other functions he used to combine. He was the<span id="Page_13" title="[13]"></span>
only man in the Commune handy with pen and
paper, and this fact must have substantially
added to his income. But, like all his kind, he
could not forget that he came from Dunkirk
or Lille; he had moments when his loneliness
got the better of his pride and he would complain
bitterly of the “sacred peasants.”</p>
<p class="calibre3">They found him seated in his little front
parlour—he only functioned in the official room
at the Estaminet on State occasions—busy with
those innumerable forms by which the food of
France was rationed, her Army conscripted, her
prices kept in check and her civil administration
facilitated. In the corner between the window
and the clock sat an old peasant who said only,
“<i class="calibre9">Bonjour.</i>”</p>
<p class="calibre3">Blanquart greeted them effusively, as who
should say: “We others, we are men of the
world.” He made polite inquiries about the
officers’ health and the weather and the War,
leading up to the introduction: “Allow me to
present you to Mister our Mayor! And now
what can I do for you?”</p>
<p class="calibre3">Major Stevenage, a little lost in the mixed
stream of good French and bad English, left it
to Dormer.</p>
<p class="calibre3">“It is with reference to the claim of Vanderlynden!
Can one arrange it?”</p>
<p class="calibre3">Blanquart had only time to put in: “Everything
arranges itself,” before the Mayor cut him
short.</p>
<p class="calibre3"><span id="Page_14" title="[14]"></span>“You have some nice ideas, you others.
Arrange it, I believe you. You will arrange it
with our Deputy.”</p>
<p class="calibre3">Blanquart put in: “Mister the Mayor was
insulted by the troops. We wrote to our
Deputy!”</p>
<p class="calibre3">Major Stevenage fidgeted. He had found it
most difficult to go through this sort of thing,
day after day, for years. He had been trained
to deal with Asiatics. He turned on Blanquart:</p>
<p class="calibre3">“Why didn’t you write to me first?” but the
Mayor cut in again. His general outlook on
life was about that of an English agricultural
labourer plus the dignity of Beadledom. This
latter had been injured, and the man, who seldom
spoke a dozen sentences a day, now was voluble.
He understood more English than one gave him
credit for.</p>
<p class="calibre3">“Why write to you, officer, you are all of the
same colour!” (By this time not a German
attack could have stopped him.) “My Garde
Champêtre comes to tell me that there is a crime
of violence at Vanderlynden’s. They demand
that I go to make <i class="calibre9">procès-verbal</i>. I put on my
tricolour sash. I take my official notebook. I
arrive. I demand the officer. <i class="calibre9">Il s’est foutu de
moi!</i> (Untranslatable.) He says he has orders
to march to the trenches. His troops hold me
in derision. They sing laughable songs of me
in my official capacity——”</p>
<p class="calibre3">“It is very well, Monsieur the Maire,”<span id="Page_15" title="[15]"></span>
Dormer broke in. “We go to make an arrestation.
Can you indicate the culpable?”</p>
<p class="calibre3">“But I believe you, I can indicate him,” cried
the old man.</p>
<p class="calibre3">Dormer waited breathlessly for some fatal
name or number which would drag a poor wretch
through the slow exasperation of Court-Martial
proceedings.</p>
<p class="calibre3">“It was a small brown man!”</p>
<p class="calibre3">“That does not lead us very far!” said Dormer
icily.</p>
<p class="calibre3">“Wait!” The old man raised his voice.
“Achille!” The door opened, and Achille
Quaghebeur, the Garde Champêtre, in attendance
on the Maire, stepped in and closed it
behind him. He had, in his dark green and
sulphur-coloured uniform, with his assumption of
importance, the air of a comic soldier out of
“Madame Angot.” “Produce the corroborative
article!”</p>
<p class="calibre3">Achille found in his tail pocket surely the oldest
and most faded of leather pocket-books. From
this in turn he produced a piece of A.S.C. sacking,
on which the word OATS was plainly
printed in black.</p>
<p class="calibre3">“Voila!” said the Maire.</p>
<p class="calibre3">“Totally useless!” growled the Major, turning
red.</p>
<p class="calibre3">This made the Maire furious; he grasped the
intonation and expression if not the words.</p>
<p class="calibre3">“You others, you are enough to send one to<span id="Page_16" title="[16]"></span>
sleep standing up. One produces the <i class="calibre9">corroborative</i>
pieces and you treat them as useless.” And
there followed a tirade during which Dormer
drew the Major outside, with profuse <i class="calibre9">Bonjours!</i>
He thought that Blanquart was trying to sign to
him that he wanted to say something to him
privately. But the Major was upset, his dignity
was hurt. A soldier by profession, he had
reduced the settlement of claims to a fine art.
He was said to have settled three thousand between
the time he was made A.P.M. to the division
on the Aisne to the day of his death at Bailleul.
He told the chauffeur to drive to Vanderlynden’s.
The man seemed to know the way, and had probably
been to the place many times. As the car
jolted and ground over the cobbles into the yard,
Dormer said:</p>
<p class="calibre3">“I shall ask for the daughter, Madeleine.”</p>
<p class="calibre3">“Just so!”</p>
<p class="calibre3">“I don’t believe——”</p>
<p class="calibre3">“Nor do I,” said the Major stoutly.</p>
<p class="calibre3">Neither of them could pronounce the word
“rape.”</p>
<p class="calibre3">They got out, knocked at the door and knocked
again. The place seemed not so much empty
and deserted as enveloped in one of those encompassing
noises that only sort themselves out on
investigation. Too deep for a separator, too
near for an aeroplane, Dormer diagnosed it:
“They’ve got the Government thrasher in the
back pasture, next the rye!” (He had a good<span id="Page_17" title="[17]"></span>
memory and could tell pretty well how most of
the people distributed crops and work.)</p>
<p class="calibre3">They recrossed the bridge of the moat and
skirting the latter entered the back pasture.
There against the gate that gave on to the arable
“plain,” as it was called in those parts, was the
Government thrasher, the women labourers, and
right on the top of the stack, old Vanderlynden.</p>
<p class="calibre3">Dormer shouted! Vanderlynden paid not
the slightest heed. Perhaps he was deaf, no
doubt the thrasher buzzed loud enough; but
above all he was one of those old peasants whose
only reply to this unheard-of War in which all had
been plunged was to work harder and more
continuously, and to show less and less consciousness
of what went on round about them.
There he stood, black against that shy and
tender blue of Flemish sky, the motions of his
body mechanical, his face between collarless shirt
and high-crowned, peaked cap, expressionless.
Finally, Dormer took one of the short stout girls
that were employed in raking the straw away from
the travelling band, and shook her roughly by the
arm.</p>
<p class="calibre3">She was, of course, a refugee Belgian. No one
else would work like that, not even a Chinese
woman. Like a clockwork figure, she began to
speak in “English”:</p>
<p class="calibre3">“No bon offizer billet all full you go Mairie!”
without stopping for one moment her raking.</p>
<p class="calibre3">Dormer held her forearm rigid, and stopped her.</p>
<p class="calibre3"><span id="Page_18" title="[18]"></span>“<i class="calibre9">Saagte patron heer t’kom!</i>”</p>
<p class="calibre3">That reached her consciousness. Throwing
down her implement, she put both hands to her
mouth and began shouting “Hoi!” at old
Vanderlynden, and might have gone on shouting
indefinitely if Dormer had not gone round to the
French Army mechanic who drove the machine
and given him an English canteen cigarette.
That would have stopped an offensive. It soon
stopped the thrasher and Vanderlynden looked
down at his visitors.</p>
<p class="calibre3">“Good day, Patron!” called Dormer; “can
we see Mademoiselle?”</p>
<p class="calibre3">The old man got down with unexpected agility.
“Good day, my officer, what is it that there is?”</p>
<p class="calibre3">Dormer held out the blue claim form. At the
sight of it, there came into Vanderlynden’s face
the look that a mule gives its feed, when, expecting
and even enjoying bits of wood, leather, and
nails, it comes across a piece of tin: not so much
protest as long and malevolent calculation of the
unknown. As a matter of fact he could not read
more of it than his signature. He muttered once
or twice, “<i class="calibre9">myn reclamorsche</i>,” but got no further.</p>
<p class="calibre3">“Can we see Mademoiselle?” repeated
Dormer.</p>
<p class="calibre3">The old man stared at him with the incredulity
of a villager who finds a stranger ignorant of
village news: “But, my officer, my young lady
is gone!”</p>
<p class="calibre3">At that moment the French mechanic, who had<span id="Page_19" title="[19]"></span>
lighted his cigarette and now only wanted to be
done with the job, put his lever over, and set the
thrasher buzzing again. As if spell-bound, old
Vanderlynden gave one leap and regained his
place on the stack. The Belgians fell to at their
several jobs. The corn flew, the wheels whizzed,
the grain rattled in the hopper, the straw swished
in long swathes beneath the rakes. Dormer
and the Major were left standing, idle and forgotten,
with their War, while the real business of
the farm went steadily forward, only a little
hastened because the thrasher had to be at
Watten next day.</p>
<p class="calibre3">They walked back to the car, in a black frame
of mind. Neither spoke, from war habit of not
mentioning the omnipresent perversity of things.
But Madeleine Vanderlynden’s departure from
the farm, coming after the wording of the claim,
was ominous indeed.</p>
<p class="calibre3">Travelling by motor has many disadvantages,
but against all these it has one crowning advantage:
to those who are weary and overspent, it
provides more immediately and completely than
any other physical sensation the feeling of escape.
What magic lies behind that word! To get
into the car and go, no matter whither, and to
leave at any rate one incomprehensible muddle
behind him: that was the illusion while the
chauffeur was starting.</p>
<p class="calibre3">No farther off than the gate of the pasture,
swaying at slowest speed over the unevenness of<span id="Page_20" title="[20]"></span>
the entry, the car stopped. A motor-cyclist
slithered up beside it, saluted the A.P.M. and
produced one of those scores of messages that
fluttered about just beyond the end of the field
telephone. Dormer might have passed unknown,
but the A.P.M. was unmistakable. Having
handed over the flimsy envelope, the pocket
Hermes threw his leg over the saddle of the
gibbering machine that carried him, and was
away up the lane and out on to the <i class="calibre9">pavé</i> road,
out of sight before the A.P.M. could get out the
words “No answer.”</p>
<p class="calibre3">The A.P.M. sat frowning at the pink Army
message form. The chauffeur sat frowning, one
hand on the wheel, his foot keeping the engine
going by light continual touches on the accelerator,
his face screwed round to catch the order to
proceed. The Sergeant of police sat perfectly
still and impassive, looking before him, the sunlight
glinting on the tiny fair hairs of his clipped
moustache. The cyclist had gone, the chauffeur
wanted to go, and, after a moment, quietly
slipped into first gear and let the car gently
gather way. The policeman did not have to
want. He had simply to sit still and his morning
would pass as his other mornings did, in passively
guarding law and order in the organization of
the British Armies in France and Flanders. It
was not until the car was already moving at
more than walking pace that the A.P.M. spoke,
and Dormer got the queerest sensation from the<span id="Page_21" title="[21]"></span>
sequence of such small events. For the first time
it seemed to him that the A.P.M. was not in
possession of the initiative. It was these private
soldiers, waiting, coming and going, that forced
him to give an order. The impression lasted
only a moment, but it was disturbing. Decidedly,
Dormer felt, he was not well, having such notions.
Then he had no more time to think, for the
A.P.M. was holding out the pink wire for him to
read. He read:</p>
<p class="calibre3">“Corps requires signed statement of withdrawn
claim.” The illusion of escape was gone. The
botheration was not behind, it was ahead of them.</p>
<p class="calibre3">“No use saying she isn’t there. We shall
have to concoct something.” He was obviously
waiting for Dormer to suggest.</p>
<p class="calibre3">“I think, sir, we might go back to Blanquart,
and find out the girl’s whereabouts. The Maire
will be gone by now!”</p>
<p class="calibre3">“Thank goodness. To Hondebecq Mairie.”
The car flew from second to top speed.</p>
<p class="calibre3">Back at the Grand’ Place of the village, the car
stopped, the chauffeur folded his hands, at the
order to wait, the A.P.M. and Dormer entered
the Estaminet. It was empty, as Dormer had
foreseen. The Maire and his Secretary were not
people who had time to waste, and were both
gone about their jobs—the Maire to his farm, the
clerk to his school, the classes of which were plainly
audible through the wall, grinding out some lesson
by heart, in unison, like some gigantic gramophone<span id="Page_22" title="[22]"></span>
with a perpetual spring. It was the hour at which
all France prepares for its substantial meal.</p>
<p class="calibre3">Outside, the Grand’ Place was empty, save for
the sunshine, not here an enemy, as farther
south, but the kindly friend that visits the coasts
of the North Sea all too rarely, wasting its pale
and tepid gold on the worn stones, on the green-shuttered,
biscuit-coloured façades of substantial
two-storied houses, with steep roofs and
tall chimneys, behind which protruded the
summits of ancient Holland elms. For a long
while there was no movement, save the flutter of
a straw caught in the cobbles. The A.P.M.
fidgeted. There was no sound but the classes
next door, the wind in the street, the faint tremor
of the window-panes, in response to some distant
inaudible shelling.</p>
<p class="calibre3">“You wouldn’t think there was a war going
on within twenty miles?”</p>
<p class="calibre3">“Twenty kilometres, sir!”</p>
<p class="calibre3">“Is it possible? Are we going to wait all
day, Dormer?”</p>
<p class="calibre3">“No, sir, only a moment; the people of the
house can’t be far off, but the door behind the
bar is locked. I don’t want to go into the school
myself, Blanquart won’t like it, and one wants to
keep on the right side of him.”</p>
<p class="calibre3">“Why won’t he like it? He’ll have to.”</p>
<p class="calibre3">“The children get out of hand, sir, at the sight
of a uniform. I’ve noticed it when I’ve been
billeting.”</p>
<p class="calibre3"><span id="Page_23" title="[23]"></span>“Do they?”</p>
<p class="calibre3">“Yes, sir; it’s all fun to them still.”</p>
<p class="calibre3">“Is it?” The A.P.M. grimaced and began
reading the signs over the little shops:
“<i class="calibre9">Charcuterie</i>—what’s that?”</p>
<p class="calibre3">“Baked-meat shop. Pork-butcher’s we should
call it, sir!”</p>
<p class="calibre3">“<i class="calibre9">Quincaillerie.</i>”</p>
<p class="calibre3">“Hardware!”</p>
<p class="calibre3">“Who’s this, coming across the square?”</p>
<p class="calibre3">“Belgian refugee, sir!”</p>
<p class="calibre3">Dormer had no doubt about it. The heavy
round-shouldered figure, the mouth hanging
loosely open, the bundle carried under the arm,
the clumsy boots, the clothes apparently suspended
round the waist by a string. Her story was
written all over her: turned out of some Walloon
or Flemish farm or town, at the approach of the
Germans—tramping along a road with a retreating
army all mixed up with a nation on the move,
she had lost home, parents, occupation, all in a
few hours, and was glad to get board and bed and
any odd job that she could do.</p>
<p class="calibre3">“Is this the sort of person we have to interview?”</p>
<p class="calibre3">“Oh no, sir. Different type!”</p>
<p class="calibre3">The woman showed some mild interest at the
sight of the car, and exchanged banter in pidgin
English with the chauffeur and policeman. The
invitation from the latter “promenade,” and the
smiling, flattered refusal “promenade no bon!”<span id="Page_24" title="[24]"></span>
could be heard. Then she entered and stood
before them.</p>
<p class="calibre3">“<i class="calibre9">Bonjour</i>, offizer, what you want?”</p>
<p class="calibre3">“Will you kindly tell the Maire’s Secretary
one waits to see him.”</p>
<p class="calibre3">“You want billets?” in English. “Billets
na poo!”</p>
<p class="calibre3">“No!” Dormer was always piqued when
his French was disregarded or misunderstood.
“We want M. Blanquart!”</p>
<p class="calibre3">“All right.” She returned with him in a
moment.</p>
<p class="calibre3">“M. Blanquart, we have been to the farm and
seen Vanderlynden. He’s very busy, and we
didn’t get much out of him, but we gather his
daughter has left home. Do you know her
address?”</p>
<p class="calibre3">A look of incredulity visited the face of the
schoolmaster. He pointed across the square.
“There. She has taken the ‘Lion of Flanders.’
She gives lunch to officers!”</p>
<p class="calibre3">When this was conveyed to the A.P.M. he was
considerably annoyed. “Why couldn’t that old
fool Vander what’s-his-name say so?”</p>
<p class="calibre3">Blanquart understood perfectly, not only the
words, but the feeling. “Ah, Monsieur, there
you have the peasant. I have lived among them
all my life. I am not of them, I am from St.
Omer, but I know them well. They are like that.
They are thrashing. They are sowing. They
cannot attend to anything else, even if it be their<span id="Page_25" title="[25]"></span>
own business. You and I shall be treated like the
weather, something to be used or avoided....”</p>
<p class="calibre3">But the A.P.M. had stepped out of the
Estaminet de la Mairie. Dormer lingered, just
sufficiently to say:</p>
<p class="calibre3">“We are much obliged, M. Blanquart, we will
attend to the affair.” For he had been brought
up to behave as a little gentleman and knew that
politeness cost nothing and that he might require
the Secretary of the Mairie again.</p>
<p class="calibre3">Outside, the chauffeur was busy underneath
the car, the policeman stood beside it, legs apart,
hands clasped behind his back, face expressing
absolutely nothing. In a few strides Dormer
caught up to the A.P.M.</p>
<p class="calibre3">“This lady speaks good English, sir. No
doubt you will conduct the inquiry yourself?”</p>
<p class="calibre3">“I hope so, if we really have found the person
at last. We’ve wasted nearly the whole morning.”</p>
<p class="calibre3">Dormer was relieved; his mind, always inclined
to run a little in advance, had already
arrived at the point at which some one would have
to ask this woman:</p>
<p class="calibre3">“Are you the victim of this shocking crime?”
He didn’t want to do it, for he felt that it was the
A.P.M.’s business.</p>
<p class="calibre3">The two officers entered the Café-Restaurant
of the “Lion of Flanders.” The whole of the
ground floor, a long, low room looking out into
the Grand’ Place, had been cleared and set with
little tables. Round the desk from which the<span id="Page_26" title="[26]"></span>
Patronne supervised the business, one or two
officers from neighbouring billets were drinking
mixed vermouth. The air was redolent of preparation,
and it was only because they remained
standing that the A.P.M. and Dormer attracted
attention. Finally, a rough middle-aged woman
in an apron asked what she could do for these
gentlemen. Feeling the subject to be increasingly
delicate, Dormer ordered two mixed vermouths
and then asked if they might speak
to Mademoiselle Vanderlynden upon business.
The drinks were served, and behind them came
the person required. No sooner had she come
and inquired what was wanted, than Dormer
wished to goodness she had not. He realized
more than ever how difficult it would be to say
to such a person, “Are you the victim of the
unmentionable crime?” But there she stood,
quite good looking, imperturbable, a little impatient
perhaps, obviously wanting to know
without delay why she had been sent for in the
middle of a busy morning. This was comforting
in a sense; it showed there was something
wrong with the whole atrocious story. On the
other hand it was awkward, one had to go on
and explain. So he pulled out the blue printed
claim: the A.P.M. in spite of what had been
said, left it to him.</p>
<p class="calibre3">“It is about this claim of your father’s.”</p>
<p class="calibre3">She took it, scrutinized it a moment, and
handed it back:</p>
<p class="calibre3"><span id="Page_27" title="[27]"></span>“Ah, that.” She was not helpful.</p>
<p class="calibre3">“You are of course familiar with the whole
story?”</p>
<p class="calibre3">“Yes, I remember it all.”</p>
<p class="calibre3">The A.P.M. was listening attentively, impressed
by her glib, adequate English, and even
more so by her personality. Dormer, on the
other hand, was occupied with his own feelings.</p>
<p class="calibre3">“There is some mistake, is there not?”</p>
<p class="calibre3">“No, there is no mistake.”</p>
<p class="calibre3">“The Major has come to see the—er—the
damage!”</p>
<p class="calibre3">“I shall be pleased to go with you to the
farm, after lunch.”</p>
<p class="calibre3">“That’s a jolly good idea,” the A.P.M. broke
in. “We’ll have lunch here, and go and look
at the damage afterwards.”</p>
<p class="calibre3">“Very good. Will you take a chair, sir?”
and she was gone.</p>
<p class="calibre3">“You see, it wasn’t what you thought,” the
A.P.M. went on, finishing his drink at a gulp,
and making Dormer feel, for the twentieth time,
what a grossly unfair War it was.</p>
<p class="calibre3">The lunch was long, far more of the Flemish
midday dinner than the French déjeuner. The
A.P.M. took the lot, commented freely, enjoyed
himself immensely. There were <i class="calibre9">hors d’œuvres</i>
(sardines, beans in oil, some sort of sausage, a
kind of horse-radish, “Wonder where the devil
she gets ’em?” said the A.P.M.), soup (ordinary,
but enlivened by parsley and bits or toast fried<span id="Page_28" title="[28]"></span>
in fat and something, third cousin to a piece
of garlic, “scrumptious”), veal and spinach
(very good, but “no fish, pity!”). In a moment,
Mademoiselle Vanderlynden stood over them.
“I am sorry, we have only sardines, they will
not let the fish come by train!” Chicken and
salad (“Excellent. Ah, they understand oil, the
French”), little biscuits, coffee that dripped
through a strainer into glasses, rum (“That’s
English, I bet!”), and Dormer, shy in such
matters, and without social code, began wondering
whether he could offer to pay.</p>
<p class="calibre3">He had learned during bitter years, one rule:
“Always treat an A.P.M. if you can!” This
had not been his preoccupation during the meal.
He had been haunted by a tag of verse—from
the “Ingoldsby Legends” which he certainly
hadn’t read for twenty years. He was not one
to read “poetry.” But neither had he a regular
soldier’s trained indifference. He knew where
it was going to end, this quest on which they
were engaged. Some poor brute who had
volunteered to come to this blessed country to
fight the Germans, would be hauled out of some
ghastly apology for a “rest” camp—if he were
lucky—more likely out of some dug-out or cellar,
or even from Hospital—placed under arrest—frightened
dumb, if by any chance he had any
speech in him, and finally tried by a court to
whom he was a “Tommy” (the sort of person
who enlisted in the regular army because he was<span id="Page_29" title="[29]"></span>
out of work), and sentenced to some penalty.
And here was the A.P.M. eating and drinking
with gusto. It reminded Dormer of:</p>
<div class="poetry-container">
<div class="poetry">
<div class="verse">Send for Trefooze, and Lieutenant Tregooze,</div>
<div class="verse">Send for Sir Carnaby Jenks of the Blues.</div>
<div class="verse">How much must I fork out, my trump,</div>
<div class="verse">For the whole first floor of the Magpie and Stump?</div>
</div>
</div>
<p class="calibre3">the rhyme of the drunken swells who couldn’t
even keep awake to see a man hanged. It was,
however, the ideal state of mind for making war.</p>
<p class="calibre3">The A.P.M. was saying to Mademoiselle
Vanderlynden: “The bill please, Madam,” and
when he got it, “By Gad, did we drink all
that? Well, I don’t grudge it.”</p>
<p class="calibre3">So he was going to pay. The room was
emptying now, there were no troops in the
village, and most of the officers lunching there
(with shy propitiatory looks toward the A.P.M.)
had some way to ride to get back to their units.
Here was Mademoiselle ready to go and show
them the damage. She wore no hat, but her
clothes were good of their kind and she carried
the day’s takings clasped to her breast, in a
solid little leather dolly-bag, far from new. The
A.P.M. allowed her the rare privilege of a lift
in the car. They went back over the same road
that the two officers had followed in the morning.
Once more Dormer had his queer feelings. There
was something wrong about this. Three times
over the same road and nothing done. As they<span id="Page_30" title="[30]"></span>
turned into the by-road, Mademoiselle Vanderlynden
held up her hand. “Stop here, please!”</p>
<p class="calibre3">They were at the corner of the big pasture
before the house. There was an ordinary hedge,
like an English one, thickened at this angle
into a tiny copse, with a dozen young poplars.
Mademoiselle soon found a gap in the fence and
led them through, remarking, “The troops made
this short cut!”</p>
<p class="calibre3">They found themselves in Vanderlynden’s
pasture, like hundreds of others over a hundred
miles of country. There were no troops in it
at the moment, but it had the air of being continuously
occupied. In long regular lines the
grass had been trampled away. Posts and wire,
and a great bank of manure marked the site of
horse-lines. Nearer the house, tents had been set
up from time to time, and circles, dotted with
peg and post holes, appeared half obliterated.
At the corners of the field were latrines, and
at one spot the cookers had blackened everything.</p>
<p class="calibre3">“Billets for the troops!” reflected Dormer,
to whom the idea of lodging in the open had never
ceased to be a thoroughly bad joke. “Stables
for horses, stables for men!” Obviously enough
the machinery of War had been here in full
swing. Dormer (a man of no imagination) could
almost see before him the khaki-clad figures,
the sullen mules, the primitive vehicles filing
into the place, tarrying ever so briefly and filing
out again to be destroyed. But Mademoiselle<span id="Page_31" title="[31]"></span>
Vanderlynden was occupied with the matter in
hand, and led to the other side of the coppice,
where there had been built by some previous
generation of pious Vanderlyndens a little shrine.
It was perhaps eight feet high, six feet thick,
and had its glazed recess towards the main road.
But the glazing was all broken, the altar torn
down, and all those small wax or plaster figures
or flowers, vases, and other objects of the trade
in “votive offerings” and <i class="calibre9">objets de piété</i> which
a Vanderlynden would revere so much more
because he bought them at a <i class="calibre9">fournitures ecclésiastiques</i>,
rather than made them with his own
hands, were missing. Army wire had been
used to fasten up the gaping aperture.</p>
<p class="calibre3">“There you are,” said Mademoiselle. She
added, as if there might be some doubt as to
ownership: “You can see that it is ours. Here
is our name, not our proprietors!”</p>
<p class="calibre3">Sure enough, on a flat plaster panel was a
partially effaced inscription: “Marie Bienheureuse—prie
pour—de Benoit Vanderl—femme
Marthe—Juin 187——”</p>
<p class="calibre3">The A.P.M. lighted a cigar, and surveyed
the ruins. He was feeling extremely well, and
was able to take a detached unofficial attitude.
“Oh, so that’s the Virgin, is it?”</p>
<p class="calibre3">“No. That is the place for the image. The
image is broken, as I told you, and we removed
the pieces.”</p>
<p class="calibre3">“Very good. Then I understand you claim<span id="Page_32" title="[32]"></span>
a thousand francs for the damage to the brick-work
and the—er—altar furniture which was—ah,
broken—it seems too much, you know!”</p>
<p class="calibre3">“Perhaps, sir, you are not well ack-vainted
with the price of building materials!” (Ah,
thought Dormer, she speaks pretty good English,
but that word did her.)</p>
<p class="calibre3">“Oh, I think so, I’m a bit of a farmer myself,
you know. I have a place in Hampshire, where
I breed cattle.”</p>
<p class="calibre3">Mademoiselle’s voice seemed to rise and
harden:</p>
<p class="calibre3">“Yes, sir; but if you are rich, that is not a
reason that you should deny justice to us, who
are poor. I do not know if I can get this altar
repaired, and even if I can there is also the
question of the <i class="calibre9">effraction</i>——”</p>
<p class="calibre3">“The what?”</p>
<p class="calibre3">“Legal damages for breaking in—trespass,
sir,” put in Dormer, alarmed by the use of French.
He could see she was getting annoyed, and
wished the A.P.M., the lunch, the claim, the
farm and the War, all the blessed caboodle,
were with the devil.</p>
<p class="calibre3">“Oh, I see.”</p>
<p class="calibre3">“Et puis, and then there are <i class="calibre9">dédommagements</i>—what
would you say if I were to knock down
your Mother’s tomb?”</p>
<p class="calibre3">“What’s that. Oh, I can’t say, I’m sure. I
really can’t go into all this. Captain Dormer,
there is obviously no arrest to be made. It is<span id="Page_33" title="[33]"></span>
purely a claim for compensation. I will leave
it to you. I must be getting back. <i class="calibre9">Comprenez</i>,
Mademoiselle, this officer will hear what you
have to say and will settle the whole matter with
you. Famous lunch you gave us. Au revoir.
If you care for a game of bridge this evening,
Dormer, come round to B Mess!”</p>
<p class="calibre3">Dormer took out his field notebook and conducted
the inquiry partly in English, partly in
French.</p>
<p class="calibre3">They sat in the cavernous old tiled kitchen,
half-filled with the stove and its stupefying heat,
half with the table, scrubbed until the grain of
the wood stood out in ribs.</p>
<p class="calibre3">Mademoiselle Vanderlynden had dismissed the
A.P.M. from her mind with the remark that he
was a droll type, and gave Dormer her full
attention, rather as if he had been a dull boy in
the lowest class, and she his teacher.</p>
<p class="calibre3">“When did this occur?”</p>
<p class="calibre3">“Why, in April. It was wet, or he would
not have done it!”</p>
<p class="calibre3">“Did you see it done?”</p>
<p class="calibre3">“Yes. I even tried to stop it!”</p>
<p class="calibre3">“Where were you?”</p>
<p class="calibre3">“Why naturally I was at that hole in the
fence. One cannot always hire a boy to keep
the cattle from straying.”</p>
<p class="calibre3">“Well?”</p>
<p class="calibre3">“Well, then the troops came in. They were
not pretty to see!”</p>
<p class="calibre3"><span id="Page_34" title="[34]"></span>“What troops were they?”</p>
<p class="calibre3">She turned over a dirty dog-eared memorandum
book.</p>
<p class="calibre3">“469 Trench Mortar Battery.”</p>
<p class="calibre3">“So they had had a bad time?”</p>
<p class="calibre3">“One gathered that. They were very few,
and some of their material was missing. At the
last came this man with his two mules. One
was sick, one was wounded. Most of the men,
as soon as they had put up their animals, fell
down and slept, but this one kept walking about.
It was almost dark and it was beginning to rain.
I asked him what he wanted.”</p>
<p class="calibre3">“What did he say?”</p>
<p class="calibre3">“‘To Hell with the Pope!’”</p>
<p class="calibre3">The shibboleth sounded so queer on her lips
that Dormer glanced at her face. It was blank.
She had merely memorized the words in case
they might be of use to her. She went on:</p>
<p class="calibre3">“He did not like the images on the altar!
Then he began to break the glass, and pull down
the woodwork. One saw what he wanted. It
was shelter for his mules.”</p>
<p class="calibre3">“You cautioned him that he was doing
wrong?”</p>
<p class="calibre3">“I believe you. I even held him by the arm.”</p>
<p class="calibre3">“That was wrong of you, Mademoiselle. You
should have informed his officer.”</p>
<p class="calibre3">“Oh, you must understand that his officer
was asleep on the kitchen floor. But so asleep.
He lay where he had fallen, he had not let go<span id="Page_35" title="[35]"></span>
the mug from which he drank his whisky. So
much—(she held up four graphic fingers)—ah,
but whisky you know!”</p>
<p class="calibre3">“I see. You were unable to report to the
officer in charge of the party. But still, you
should never touch a soldier. He might do
you an injury, and then, at a court of inquiry,
it would be said against you that you laid hands
on him.”</p>
<p class="calibre3">“Oh, you understand, one is not afraid, one
has seen so many soldiers these years. And as
for the court of inquiry, we have had four here,
about various matters. They all ended in
nothing.”</p>
<p class="calibre3">“Well, well, you endeavoured to prevent the
damage, and being unable to report to the proper
authority, you made your claim for damage in
due course. But when the officer woke up, you
informed him that you had done so?”</p>
<p class="calibre3">“Why necessarily, since we had the Maire to
make a <i class="calibre9">procès-verbal</i>!”</p>
<p class="calibre3">“So I hear, from the Maire himself. But
apparently the Maire did not do so, for the
procès-verbal is not included with the other
papers.”</p>
<p class="calibre3">“No, the Maire was prevented by the troops.
(A grim smile broke for a moment the calculated
business indifference on the face of one who
excluded emotion, because it was a bad way of
obtaining money.) Oh, la-la! There was a
<i class="calibre9">contretemps</i>!”</p>
<p class="calibre3"><span id="Page_36" title="[36]"></span>“Do you mind telling me what occurred?”
She seemed to regret that brief smile, and
apologized to herself.</p>
<p class="calibre3">“All the same, it was shameful. Our Maire
is no better than any other, but he is our Maire.
One ought to respect those in power, ought not
one, sir?”</p>
<p class="calibre3">“In what way were the troops lacking in
respect?”</p>
<p class="calibre3">“They sang. They sang—<i class="calibre9">casse-tête</i>—enough
to split your head, all the way to the village!”</p>
<p class="calibre3">“Oh, they were on the move, were they?”</p>
<p class="calibre3">“It was pitiable, I assure you, sir, it was
shameful to see. <i class="calibre9">Ces pauvres êtres.</i> They hardly
had any sleep. Only a few hours. Then it
seems the Bosche made a counter-attack, and
paff! here comes a motor-cyclist, and they were
obliged to wake up and fall in. Some of them
could only stand up with difficulty. But at
length, they were ready; then the Maire came.
We had sent for him <i class="calibre9">d’urgence</i>, when we saw the
troops were going, because you can’t make a
<i class="calibre9">procès-verbal</i> of a person who is no longer there!”</p>
<p class="calibre3">“No, quite right. But why did they sing?”</p>
<p class="calibre3">“Ah, <i class="calibre9">ça tombait d’accord</i>. Just as the officer
gives the word, the Maire arrives. We had
informed him it was a crime of violence, and
he had taken it very serious. He is old, our
Maire. He had put on his—<i class="calibre9">écharpe</i>.”</p>
<p class="calibre3">“What is that?”</p>
<p class="calibre3">She made a vivid gesture with her hands.</p>
<p class="calibre3"><span id="Page_37" title="[37]"></span>“It goes so! It is tricolour. It is the
Maire’s official dress!”</p>
<p class="calibre3">“Ah, his official scarf!”</p>
<p class="calibre3">“That is it. Also, he had mounted his hat!”</p>
<p class="calibre3">“How did he do that?”</p>
<p class="calibre3">“The usual way. But it was a long hat, a
hat of <i class="calibre9">grande tenue</i>—like a pot of confiture.”</p>
<p class="calibre3">“Mademoiselle, this will not do. I cannot
settle this matter here and now, I must pass on
all the papers to my superior officer, who will
place them with the proper authority. They
will ask ‘Is there no <i class="calibre9">procès-verbal</i>?’ Am I to
say: ‘The Maire went to make one. He put
on his hat and the troops began to sing.’ It
sounds like a joke.”</p>
<p class="calibre3">“Ah, you others, you are always the ones to
laugh. It was just exactly as I have said. They
sang!”</p>
<p class="calibre3">“But you told me just now that they were
tired out!”</p>
<p class="calibre3">“Quite true!”</p>
<p class="calibre3">“It will never sound so. What did they sing?”</p>
<p class="calibre3">“Old Hindenburg has bought a hat!”</p>
<p class="calibre3">In a moment Dormer was convinced. The
words painted, framed and hung the picture for
him. He had just been beginning to hope that
the whole thing would break down from sheer
improbability. He now saw it stamped and
certified with eternal truth. There was no need
for her to add: “They were not gay, you understand,
they were <i class="calibre9">exalté</i>!”</p>
<p class="calibre3"><span id="Page_38" title="[38]"></span>“Excited!”</p>
<p class="calibre3">“Ah! Excited, like one is after no sleep
and no food and then something very strange.
They were excited. They called the Maire
‘Maréchal Hindenburg,’ and ‘Bosche,’ and
‘Spy.’ Those are words that ought not to be
used between allies!”</p>
<p class="calibre3">“No, Mademoiselle, they ought not.”</p>
<p class="calibre3">But for a moment, the hardness left her face,
she became almost impersonal.</p>
<p class="calibre3">“It was curious. They sang that—<i class="calibre9">sur une
aire de psaume</i>, to a church tune.”</p>
<p class="calibre3">“Yes, yes!” agreed Dormer. Out of the
depth of his experience as a churchwarden welled
up the strains of Whitfield, No. 671, and out
of the depths of his experiences as a platoon
commander came a sigh: “They will do it.”</p>
<p class="calibre3">He went through his notes to see if there
were anything more he wanted to know, but
from business habit he had already possessed
himself of the essentials. He did not like the
way the thing was shaping. He knew only
too well what happened in the army. Some
individual being, besides a number on a pay
roll, a human creature, would do something
quite natural, perhaps rather useful, something
which a mile or two farther on, in the trenches,
would be worth, and might occasionally gain,
the Military Medal. This business of breaking
down a bit of wood and plaster, to shelter mules,
had it occurred a little farther on, had it been a<span id="Page_39" title="[39]"></span>
matter of making a machine-gun emplacement
in an emergency, would have earned praise. It
showed just that sort of initiative one wanted in
War-time, and which was none too easy to get
from an army of respectable civilians. But at
the same time, in billets, there was another set
of rules just as important, which in their essence
discouraged initiative and reduced the soldier to
a mere automaton. The otherwise excellent thing
which he did broke those rules. That again
did not matter much, unless it was brought into
accidental prominence by colliding with some
other event or function—this Maire and his
dignity for instance, would play the very devil,
make a mountain out of a molehill, such was the
perversity of things. Fascinated against his
better judgment which told him “The less you
know about the business, the better,” he found
himself asking:</p>
<p class="calibre3">“What was this man like, Mademoiselle?”</p>
<p class="calibre3">There was no answer, and he looked up.
She had left him, gone into the back kitchen
to some job of her own. She had left him as
though the War were some expensive hobby of
his that she really could not be bothered with
any longer. On hearing his voice she returned
and he repeated his question. He never forgot
the answer.</p>
<p class="calibre3">“Like—but he was like all the others!”</p>
<p class="calibre3">“You couldn’t pick him out in a crowd?”</p>
<p class="calibre3">“Perhaps. But it would be difficult. He<span id="Page_40" title="[40]"></span>
was about as big as you, not very fat, he had
eyes and hair like you or anyone else.”</p>
<p class="calibre3">“You didn’t, of course, hear his name or
number?”</p>
<p class="calibre3">“They called him ‘Nobby.’ It was his name,
but they call every one ‘Nobby.’ His number
was 6494. I saw it on his valise.”</p>
<p class="calibre3">“On his pack?”</p>
<p class="calibre3">“Yes.”</p>
<p class="calibre3">“Thank you, Mademoiselle. You have told
me all I want.” In his heart he feared she had
told him much too much, but she had gone on
with her work. He rose to go, but passing
the dark entry of the back kitchen, he stopped,
as though to avoid a shell. He thought he saw
a headless figure, but it was only a shirt which
Mademoiselle Vanderlynden had flung over a
line before putting it through the wringer. He
went out. She did not accompany him. She
was busy, no doubt.</p>
<p class="calibre3">He had to walk to the main road, but once
there, found no difficulty in “jumping” a lorry
that took him back to Divisional Head-quarters.
On the steps of the Town Hall he crossed the
A.P.M. It was very late for that functionary to be
about. He had not even changed into “slacks.”</p>
<p class="calibre3">“Hello, young feller, you got back then?”</p>
<p class="calibre3">“Yes, sir.” Dormer rather wanted to say,
“No, sir, I’m not here, I’m at the farm where
you left me.”</p>
<p class="calibre3">The A.P.M. passed on, but turned to call out:<span id="Page_41" title="[41]"></span>
“No bridge to-night. We’re on the move!”</p>
<p class="calibre3">So it seemed. The interior of the old building
was in confusion. The Quartermaster-Sergeant
was burning orders, schedules, rolls and
parade states of the Corps they were leaving.
Signallers were packing their apparatus, batmen
were folding beds and stuffing valises. Policemen
were galvanized into a momentary activity.</p>
<p class="calibre3">To Dormer it was the old, old lesson of the
War. Never do anything, it is always too late.
He had been bound, by a careful civilian conscience,
to try to get to the bottom of the matter.
He might just as well have torn it up and let
it take its chance. No, the Vanderlyndens would
never let it rest until they got some sort of satisfaction.
The Mayor and the French Mission and
Heaven knows who else would have something
to say. He wrote a brief but careful report,
and sent the thing off to an authority at Boulogne
who dealt with such matters.</p>
<p class="calibre3">The weeks that followed were full of education
for Dormer’s detached, civilian mind. Accustomed
to be part of a battalion, almost a close
family circle of known faces and habits, then
associated with the staff of a division that stuck
in one place, he had never before seen an army,
and that army almost a nation, on the move.
Under his eyes, partly by his effort, fifteen<span id="Page_42" title="[42]"></span>
thousand English-speaking males, with the proper
number of animals and vehicles, impedimenta,
movable or fixed, had got into trains, and got
out of them again, and marched or been conveyed
to a place where Dormer had to take leave of
all preconceived notions of life.</p>
<p class="calibre3">No-Man’s-Land, with trenches beside it, he
was familiar with, but here were miles of
had-been No-Man’s-Land, grassless, houseless,
ploughed into brown undulations like waves of
the sea by the barrages that had fallen upon it;
covered with tents and huts, divided by wandering
rivers of mud or dust, which had been at
some distant time, weeks before, roads. Into
this had poured, like the division to which
he was attached, forty other divisions, always in
motion, always flowing from the railhead behind,
up to the guns in front, shedding half the human
material of which they were composed, and
ebbing back to railhead to go elsewhere.</p>
<p class="calibre3">He came to rest in a tiny dug-out on a hillside
of loose chalk, which he shared with a signal
officer, and past which, at all hours of the day
and night, there passed men, men, men, mules,
men, guns, men, mules, limbers, men, men, men.</p>
<p class="calibre3">At least this is how they appeared to him.
Forced by Nature to sleep for some of the hours
of darkness, and forced by the Germans to be
still for all the clearest of the daylight, it was at
the spells of dusk and dawn that he became
busiest, and that infernal procession was ever<span id="Page_43" title="[43]"></span>
before his eyes. It was endless. It was hopeless.
By no means could his prim middle-class
mind get to like or admire anything so far from
the defined comfort and unvarying security to
which he belonged and to which he longed to
return. It was useless. With the precision of
a machine, that procession was duplicated by
another moving in the opposite direction.
Lorries, ambulances, stretchers, men, men, guns,
limbers, men, men, men. The raw material
went up. The finished article came back.
Dormer and his companion and their like, over
twenty miles of line, sorted and sifted and kept
the stream in motion.</p>
<p class="calibre3">That companion of his was not the least of
his grievances. The fellow was no Dormer, he
was opposite by name and nature. His name
was Kavanagh, and one of the meagre comforts
Dormer got was by thinking of him as a d——d
Irishman. He was, or had been going to be, a
schoolmaster, and next to nature (or nationality),
the worst thing about him was he would talk.
And he would <i class="calibre9">not</i> keep his hands still. Two
things that Dormer most gravely disapproved of,
and which he attributed in equal shares to lack of
experience of the world, and too much signalling.</p>
<p class="calibre3">His talk was such tripe, too! He never lost
a moment. He started first thing in the morning.
All the traffic that was going up forward
was gone. The earth was empty, save for anti-aircraft
guns pop-popping at planes high in the<span id="Page_44" title="[44]"></span>
Italian blue. Dormer had shaved and breakfasted
and hoped to catch up some of the sleep
he had lost during the night. But would that
fellow allow that? No. Listen to him now,
under the tiny lean-to they had contrived, by the
dug-out steps, for washing purposes. He was—reciting—would
one call it?</p>
<div class="poetry-container">
<div class="poetry">
<div class="first">“The last tattoo is beating, boys,</div>
<div class="verse">The pickets are fast retreating, boys,</div>
<div class="verse">Let every man</div>
<div class="verse">Fill up his can</div>
<div class="verse">And drink to our next merry meeting, boys!”</div>
</div>
</div>
<p class="calibre3">“Do you call that poetry?”</p>
<p class="calibre3">“No.”</p>
<p class="calibre3">This was rather awkward. Dormer had intended
a snub. Not caring for poetry himself, he
had tried to take a high line. He went on lamely:</p>
<p class="calibre3">“Oh! What do you call it then?”</p>
<p class="calibre3">“A most amazing picture of the mentality of
1815. Compare it with that of 1915. In that
old war of ours against the French, we swore, we
drank, we conquered. What do you think that
same fellow would have to write about us to-day?”</p>
<p class="calibre3">“He wouldn’t,” put in Dormer, without avail.</p>
<p class="calibre3">“Something like this:</p>
<div class="poetry-container">
<div class="poetry">
<div class="first1">“‘’Z day is fast approaching, boys,</div>
<div class="verse">In gas-drill we want coaching, boys,</div>
<div class="verse">Our iron ration</div>
<div class="verse">Will soon be in fashion.’</div>
</div>
</div>
<p class="calibre3">What rhymes to coaching?”</p>
<p class="calibre3"><span id="Page_45" title="[45]"></span>“How should I know?”</p>
<p class="calibre3">“Joking apart, Dormer!” (As if Dormer
had been joking.) “Do you catch the impulse
of the slogan? Of course, iron rations and gas
helmets make a much more efficient soldier than
drums and bayonets and rum, but the zest is
all gone!”</p>
<p class="calibre3">Dormer did not reply; a belated party of
engineers of some special service were passing
up the road, and from where he lay in the dug-out
he could see khaki-covered bodies upon dusty
legs, but no heads, the beam of the entrance
was too low. Suddenly he said:</p>
<p class="calibre3">“Did you ever dream that the army was like
a giant without a head?”</p>
<p class="calibre3">“What did you say?”</p>
<p class="calibre3">Good gracious, what had he said? He replied,
“Oh, nothing,” and bit his lips. It must
be want of sleep. Fortunately Kavanagh did
not hear. He was going on with his poetry.</p>
<div class="poetry-container">
<div class="poetry">
<div class="first">“The Colonel, so gaily prancing, boys,</div>
<div class="verse">Has a wonderful way of advancing, boys,</div>
<div class="verse">Sings out so large</div>
<div class="verse">Fix bayonets and cha-a-a-rge,</div>
<div class="verse">It sets all the Frenchmen a-dancing, boys!</div>
</div>
</div>
<p class="calibre3">“What days they must have been, Dormer!
You ought to have been a Colonel. Can’t you
see yourself on a big brown horse, gaily prancing?
There ought to be a school for gaiety, just as
there is for bayonet fighting and bombing.<span id="Page_46" title="[46]"></span>
Can’t you imagine yourself in a shako, like a
top hat, with the brim in front only, glazed,
with whacking great numerals?”</p>
<p class="calibre3">Dormer wanted to say: “You’ve got a marvellous
imagination!” which would have been
intended as an unfavourable criticism. But the
words stuck on his lips. Instead he said:</p>
<p class="calibre3">“It’s all very well. You don’t seem to see
the serious part of all this—waste!”</p>
<p class="calibre3">“Waste, my dear fellow!” And to Dormer
the harsh, cheerful voice had all the officious
familiarity of a starling, gibing at one from an
apple tree. “Waste is not serious. It is
nature’s oldest joke. It used to be called Chaos.
From it we came. Back to it we shall go. It
will be called Immortality. The Graves Commission
will give it a number, a signboard, and
a place on the map, but it will be Immortality
none the less. From Titans to tight ’uns, ‘each
in his narrow grave.’”</p>
<p class="calibre3">“Oh, chuck it,” said Dormer, disgusted and
having no memories of that quotation. “You’ve
evidently never been in charge of a burying
party!”</p>
<p class="calibre3">“I have. I did twelve months in the line, as a
platoon commander. How long did you do that?”</p>
<p class="calibre3">“Twelve months about!”</p>
<p class="calibre3">“I believe you, where thousands wouldn’t.
Twelve months was about the limit. In twelve
months, the average Infantry subaltern got a
job, or got a blighty! I know all about it!”</p>
<p class="calibre3"><span id="Page_47" title="[47]"></span>“Then you ought to know better than to
speak so. It’s not a joke!”</p>
<p class="calibre3">“My dear Dormer, if it were not a grim joke
it would be utterly unbearable.”</p>
<p class="calibre3">“I disagree entirely. It’s that point of view
that we are suffering from so much. You don’t
seem to see that this army is not an army of
soldiers. It is an army of civilians enlisted under
a definite contract. They aren’t here for fun.”</p>
<p class="calibre3">“Oh, come, Dormer, don’t you believe in
enjoying the War?”</p>
<p class="calibre3">“I believe in getting it done.”</p>
<p class="calibre3">“You never will, in that frame of mind.”</p>
<p class="calibre3">“Oh, shan’t I? What would happen if I
didn’t see that the right people get to the right
place, with the right orders and right supplies,
including you and your blessed flagwaggers?”</p>
<p class="calibre3">“Nothing to what will happen if the troops
once begin to regard the show as a matter of
business! You haven’t got a shako and a big
brown horse, but you must play up, as if you
had!”</p>
<p class="calibre3">“What rot you talk. I have a tin hat because
it will stand shrapnel better than a shako. I
have mules because they stand the life better
than a horse?”</p>
<p class="calibre3">“Yes, but do you admire your tin hat? Do
you really care for mules!”</p>
<p class="calibre3">Something made Dormer say in spite of himself:</p>
<p class="calibre3">“I did once come across a man who cared!”</p>
<p class="calibre3"><span id="Page_48" title="[48]"></span>“There, what did I tell you. He was winning
the War!”</p>
<p class="calibre3">(“Whatever did I tell him that for?” Dormer
asked himself vexedly. “A nice song he’ll make
of it.”) But he only said:</p>
<p class="calibre3">“You’re all wrong, as usual. He did nothing
of the sort. He just made a row in billets!”</p>
<p class="calibre3">“Quite right too. Most of ’em deserve a row!”</p>
<p class="calibre3">“Possibly, but he went the wrong way to
work!”</p>
<p class="calibre3">“Ah, that depends!”</p>
<p class="calibre3">(Irritating brute!)</p>
<p class="calibre3">“No, it doesn’t. Were you ever at Ypres?”</p>
<p class="calibre3">“Was I not. I was hit at Hooge stables, and
had to walk nearly a couple of miles to get to a
dressing station!”</p>
<p class="calibre3">“Well, then, you remember, in the back billets,
a place called the ‘Spanish Farm’?”</p>
<p class="calibre3">“Don’t I just. Great big old house, with a
moat, and pasture fore and aft.”</p>
<p class="calibre3">What a way to put it!</p>
<p class="calibre3">“Well, this chap I’m telling you of, was
billeted there. He was attached to a Trench
Mortar Battery. He was in charge of the mules.
He didn’t talk a lot of rot about it, as you suggest
he should. One of his mules was wounded and
the other sick. He broke down the front of the
shrine at the corner of the pasture to get a bit
of shelter for them!”</p>
<p class="calibre3">The effect of this recital was not what Dormer
expected.</p>
<p class="calibre3"><span id="Page_49" title="[49]"></span>“That was an unspeakably shocking thing to
do, worse than losing any number of mules!”</p>
<p class="calibre3">“I suppose you’re a Catholic?”</p>
<p class="calibre3">“Yes, I am!”</p>
<p class="calibre3">“I thought as much. Well, I’m not, nor
was this driver I’m telling you about. He just
hated the waste and destruction of it all.”</p>
<p class="calibre3">“So he destroyed something more precious
and permanent.”</p>
<p class="calibre3">“He thought a live mule was better than a
dead saint.”</p>
<p class="calibre3">“He was wrong!”</p>
<p class="calibre3">And then the fellow shut up, got quite sulky.
Dormer was delighted with his prowess in
argument, waited a moment, turned on his side, and
slept, as only men can who live in the open air, in
continual danger of their lives, and who lose the
greater portion of the night in ceaseless activity.</p>
<p class="calibre3">When his servant woke him, with tea and
orders and the nightly lists of traffic and stores,
it was a wonderful golden and green sunset,
tremulous with the evening “hate.” The purple
shadows were just sufficiently long to admit of
getting the wounded back, and the road was
filled with ambulances, whirring and grinding
as they stopped, backed, and restarted, while a
steady punctual crash, once a minute, showed
that the Bosche were shelling the road or one<span id="Page_50" title="[50]"></span>
of the innumerable camps or dumps along it, in
the neighbourhood. Amid all this clamour,
Kavanagh was not silenced, but recited at the
top of his voice, and Dormer had a suspicion that
the real reason was that it helped to keep down the
nervousness that grew on men, as the years of
the War rolled on, and the probability of being
hit increased. Especially as, far overhead, the
planes that circled and swooped like a swarm of
gleaming flies, were attracting considerable anti-aircraft
fire, and all round, big jagged bits were
coming to earth with a noise almost echoing that
of the ambulances.</p>
<p class="calibre3">Dormer’s tidy mind was soon called into
action. Some wounded who had died on the
way to the dressing station, had been laid out
beside the road as the ambulances had enough to
do without carrying corpses ten miles. He
went to make sure the M.O. had arranged for a
burial party, as he had the strongest belief that
casualties lying about were bad for the morale
of the troops. When he got back to the dug-out,
Kavanagh was “going on,” as he bent over a
map of the extensions of the divisional cable lines,
like a crow on a gate.</p>
<p class="calibre3">“See those chaps, Dormer?”</p>
<div class="poetry-container">
<div class="poetry">
<div class="first">“Quis procul hinc—the legends writ</div>
<div class="verse">The—er—Picard grave is far away,</div>
<div class="verse">Quis ante diem periit</div>
<div class="verse">Sed miles, sed pro patria.”</div>
</div>
</div>
<p class="calibre3"><span id="Page_51" title="[51]"></span>“Do you believe in pronouncing Latin like
Julius Cæsar or like Jones Minor?”</p>
<p class="calibre3">“I don’t believe in it at all. Pure waste of
time!”</p>
<p class="calibre3">“Dormer, you are a Utilitarian!”</p>
<p class="calibre3">“Have it your own way so long as you get that
cable line of yours sited. I’ve got parties coming
up to-morrow to dig it in!”</p>
<p class="calibre3">“I shall be ready for them. Think of all that
language, and language is only codified thought,
buried in the ground, Dormer!”</p>
<p class="calibre3">“I have all the thinking I want over all the
men buried in the ground. We’re losing far too
many!”</p>
<p class="calibre3">The “victory” of the Somme had been a
saddening experience for Dormer.</p>
<p class="calibre3">“That shows how wrong you are. We are
mortal. We perish. But our words will live.”</p>
<p class="calibre3">“Rot! Do you mean to say that ‘825
Brigade relieve you to-morrow Nth. Div. Ack,
ack, ack’ will live! Why should it? It’ll be
superseded in four days. Who wants to perpetuate
it?”</p>
<p class="calibre3">“I disagree with you, Dormer, I really do.
Here we are at the great crisis of our lives, of the
life of European Civilization perhaps. Some
trumpery order you or I transmit may mean in
reality ‘Civilization is defeated, Barbarism has
won!’ or it may mean, I hope, ‘Lift up your
eyes unto the hills from whence——’”</p>
<p class="calibre3">“I wish you wouldn’t joke about the Bible!”</p>
<p class="calibre3"><span id="Page_52" title="[52]"></span>“I’m not joking, and you’ll find it out before
long. Men will fight so long as they’ve got
something to fight about!”</p>
<p class="calibre3">“Well, they have. They want to get home.
They’ll fight fast enough about that.”</p>
<p class="calibre3">“Not they. That isn’t the thing to make ’em
fight. It’s more likely to make ’em run away.
They want an idea.”</p>
<p class="calibre3">“They’ve had enough ideas, I should think.
I seem to remember the walls covered with posters,
with an idea a-piece.”</p>
<p class="calibre3">“Those ideas were much too superficial and
temporary. They want to feel that they are
something, or that they do something so important
that it doesn’t matter whether they live or die!”</p>
<p class="calibre3">“That’s all wrong. It does matter. This
War will be won by the side that has most men
and most stuff left.”</p>
<p class="calibre3">“Nonsense. It will be won by the side that
has the most faith.”</p>
<p class="calibre3">“Oh, well, you go and have faith in your cable
line. I’ve got to have it in these working
parties.”</p>
<p class="calibre3">It was now dusk enough for the main body of
troops to get on the move. The broad valley
below was in ultramarine shadow, the round
shoulders of the down touched with lemon-coloured
afterglow. Up the drift of chalk dust
that represented where the road had once been,
an insignificant parish road from one little village
to another, but now the main traffic artery of an<span id="Page_53" title="[53]"></span>
Army Corps, there came pouring the ceaseless
stream, men, men, men, limbers, men, mules,
guns, men.</p>
<p class="calibre3">The longer he looked at them, the more certain
he became that he was right. Not merely the
specialists in mechanics, engineers, ordnance,
signals, gunners, but the mere infantry had taken
months to train, and could be knocked out in a
moment. The problem, of course, was to save
them up until the moment at which they could
produce the maximum effect.</p>
<p class="calibre3">How docile they were. Platoon for this,
platoon for that, section of engineers, then a
machine-gun company. Then rations, then
limbers, wagons, hand-carts full of every conceivable
kind of implement or material. Very
soon he was obliged to stand in the middle of
the road, with the stream of traffic going up, before
him, and the stream of traffic coming back, behind,
so that in addition to checking and directing
one lot he had to keep an eye on the other to see
that they did not begin to smoke until they were
well down the side of the hill. Gradually the
darkness thickened, and the crowd thinned, and
the thunder of the front died down. At length
he was left with only a belated hurrying limber
or two, or ambulance, sent back for the third or
fourth time to clear the accumulation of casualties.
At last he felt justified in getting into his bunk and
shutting his eyes.</p>
<p class="calibre3">Thank goodness that fellow wasn’t back. He,<span id="Page_54" title="[54]"></span>
Dormer, would be asleep, and would not hear him.
He counted the khaki shoulders and dusty wheels
that went round and round beneath his eyelids,
until he went off.</p>
<p class="calibre3">Unfortunately for that particular <i class="calibre9">malaise</i> which
the War occasioned to his precise and town-bred
spirit, that was not his last sleep that he slept that
night. Many a one never woke again to hear
the earth-shaking clamour of the barrage, to see
that eternal procession of men, men, mules,
limbers, men, guns, ambulances, men, lorries,
going on and on like some gigantic frieze. But
Dormer did. He was one of those who, had he
been born in the Middle Ages, would have been
described as under a curse, or pictured as working
out an atonement for his own or some one else’s
misdeeds. He had to go on doing his very best,
and the more he disliked the whole business the
harder he worked. The harder he worked the
longer it seemed to that desired day when he
might return to the quiet niceties of a branch
bank in a provincial town. And all the time
Kavanagh kept up that ceaseless argument as to
one’s mental attitude. Dormer didn’t really believe
in having such a thing, for he felt bound to
join issue with the absurd ramblings of the other,
and he could not escape, because their jobs
naturally threw them together and because he
secretly admired the way that Kavanagh did his
work.</p>
<p class="calibre3">So the days turned into weeks and the weeks<span id="Page_55" title="[55]"></span>
into months, the casualty lists grew longer and
longer, the visible fruits of the immense effort
grew smaller and smaller, and as the year wore
on, the weather broke, and the only conditions
that make life in the open tolerable, light and
drought, disappeared, and they dwelt in the
sodden twilight of tent or hut, while what had
been the white powdery dust, became the cement-like
mud that no scraping could remove. Sitting
dejectedly over some returns he heard</p>
<p class="center">“Still, be still, my soul, the arms you bear are brittle!”</p>
<p class="calibre3">“It’s all very well to sit there and sing. This
offensive is a failure, we shall never get through.”</p>
<p class="calibre3">“I’m afraid you’re right, Dormer. I told you
how it would be. I hope we shall learn the
lesson.”</p>
<p class="calibre3">“It means another winter in the trenches.”</p>
<p class="calibre3">“Evidently.”</p>
<p class="calibre3">“It’s very bad for the men. They’ve nothing
to show for all that’s been done.”</p>
<p class="calibre3">“That’s nothing new.</p>
<div class="poetry-container">
<div class="poetry">
<div class="first2">“‘I’m sick of parading,</div>
<div class="verse">Through cold and wet wading,</div>
<div class="verse">Or standing all day to be shot in a trench!</div>
<div class="verse">I’m tired of marching,</div>
<div class="verse">Pipe-claying and starching,</div>
<div class="verse">How neat we must be to be shot by the French.’</div>
</div>
</div>
<p class="calibre3">That’s what the men thought of it a hundred
years ago. Then, they had to pipeclay their belts,
two whacking great chest-constricting cross-belts.<span id="Page_56" title="[56]"></span>
And their officers didn’t arrange for them to play
football, every time they went out to rest. In
fact they didn’t go out to rest. They just stayed
in the line.”</p>
<p class="calibre3">“It wasn’t very dangerous, was it?”</p>
<p class="calibre3">“There wasn’t the shell-fire, of course, but
what about disease?”</p>
<p class="calibre3">“They were regulars.”</p>
<p class="calibre3">“My dear fellow, when is a soldier not a
soldier?”</p>
<p class="calibre3">“I don’t like riddles.”</p>
<p class="calibre3">“This is a serious question. How long will
the War last?”</p>
<p class="calibre3">“Oh,” cried Dormer bitterly, “another two
years, I suppose.”</p>
<p class="calibre3">“You’re about twenty wrong. We shall have
conscription shortly, then the real strength will
be put into the fight and will compensate for the
losses of France and the inertia of Russia. We
shall then settle down to the real struggle between
England and Germany for the markets of the
world.”</p>
<p class="calibre3">Dormer frowned. “You’re a Socialist,” he
said.</p>
<p class="calibre3">“Never mind my opinions. It won’t matter
by the time we get back into civvys what we are!”</p>
<p class="calibre3">Something rose up in Dormer. He said with
certainty:</p>
<p class="calibre3">“You’re wrong. The men’ll never stand it.
Two years at most.”</p>
<p class="calibre3">“The men stood it very well in the Peninsular<span id="Page_57" title="[57]"></span>
for six years, and most of them had been fighting
somewhere or other for the previous quarter of a
century.”</p>
<p class="calibre3">“Once again, they were regulars.”</p>
<p class="calibre3">“Once again, so are you.</p>
<div class="poetry-container">
<div class="poetry">
<div class="first2">“‘For gold the sailor ploughs the main,</div>
<div class="indent">The farmer ploughs the manor,</div>
<div class="verse">The brave poor soldier ne’er disdain,</div>
<div class="indent">That keeps his country’s honour!’</div>
</div>
</div>
<p class="calibre3">That’s you to the life, Dormer. Twenty years
hence you’ll be a bronzed veteran, in a dirty
uniform, with a quarter of a century’s polish on
your Sam Browne. You have already had more
iron whizz past your head than any regular
soldier gets in a lifetime, or even the lifetime of
two or three generations. You’ve had a practical
experience of war that any general might envy.
The only complaint I have to make against you
is that you’re conducting the whole business as if
you were back in your beastly bank, instead of,
as the song says, behaving as one ‘That keeps
his country’s honour!’”</p>
<p class="calibre3">“That’s all nonsense. I’ve just sent the 561
Brigade to occupy the new line that was taken up
after the stunt last Thursday. You know what
it’s like. It’s the remains of a German trench
turned round, so that they have all the observation.
They’ve strafed it to Hell, and we are
firing on photographs of trenches that are probably
empty. It’s all nonsense to say the defending<span id="Page_58" title="[58]"></span>
side loses more men than the attacking.
That’s true while the attack is in progress, but an
attack in its very nature cannot last long, and then
the defenders get their own back.”</p>
<p class="calibre3">As he said the words they were enveloped in an
explosion that shook the wet out of the canvas
upon them, and whose aftermath of falling débris
was echoed by stampeded traffic in the road.</p>
<p class="calibre3">“The Bosche seem set on proving you right,”
laughed Kavanagh. “They forget, as you do,
that, sooner or later, an attack gets through and
ends the War.”</p>
<p class="calibre3">“Not this one. Nothing but no more reserves
will end this. And that may happen to both sides
at once. It may all end in stalemate!”</p>
<p class="calibre3">“If it does, we shall fight again. We represent
Right. The enemy represents Wrong.
Don’t you ever forget that for a moment.”</p>
<p class="calibre3">“I don’t. I believe we are in the right, or I
should never have joined up.” When really
moved, there came into Dormer’s grey inexpressive
face a queer light, that might have made the
Germans pause, had they seen it. He was a man
of few theories, but he was literally ready to die
for those few, when they were attacked. He went
on shyly: “But I don’t believe in war as a
permanent means of settling ‘disputes.’”</p>
<p class="calibre3">“Bravo!” cried Kavanagh. “I like you
when you speak out. I only wish you did more
of it. You’re quite right, but what you don’t see
is that modern society is so rotten that it can only<span id="Page_59" title="[59]"></span>
be kept alive by violent purges, credit cycles,
strikes, and wars. If it were not for such drastic
remedies people of the twentieth century would
perish of ease and comfort.”</p>
<p class="calibre3">“Come, ease and comfort never killed anyone.”</p>
<p class="calibre3">“Spiritually!”</p>
<p class="calibre3">“Oh, I don’t go in for spiritualism!” Dormer
was saying, when his servant brought him his tea.
There was bread, that had rolled on the floor
of a lorry until it tasted of dust, oil, blood, and
coal. There was butter. There was marmalade.
There was some cake they had sent him from
home. Leaning his elbows on the board on
which they wrote, he held his enamel mug in
both hands and swilled his chlorinated-water,
condensed-milk tasting tea. For the first time,
as he clasped the mug and filled his gullet he was
warm, hands, mouth, neck, stomach, gradually all
his being. He put the mug down nearly empty
and shoved the cake over to Kavanagh. “Have
some?” he mumbled.</p>
<p class="calibre3">They found themselves in a village of the
Somme country, hardly recognizable for the division
that had come there for the offensive, five
months before. Just infantry, with the necessary
services, without artillery, or cavalry, they were
billeted in barns and cottages up and down a
narrow valley, with cliff-like downs rising each<span id="Page_60" title="[60]"></span>
side and a shallow, rapid stream flowing between
poplars and osier beds at the bottom. Dormer
was entrusted with the critical military operation
of organizing Football, Boxing and entertainment,
and spent his time to his great satisfaction,
up and down the three miles of road that ran
through the Divisional Area, notebook in hand,
listing the battalions or companies as entering for
one or another of these sports. He liked it and
it suited him.</p>
<p class="calibre3">Mildly interested in sport as such, what he
liked about his job was that it kept his feet warm
and his mind employed, and he arranged so that
his daily journey ended sufficiently far from Head-quarters
for some hospitable unit to say, “Oh,
stop and have lunch!” It would then be a nice
walk back, a quiet hour or so, getting the correspondence
into shape before the Colonel returned
from the afternoon ride, by which he shook down
his lunch and made a place for his dinner. After
that would be tea, orders to sign and circulate,
mess, a game of cards, and another day would be
done. He had long found out that the great art of
war lay not in killing Germans, but in killing time.</p>
<p class="calibre3">Over and over again, every day and all day, as
he moved up and down those wintry roads, he
looked at the faces of the men who knew now that
the great offensive had resulted in infinitesimal
gains, enormous losses, and only approached the
end of the War by so many weeks. He failed
entirely to make out what was going on in their<span id="Page_61" title="[61]"></span>
minds. Officers were always officially pleased to
see him because he was attached to Divisional
Head-quarters, because he came to talk about
games, not about work, because he was, as he was
perfectly conscious, one of the most difficult
fellows in the world to quarrel with. He had
never had any great bitterness in life, and was so
averse to official “side” that he made an effort
to appear as informal as possible. Sometimes
N.C.O.’s would be produced, consulted as to
whether a team could be got together, what
amount of special training could be allowed
intending pugilists, without interfering with necessary
drills and fatigues, what histrionic, (or to put
it frankly), what music-hall talent could be found.
The N.C.O.’s were (of course) keen, smart, attentive,
full of suggestions and information. They
had to be. They kept their jobs by so being,
and their jobs gave them just the opportunity
to live about as well as lumbermen in the remote
parts of North America, instead of existing like
beasts in barns, not pet animals, not marketable
produce, but just beasts, herded and disposed of,
counted and controlled, for such was the fate of
the average infantryman, and war being what it
is, there came a gradual acquiescence in it. It
could be no other.</p>
<p class="calibre3">But all those plain soldiers, of whom only one
or two per cent had even a voice in their entertainment,
of what they thought, who knows?
Dormer wondered. He wondered even more at<span id="Page_62" title="[62]"></span>
himself. Why on earth, in the midst of a
European War that had changed his whole existence
so dramatically, he should want to go
bothering his head about what was happening
to other people he couldn’t think, but he went on
doing it. Otherwise the life suited him rather
well, and with every fresh week that separated him
from the offensive, a sort of balance so natural
to the thoroughly balanced sort of person that
he was, went on adjusting itself, and he found himself
thinking that perhaps in the new year there
might be a new chance, the French, the Russians,
the Italians might do something, so might we.
Then it would be over, and one could go home.</p>
<p class="calibre3">It was then that the inevitable happened. He
knew it as soon as he got into the room at the
Mairie that served for Q. office. He was so sure
that he stood turning over the correspondence on
his desk, the usual pile of returns, orders, claims
and indents, without reading them, certain that
the Colonel was going to speak to him. At last
the Colonel did speak:</p>
<p class="calibre3">“Look here, Dormer, I thought we settled
this?”</p>
<p class="calibre3">There it was, the blue questionnaire form, the
other memorandums, Divisional, Corps, Army
French Mission, Base Authority, all saying
“Passed to you please, for necessary action.”
With an absurd feeling that it did not matter
what he said, or did, and that the whole thing was
arranging itself without him, he got out:</p>
<p class="calibre3"><span id="Page_63" title="[63]"></span>“What is that, sir?”</p>
<p class="calibre3">“This—er—civilian claim for compensation.
Something about a girl in a hayfield. What did
you do, when we were up in Flanders?”</p>
<p class="calibre3">He rebelled so against the unfairness of it.</p>
<p class="calibre3">“Major Stevenage had the matter in hand. I
went with him to the spot.”</p>
<p class="calibre3">“What did you find?”</p>
<p class="calibre3">“It was not what I—you—we thought, sir.
The words ‘<i class="calibre9">La Vierge</i>’ were intended to convey
that a shrine had been damaged.”</p>
<p class="calibre3">“A shrine? Really. How odd the French
are? It was accidental, was it? Bad driving?”</p>
<p class="calibre3">“No, sir, not exactly. A driver wanted shelter
for his mules——”</p>
<p class="calibre3">“Quite right, quite right.”</p>
<p class="calibre3">“So he broke into the shrine——”</p>
<p class="calibre3">“Ah, that was a mistake, of course. Whatever
were his unit about to let him?”</p>
<p class="calibre3">“The matter was not reported until later.”</p>
<p class="calibre3">“Then they placed him under arrest and
stoppages?”</p>
<p class="calibre3">“They were moved immediately, sir. But I
didn’t gather that any action was taken.”</p>
<p class="calibre3">“But when Major Stevenage found it out?”</p>
<p class="calibre3">“It had happened so long before that he
thought it was impossible to pursue the matter.
So I made a report and sent it to the proper authority,
to see if an ex. gratia payment could be made.”</p>
<p class="calibre3">“And they have done nothing, of course. So
the French Mission have dug it up again.”</p>
<p class="calibre3"><span id="Page_64" title="[64]"></span>“Indeed, sir.”</p>
<p class="calibre3">“Yes. Oh, I can’t wade through all this.
But I tell you what, young Dormer. You’ve got
yourself involved in this correspondence, and I
shouldn’t be at all surprised if you didn’t ever
get out. I shouldn’t really.”</p>
<p class="calibre3">“I can’t see that I’ve done anything wrong, sir.”</p>
<p class="calibre3">“Can’t you? Well, it’s no good your telling
the French Mission that, I’m afraid. You might
go and try to persuade them that there’s a mistake,
or an exaggeration, and get them to drop it.
You’d better go and see them anyhow. They’re
at Flan! Take what’s-his-name with you.”</p>
<p class="calibre3">From this, Dormer, by long experience, understood
that he was to go to Army Head-quarters
and to take the Divisional French Liaison Officer
with him. He neither liked nor disliked the job.
It was the sort of thing one had to do in war-time
and he was used to it. So he went down the
little stony street to the pork-butcher’s, where,
upon the swing-gate that admitted one to the
dank, greasy, appetizing interior, where every
sort of out-of-the-way portion of the pig lay
cooked and smelling “sentimental,” hung the
placard “French Liaison Officer,” with the
number of the Division carefully smudged out.
Here, blue-coated, booted and spurred, sat the
French Liaison Officer, innumerable small
printed sheets of instructions before him, carefully
arranged on this pile or on that, while in
between lay the cardboard-covered <i class="calibre9">dossiers</i>.</p>
<p class="calibre3"><span id="Page_65" title="[65]"></span>Dormer’s immediate impression was: “Not
enough to do. Passing the time away,” but he
had too much sympathy with such an attitude to
say so. He was greeted with effusion:</p>
<p class="calibre3">“My dear Dormer, to what do I owe the
pleasure?”</p>
<p class="calibre3">Dormer never liked effusion. He replied
briefly:</p>
<p class="calibre3">“This,” and threw the papers on the table.</p>
<p class="calibre3">It amused him to watch the change in the
other’s face from purely official politeness to perfectly
genuine determination to keep out of it.</p>
<p class="calibre3">“Well, Dormer, you’ve heard of System D?”</p>
<p class="calibre3">He had to think whether it was Swedish
gymnastics or a patent medicine.</p>
<p class="calibre3">“It means ‘<i class="calibre9">Debrouillez vous</i>,’ or ‘Don’t get
mixed up with it.’ That is my advice to you.
In any case I shall leave it alone. It is a matter
of discipline purely.”</p>
<p class="calibre3">“Quite so.” Dormer did not care whether
the sarcasm was obvious. “But I have received
orders to go and see your Chief at Army Head-quarters,
and to take you with me. I suppose you
don’t mind going. It’ll be a ride.”</p>
<p class="calibre3">“I shall be delighted. I will go and tell my
servant to have my horse round. I will introduce
you to Colonel Lepage. He is a man of excellent
family.”</p>
<p class="calibre3">“I thought you would,” said Dormer to himself.</p>
<p class="calibre3">Accordingly, they rode together. The Frenchman<span id="Page_66" title="[66]"></span>
rode with style, being bound to show that he
was of the class of officer who could ride, a sharp
demarcation in his army. Dormer rode as he did
everything else. He had learned it as part of
his training, without enthusiasm, knowing that a
motor-byke was a far better way of getting about.
But he was careful of a horse as of anything else.
They arrived at Flan. It was another little stone-built
village. The only difference he could see
between it and Louches, which they had just left,
was that it stood on the top of a hill, the other
along the bottom of a valley.</p>
<p class="calibre3">Its present temporary occupants, however, he
could soon see to be a vastly different category.
Every little house was placarded with the signs or
marks of the offices or messes it contained. Very-well-groomed
orderlies and signallers strolled or
waited. Big cars and impeccable riding horses
were being held or standing. They found the
French Mission, got their horses held (instead of
turning off the petrol, and kicking down a stand,
thought Dormer) and entered.</p>
<p class="calibre3">It was the little Picard parlour of some small
<i class="calibre9">rentier</i>, who, having sold beetroot to advantage
during fifty years, found himself able at last to
fold his shirt-sleeved arms, and from his window,
or often from his doorway, to watch other people
doing what he had done in the little paved Place.</p>
<p class="calibre3">He, of course, had gone to Brittany, Bordeaux,
the Riviera, to be out of the sound of the guns
that had killed his son, and his vacant place had<span id="Page_67" title="[67]"></span>
been scheduled by a careful Maire as available for
billeting. The French army, more impressed by
orders, better trained, more experienced, had
carefully removed every picture, book, or cushion
and stored them in safety—where a British Mess
would have left them—at least until they were
broken or disappeared. At small tables sat two
or three officers in azure, with three or more bars
on the cuff. Dendrecourt halted before one of
these, clicked his heels, and saluted, and asked if
he might present the Captain Dormer, of the
English Army. Colonel Lepage rose with effusion,
excessively English:</p>
<p class="calibre3">“My dear Dormer, charmed to meet you.
Sit down. What can we do for you?”</p>
<p class="calibre3">“I have been sent to see you about a civilian
claim for compensation.”</p>
<p class="calibre3">“<i class="calibre9">L’affaire Vanderlynde!</i>” put in Dendrecourt.</p>
<p class="calibre3">“Aha!” The Colonel tapped his blotting-pad
with a paper knife, and knitted his brows.
“What have you to propose?”</p>
<p class="calibre3">“My General”—Dormer was sufficiently
practised to avail himself of that fiction—“wished
me to explain that this matter has been fully
investigated.”</p>
<p class="calibre3">“Ah! so we may shortly expect to hear that
the guilty individual has been arrested?”</p>
<p class="calibre3">“Well, not exactly an arrest, sir. The whole
affair rests upon a mistake.”</p>
<p class="calibre3">“What sort of mistake?” The other officers<span id="Page_68" title="[68]"></span>
gave up whatever they were doing, and gathered
round at the tone of the last question.</p>
<p class="calibre3">“Upon investigation, it appears that the claim
is not for—er—personal violence.”</p>
<p class="calibre3">“I should be obliged if you would define
personal violence.”</p>
<p class="calibre3">“That would take us rather far afield, sir. All
I want to point out is that the expression ‘<i class="calibre9">La
Vierge</i>’ does not refer to Mademoiselle Vanderlynden,
but to an image in a shrine.”</p>
<p class="calibre3">There was some beginnings of a titter and
Dormer was conscious that he was blushing
violently. But Colonel Lepage quelled the others
with a look. He had the matter so well in hand
that Dormer began slowly to feel that he must be
one of those political soldiers, whose every act and
speech is dictated by the necessities of some policy,
hatched high up among Foreign Offices and their
ante-rooms, and worked out in detail by underlings
dealing with underlings. Moreover, Dormer
was perfectly conscious that he was a junior officer,
and therefore a splendid target. Colonel Lepage
would not meet him that evening at Mess. He
resigned himself, and the Colonel drew a long
breath, and let himself go.</p>
<p class="calibre3">“Upon my word, it is all very fine for you
others. We are much obliged for the information
as to the meaning of the word <i class="calibre9">Vierge</i>. And
also for being told that no arrest has been made
and that no compensation has been offered.
Unfortunately the matter has gone a good deal<span id="Page_69" title="[69]"></span>
further than you suppose, and we have to furnish
a report to a higher authority, to the French War
Office in fact. The matter is a most serious one.
The claim is for trespass upon private property
not demarcated for billeting under the law of
1873. You follow?”</p>
<p class="calibre3">Dormer held his peace. With the exception
of the word demarcated, the Colonel’s English
was as good as his own and many times more
voluble. He contented himself with thinking
“Cock—cock—cock—cock pheasant!”</p>
<p class="calibre3">“Then there is the actual damage to the fabric.
You may not be aware that such an object is held
in great veneration by the owners, more particularly
in Flanders where they are very devout.
But the most serious thing of all was the treatment
accorded to the Mayor when he was—with
the most perfect legality—called in by the claimant
to take official note of the damage. This functionary
was grossly insulted by the English troops
and I regret to say that these occurrences are far
too frequent. Only last Easter at Bertezeele, the
procession of the Religious Festival was the object
of laughter of the troops, who may not be aware
that the inhabitants attach great importance to
such matters, but who should be so instructed by
their officers. And at Leders-cappell only last
week, the Mayor of that Commune also was
insulted in the middle of his official duties.
These incidents are very regrettable and must be
checked. Therefore I regret to say that your<span id="Page_70" title="[70]"></span>
explanation is valueless. Perhaps you will be so
good as to convey this to your General?”</p>
<p class="calibre3">Dormer had a feeling that whatever he said would
make no earthly difference, so he merely muttered:</p>
<p class="calibre3">“Very good, sir,” and turned on his heel.</p>
<p class="calibre3">Walking their horses down the hill from Flan,
Dendrecourt said:</p>
<p class="calibre3">“My word, he was in a state of mind, wasn’t
he? our Colonel.”</p>
<p class="calibre3">Dormer had the clearest possible presentiment
that the moment the door closed upon them, the
Colonel had said ‘Pan’ in imitation of a cork
being snapped into a bottle, and that all the rest of
the officers had laughed. So he said:</p>
<p class="calibre3">“What on earth is behind all this, Dendrecourt?”</p>
<p class="calibre3">“Why, nothing, except the dignity of France.”</p>
<p class="calibre3">“The whole job is only worth a pound or two.
I’d have paid it out of my own pocket rather than
have all this about it.”</p>
<p class="calibre3">“Well, of course, you may have enough money
to do it, but, my dear Dormer, a few pounds in
England is a good many francs in France, not
only in exchange value, but in sentiment. Then,
no one likes having his grandmother’s tomb
broken into——”</p>
<p class="calibre3">“I suppose they will get over it, if they are paid
enough money,” rejoined Dormer, bitterly, for it
was exactly what he had heard before.</p>
<p class="calibre3">“Certainly!” replied Dendrecourt, without
noticing, “but it is most unfortunate at this<span id="Page_71" title="[71]"></span>
moment. There is a religious revival in France.
A new Commander-in-Chief and a new spirit, and
these insults to the religious sentiment are very
trying. Then there is the insult to the Mayor.”</p>
<p class="calibre3">“Oh, devil take the Mayor!”</p>
<p class="calibre3">The Frenchman shrugged. “The devil has
taken all of us, my friend. We are a sacrificed
generation. You find the Mayor of Hondebecq
annoying. So do I. But not more than everything
else. You would not like it if French
soldiers laughed at an English Mayor!”</p>
<p class="calibre3">“My dear Dendrecourt, in England a Mayor
is somebody. Not an old peasant dressed up in
a top hat and an apron, all stars and stripes.”</p>
<p class="calibre3">“Well, here is lunch!” (He called it lernch.)
“I will not join with you, Dormer, in the game of
slanging each other’s nationality.”</p>
<p class="calibre3">Dormer dismounted and handed over his horse,
and went in to lunch, walking wide in the legs and
feeling a fool. The only pleasure he had had was
the male-game-bird appearance of Colonel Lepage.</p>
<p class="calibre3">Of course he said nothing about his morning’s
work, and of course Colonel Birchin had forgotten
it. At the end of the week the Division moved
into the line and he had to go forward with that
fellow Kavanagh to check the workings of communications.
They were “in” four weeks, and
came out in the great cold of January, 1917, and
were moved up near to Doullens. They had not
been out a week before the Colonel sent for him.
He knew what it would be about, but the whole<span id="Page_72" title="[72]"></span>
of his mind being occupied with keeping warm,
he did not care. They were in huts, on a high
plateau. White snow obliterated every colour,
softened every outline as far as the eye could
reach, except where the road to Arras lay black
with its solid ice, the snow that the traffic had
trodden into water, refrozen into a long black
band, scattered with cinders, gravel, chalk, anything
that made it negotiable.</p>
<p class="calibre3">Dormer looked at the collection of huts, with
the obvious pathways between, the obvious,
inevitable collection of traffic, lorries and limbers,
motor-cycles and horses, that accumulated round
any Head-quarters. He wondered how long it
would take the Bosche to discover it in some air-photo
and bomb it all to blazes. Inside Q. office,
in spite of two big stoves in the tiny box of a place,
it was so cold that every one breathed clouds of
steam, and the three officers, and the clerks, sat in
their coats.</p>
<p class="calibre3">“Look here, Dormer!”—the Colonel sounded
as though he had a personal grievance—“just look
what I’ve got from the army.”</p>
<p class="calibre3">It was an official memorandum, emanating
from Army Head-quarters and duly passed
through the Corps to whom they had belonged,
and by Corps to the Division, inquiring what
results had been arrived at in the Vanderlynden
affair, and whether it could not be reported to the
Minister of War that the matter had reached a
satisfactory conclusion.</p>
<p class="calibre3"><span id="Page_73" title="[73]"></span>“I thought you settled all that, while we were
at Louches?”</p>
<p class="calibre3">“Well, sir, I went to see them at Army Head-quarters
and explained, or tried to.”</p>
<p class="calibre3">“You don’t seem to have done any good at all.
In fact it looks as though you and Dendrecourt
had a nice morning ride for nothing.”</p>
<p class="calibre3">“I couldn’t get a word in. It suited somebody’s
politics to blackguard us just then, and I left
it at that. It didn’t seem any use arguing, sir.”</p>
<p class="calibre3">“Well, this must be stopped somehow. We
shall have the French War Minister taking the
matter up with Whitehall, directly, and a nice
figure we shall all cut. I’ve known men sent to
Salonika or Mespot, as company commanders,
for less than this.”</p>
<p class="calibre3">“Very good, sir. What shall I do?”</p>
<p class="calibre3">“Get on with it. Find out who did the beastly
damage, and straf him. Straf somebody, anyhow,
and bring the remains here in a bag. We can show
it to Corps, and they can write a sermon on the
efficiency of the Adjutant-General’s Department.”</p>
<p class="calibre3">“Yessir. If you refer to the correspondence
you will see that the name of the unit is mentioned.”</p>
<p class="calibre3">Dormer stood perfectly still, while his superior
officer turned over the closely written, printed or
typed sheets. His face was carefully veiled in
official blankness. He had an idea.</p>
<p class="calibre3">“Well, here you are,” the Colonel was saying,
“469 Trench Mortar Battery. You’ll have to go<span id="Page_74" title="[74]"></span>
and see ’em. You ought to have done so long
before!”</p>
<p class="calibre3">Dormer could not help adding, maliciously:</p>
<p class="calibre3">“Wouldn’t it be sufficient if I were to send ’em
a chit, sir?”</p>
<p class="calibre3">“No, it wouldn’t. We’ve had quite enough of
this procrastination. It’ll land us all in a nice
hole, if we’re not careful. You go and see them
and insist on getting to the bottom of it.”</p>
<p class="calibre3">“Yes, sir. The order of battle will give their
position.”</p>
<p class="calibre3">“I’ll see to that. I’ll have it looked up and let
you know in the morning.”</p>
<p class="calibre3">“Yes, sir.” He went back to his hut, delighted.</p>
<p class="calibre3">Escape. Escape. Even the illusion of escape
for a few hours, it must be at least that, for if the
469 Trench Mortar Battery were in the same
Division, the same Corps even, he would have
heard of them. They must be at least a day’s
journey away, and he would be able to get away
from the blasting and withering boredom for at
least that. Colonel Birchin, a regular, who had
been on various Staff appointments since the very
early days, had no conception how personnel
changed and units shifted, and unless he (Dormer)
were very much mistaken, it would be a jolly old
hunt. So much the better. He would have his
mind off the War for a bit.</p>
<p class="calibre3">The reply came from Corps that, according to
the order of battle, 469 Trench Mortar Battery<span id="Page_75" title="[75]"></span>
was not in existence, but try Trench Mortar
School at Bertezeele. It was all one to Dormer.
He might simply be exchanging one cold hut for
another, he might travel by rail and lorry instead
of on horse or foot. But at any rate it would be a
different hut that he was cold in and a different
mode of conveyance that jolted him, and that was
something, one must not be too particular in war-time.
So he jumped on a lorry that took him
into Doullens and at Doullens he took train and
went through Abbeville and the endless dumps
and camps by the sea, up to Étaples, where the
dumps and camps, the enormous reinforcement
depôts and mile-long hospitals stretched beside
the line almost into Boulogne, where was a little
pocket, as it were, of French civilian life, going
on undisturbed amid the general swamping of
French by English, on that coast, and of civilian
life by military. Here he got a meal and changed
and went off again up the hill, past Marquise,
and down a long hill to Calais, in the dark, and
then on, in the flat, where the country smelled
different from the Somme, and where the people
spoke differently and the names of the stations
sounded English, and where there were French
and Belgian police on the platforms.</p>
<p class="calibre3">He slept and woke at St. Omer, and slept again
and woke to find all the lights out and a general
scurry and scatteration, with the drone of aeroplanes
and the continual pop-popping of anti-aircraft
fire. Then came the shrieking whirr and<span id="Page_76" title="[76]"></span>
sharp crash of the first bomb, with its echo of
tinkling glass, barking of dogs, and rumour of
frightened humanity.</p>
<p class="calibre3">Like most people accustomed to the line,
Dormer regarded the bombing of back billets as a
spectacle rather than as one of the serious parts of
warfare, and got out to stroll about the platform
with officers going up as reinforcements. They
exchanged cigarettes and news and hardly stopped
to laugh at the horrified whisper of the R.T.O.,
“Don’t light matches here!” It was soon over,
like all bombing. If you were hit you were hit,
but if you weren’t hit in the first minute or two,
you wouldn’t be, because no plane could stay
circling up there for very long, and the bomber was
always more frightened than you were. Then
the train moved on, and Dormer could feel on each
side of him again the real camp life of units just
behind the line, mule standings, gun parks, and
tents and huts of infantry, and services. It was
midnight before he got out at Bailleul. He had
left the camp on the Arras road in the morning,
had made a great loop on the map and reached a
railhead as near the line as he had been twenty-four
hours before. He stumbled up the stony
street to the Officers’ Rest House, drank some
cocoa out of a mug and fell asleep, his head on his
valise.</p>
<p class="calibre3">In the morning he got a lift out to Bertezeele,
and found the Trench Mortar School. He
reflected that it would really be more correct to<span id="Page_77" title="[77]"></span>
say that he took a lift to the Trench Mortar
School, and incidentally touched the village of
Bertezeele. For the fact was that the English
population of the parish exceeded the French
native one. Men of all sorts and conditions
from every unit known to the Army List (and a
good many that had never graced the pages of that
swollen periodical) were drawn into this new device
for improved killing. Dormer himself, one of
those who, since the elementary home camp training
of 1915, had been in or just behind the
trenches, wondered at the complicated ramifications
with which the War was running. Apparently
those curious little brass instruments, the
bane of his life as an infantry platoon commander,
which used to come up behind his line and there,
while totally ineffective in the vital matter of
beating the Germans, were just sufficiently
annoying to make those methodical enemies take
great pains to rob him of his food and sleep for
many ensuing days, were all done away with.</p>
<p class="calibre3">Stokes, whoever he was, but he was certainly a
genius, had effected a revolution. Owing to him,
neat tubes, like enlarged pencil-guards, with a
nail inside the blind end, upon which the cap-end
of the cartridge automatically fell, were being
used, as a hosier might say, in all sizes from youths’
to large men’s. Stokes was branded with genius,
because his invention combined the two essentials—simplicity
with certainty. He had brought the
blunderbuss up to date.</p>
<p class="calibre3"><span id="Page_78" title="[78]"></span>What else were these short-range, muzzle-loading,
old-iron scattering devices? Just
blunderbusses. History was not merely repeating
itself. As the War went on it was moving
backwards. Tin helmets of the days of Cromwell,
bludgeons such as Cœur de Lion used upon
Saladin, and for mere modernity, grenades like
the original British Grenadiers of the song. He
had never had any head for poetry, but he could
remember some of the stuff Kavanagh had sung
in the dug-out. Not tow-row-row. That was
the chorus. Ah! he remembered.</p>
<div class="poetry-container">
<div class="poetry">
<div class="first">“Our leaders march with fusees,</div>
<div class="verse">And we with hand-grenades,</div>
<div class="verse">We throw them from the glacis</div>
<div class="verse">About the enemy’s ears.</div>
<div class="verse">With a tow-row-row,” etc.</div>
</div>
</div>
<p class="calibre3">Well, now we didn’t. If we had grenades we
carried them in aprons, like a market woman, with
a skirt full of apples. And if we had a blunderbuss,
like the guard of the coach in the “Pickwick
Papers,” we kept it, and all the ironmongery that
belonged to it, on a hand-barrow, and pushed it in
front of us like fish-hawkers on a Saturday night.
What a War! Kavanagh was quite right of
course. There was neither decency nor dignity
left in it. Wouldn’t do to admit that though!
And putting on his very best “Good-mornin’-Sah-I-have-been-sent-by-Divisional-Head-quarters”
expression, he asked his way to the “office”<span id="Page_79" title="[79]"></span>
as they were beginning to call the orderly room
in most detachments, and inquired for 469
Battery. Yes. They were to be seen. Orderly
room, as a Corps formation, was distant and
slightly patronizing, but the information was
correct. He could see the Officer commanding
the battery. Certainly he could, as soon as
morning practice was over. That would do.
He made himself as inconspicuous as possible
until he saw the various parties being “fallen in”
on the range, and heard the uncanny ear-tickling
silence that succeeded the ceaseless pop-pop of
practice and then drifted casually into the wooden-chair-and-table
furnished ante-room, where the
month-old English magazines gave one a tremulous
home-sickness, and men who had been mildly
occupied all the morning were drinking all the
vermouth or whisky they could, in the fear of being
bored to the point of mutiny in the afternoon.</p>
<p class="calibre3">There was, of course, the usual springtime
curiosity as to what the year might bring forth, for
every one always hoped against hope that the next
offensive would really be the last. An orderly
wandering among the tables appeared to be looking
for him, and he found himself summoned
before the Officer commanding the School.</p>
<p class="calibre3">Although his appointment was new, Colonel
Burgess was of the oldest type of soldier, the sort
who tell the other fellows how to do it. The
particular sort of war in which he found himself
suited him exactly. He had the true Indian view<span id="Page_80" title="[80]"></span>
of life, drill, breakfast, less drill, lunch, siesta,
sport, dinner, cards. So he ruled the mess cook
with a rod of iron, took disciplinary action if the
stones that lined the path leading to the door of
the ante-room and office were not properly white,
and left the technical side of the business to
Sergeant-instructors who, having recently escaped
from the trenches, were really keen on it.</p>
<p class="calibre3">He received Dormer with that mixture of
flattery due to anyone from Divisional Head-quarters
and suspicion naturally aroused as to
what he (Dormer) might be after. He was
annoyed that he had not heard of Dormer’s
arrival, and hastened to add:</p>
<p class="calibre3">“Not a very full parade this morning, units
come and go, y’know. We can never be quite
sure what we are going to get! What did you
think of our show?”</p>
<p class="calibre3">Dormer realized that the old gentleman was
under the impression he was being spied on:</p>
<p class="calibre3">“I really didn’t notice, sir. I have been sent
to see the Officer commanding 469 Trench
Mortar Battery. Matter of discipline arising out
of a claim for compensation.”</p>
<p class="calibre3">“Oh, ah! Yes indeed. Certainly. See him
now. Sergeant Innes!”</p>
<p class="calibre3">The efficient Scotch Sergeant to be found in all
such places appeared from the outer office.</p>
<p class="calibre3">“Have we anyone here from 469 T.M.B.?”</p>
<p class="calibre3">The officer required was duly produced, and the
Colonel retired to the Mess, leaving them together.<span id="Page_81" title="[81]"></span>
Dormer sized up this fellow with whom
he was thus brought into momentary contact.
This became by necessity almost a fine art, during
years of war. Dormer was fairly proficient.
The fellow opposite to him was of the same sort
as himself. Probably in insurance or stockbroking,
not quite the examination look of the
Civil Service, not the dead certainty of banking.
He had obviously enlisted, been gradually
squeezed up to the point of a Commission, had
had his months in the line and had taken to
Trench Mortars because they offered the feeling
of really doing something, together with slightly
improved conditions (hand-carts could be made
to hold more food, drink and blankets than mere
packs) and was getting along as well as he could.
He heard what Dormer wanted and his face
cleared.</p>
<p class="calibre3">“Why, that’s last April. I couldn’t tell you
anything about that. I was in Egypt!”</p>
<p class="calibre3">“You don’t know of any officer in your unit
who could give some information about the
occurrence?”</p>
<p class="calibre3">“No. There’s only young Sands, beside myself.
He couldn’t have been there.”</p>
<p class="calibre3">“Some N.C.O. then?”</p>
<p class="calibre3">“Heavens, man, where d’you think we’ve
been? All the N.C.O.’s are new since I was with
the crowd!”</p>
<p class="calibre3">“But surely there must be some record of men
who were with your unit?”</p>
<p class="calibre3"><span id="Page_82" title="[82]"></span>“Well, of course, the pay rolls go back to
Base somewhere. But I suppose you can pick the
name and number up from the conduct sheet.”</p>
<p class="calibre3">“You see, I don’t know the man’s name. His
number was given as 6494.”</p>
<p class="calibre3">“That’s a joke, of course. It’s the number
that the cooks sing out, when we hold the last
Sick Parade, before going up the line.”</p>
<p class="calibre3">“Of course it is. You’re right. I ought to
have remembered that, but I’ve been away from
my regiment for some time.” Dormer pondered
a moment, relieved. Then the thought of going
back to the Q. office with nothing settled, and the
queries of the French Mission and the whole
beastly affair hanging over his head, drove him on
again. He made his air a little heavier, more
Divisional, less friendly.</p>
<p class="calibre3">“Well, I’m afraid this won’t do, you know.
This matter has got to be cleared up. It will be
very awkward if I have to go back and inform
Head-quarters that you can’t furnish any information.
In fact, they will probably think it’s a case
of not wanting to know, and make a regular Court
of Inquiry of it.”</p>
<p class="calibre3">He watched the face of the other, and saw in a
moment how well he had calculated. The fellow
was frightened. A mere unit commander, and a
small unit at that! To such a one, of course,
Divisional Head-quarters were something pretty
near omniscient, certainly omnipotent. Dormer
watched the fellow shift in the chair without a<span id="Page_83" title="[83]"></span>
qualm. Let some one else be worried too. He
himself had had worry enough. The face before
him darkened, smirked deferentially, and then
brightened.</p>
<p class="calibre3">“Oh, there was old Chirnside. He might
know.”</p>
<p class="calibre3">“Who was he?”</p>
<p class="calibre3">“Chirnside? He was a sort of a quarter bloke.
It was before we were properly formed, and he
used to look after all our stores and orderly room
business. He had been with the battery since
its formation. We were just anybody, got
together anyhow, chiefly from the infantry, you
remember?”</p>
<p class="calibre3">Dormer saw the other glance at his shoulder
straps and just refrain from calling him sir, poor
wretch. He took down the information and
thanked his friend. Chirnside had apparently
gone to some stunt Corps, to do something
about equipment. That was all right. He
wouldn’t be killed.</p>
<p class="calibre3">Having got thus far, Dormer felt that he had
done a good deal, and went to take his leave of
Colonel Burgess. But he soon found that he was
not to be allowed to get away like that. He was
bidden to stay to lunch. There was no train
from Bailleul until the evening and he was willing
enough. The lunch was good. Food remained
one of the things in which one could take an
interest. He did so. After lunch the Colonel
took him for a walk over the golf course. This<span id="Page_84" title="[84]"></span>
was the margin of land around the range, on
which no cultivation was allowed, and from which
civilians were rigidly excluded, for safety’s sake.
At least during range practice, which took place
every day more or less, in the morning. After
that, of course, they could be without difficulty
excluded for the remainder of the day for a different,
if not for so laudable a purpose.</p>
<p class="calibre3">The Colonel was a fine example of those qualities
which have made an island Empire what it is.
Having spent most of his life from sixteen years
old at Sandhurst, then in India or Egypt, and
finally at Eastbourne, he knew better than most
men how to impose those institutions which he
and his sort considered the only ones that made
civilization possible, in the most unlikely places
and upon the most disinclined of people. Dormer
had seen it being done before, but marvelled more
and more. Just as the Colonel, backed of course
by a sufficient number of his like, and the right
sort of faithful underling, had introduced tennis
into India, duck-shooting into Egypt, and exclusiveness
into Eastbourne, against every condition
of climate or geographical position, native religion
or custom, so now he had introduced golf into
Flanders, and that in the height of a European War.</p>
<p class="calibre3">At the topmost point of the golf course, the
Colonel stopped, and began to point out the
beauties of the spot to Dormer. They were
standing on one of those low gravelly hills that
separate the valley of the Yser from that of the<span id="Page_85" title="[85]"></span>
Lys. Northward, beyond Poperinghe, was a yet
lower and greener ridge that shelved away out of
sight toward Dunkirk. East lay Ypres, in an
endless rumour of war. Southward, the Spanish
towers of Bailleul showed where the road wound
towards Lille, by Armentières. Westward,
Cassel rose above those hillocks and plains,
among the most fertile in the world. But the
Colonel was most concerned with a big square old
farmhouse, that lay amid its barns and meadows,
at a crook in the Bailleul road.</p>
<p class="calibre3">The Colonel’s eyes took on a brighter blue and
his moustache puffed out like fine white smoke.</p>
<p class="calibre3">“I had a lot of trouble with that fellow.” He
pointed to the farm. “Wanted to come and
cultivate the range. I had to get an interpreter
to see him. Said he could grow—er—vegetables
in between the shell-holes. At last we had to
order afternoon practice to keep him off. Then he
wanted this part of the land. Had to move the
guns up and make some new bunkers. Four rounds
makes a bunker, y’know. Come and have tea?”</p>
<p class="calibre3">It was very nice weather for walking, dry and
clear. The Mess had seemed tolerable at lunch,
but Dormer had not been long at tea before he
recollected what he seldom forgot for more than
an hour or so, that it was not tea, one of the
fixed occasions of his safe and comfortable life.
It was a meal taken under all the exigencies of
a campaign—chlorinated water, condensed milk,
army chair, boots and puttees on one, and on<span id="Page_86" title="[86]"></span>
this particular afternoon a temperature below
zero, in an army hut.</p>
<p class="calibre3">The Colonel, of course, occupied the place of
warmth next the stove. The remainder of the
Mess got as near to it as they could. The result
was, that when the Colonel began to question
him as to the object of his visit, and how he had
progressed towards attaining it, everybody necessarily
heard the whole of the conversation. He
now realized that he was telling the tale for the
fourth time. He had told it to Kavanagh, then
to Colonel Birchin. Now he had told it to the
Officer commanding 469 T.M.B. and finally here
he was going over it again. He resented it as
a mere nuisance, but was far from seeing at
that moment the true implication of what he
was doing. The matter was not a State secret.
It was an ordinary piece of routine discipline,
slightly swollen by its reactions in the French
Mission, and by the enormous size and length
of the War.</p>
<p class="calibre3">If he had refused to say why he was there
the Colonel would certainly have put him down
as having been sent by the Division, or some one
even higher, to spy on the activities of the School.
He didn’t want to be labelled as that, so told
what he knew glibly enough. The Colonel waxed
very voluble over it, gave good advice that was
no earthly use, and dwelt at length on various
aspects of the case. The French were grasping
and difficult and superstitious, but on the other<span id="Page_87" title="[87]"></span>
hand, drivers were a rough lot and must be kept
in check. They were always doing damage.
The fellow was quite right of course to look after
his mules. The animals were in a shocking state,
etc., etc., but quite wrong, of course, to damage
civilian property, tradition of the British Army,
since Wellington all the other way, the French
naturally expected proper treatment, etc., etc.</p>
<p class="calibre3">Dormer had heard it all before, from Colonel
Birchin, Major Stevenage and others, with exactly
the same well-meant condescension, and the same
grotesque ineffectiveness. This old Colonel, like
all his sort, couldn’t solve the difficulty nor shut
the French up, nor appease G.H.Q.</p>
<p class="calibre3">Presently, the old man went off to the orderly
room to sign the day’s correspondence, the Mess
thinned and Dormer dozed discreetly, he had had
a poor night and was desperately sleepy. Some
one came to wake him up and offered him a wash,
and he was glad to move, stiff with cold, and
only anxious to pass the time until he could get
the midnight train from Bailleul. They were
very hospitable, made much of him at dinner,
and he ate and drank all he could get, being
ravenous and hoping to sleep through the discomforts
of the long train journey in the dark.
He was getting fairly cheerful by the time the
Colonel left the hut, and only became conscious, in
the intervals of a learned and interesting discussion
of the relative theories of wire-cutting, that a “rag”
was in progress at the other end of the room.</p>
<p class="calibre3"><span id="Page_88" title="[88]"></span>A gunner officer, a young and happy boy who
was still in the stage of thinking the War the
greatest fun out, was holding a mock Court of
Inquiry. Gradually, the “rag” got the better of
the argument and Dormer found himself being
addressed as “Gentlemen of the jury.” A target
frame was brought in by some one to act as a
witness-box, but the gunner genius who presided,
soon had it erected into a sort of Punch and
Judy Proscenium. Then only did it dawn on
Dormer that the play was not Punch and Judy.
It was the Mayor of Hondebecq being derided
by the troops, with a Scotch officer in a kilt
impersonating Madeleine Vanderlynden, and receiving
with the greatest equanimity, various
suggestions that ranged from the feebly funny
to the strongly obscene. O.C. 469 T.M.B.
found a willing column formed behind him which
he had to lead round the table, an infantryman
brought a wastepaper basket to make the Mayor’s
top hat, and in the midst of other improvisations,
Dormer discovered the gunner standing in front
of him with a mock salute.</p>
<p class="calibre3">“Do you mind coming out of the Jury and
taking your proper part?”</p>
<p class="calibre3">It was cheek, of course, but Dormer was not
wearing red tabs, and beside, what was the use
of standing on one’s dignity. He asked:</p>
<p class="calibre3">“What part do I play?”</p>
<p class="calibre3">“You’re Jack Ketch. You come on in the
fourth Act, and land Nobby one on the nob!”</p>
<p class="calibre3"><span id="Page_89" title="[89]"></span>“I see. What are you?”</p>
<p class="calibre3">“Me? I’m the Devil. Watch me devilling,”
and with a long map-roller he caught the
players in turn resounding cracks upon their
several heads.</p>
<p class="calibre3">They turned on him with common consent, and
in the resulting struggle, the table broke and subsided
with the whole company in an ignominious
mass. The dust rose between the grey canvas-covered
walls and the tin suspension lamp rocked
like that of a ship at sea. Everybody picked
themselves up, slightly sobered, and began to
discuss how to get the damage repaired before
the Colonel saw it in the morning.</p>
<p class="calibre3">O.C. 469 T.M.B. stood at Dormer’s elbow:</p>
<p class="calibre3">“We’ve just got time to catch your train.”</p>
<p class="calibre3">“Come on.” Dormer had no intention of
being marooned in this place another day. Outside
a cycle and side-car stood panting. Dormer
did wonder as they whizzed down the rutted
road how long such a vehicle had been upon the
strength of a Trench Mortar School, but after
all, could you blame fellows? They were existing
under War conditions, what more could one ask?</p>
<p class="calibre3">He woke to the slow jolting of the train as
it slowed up in smoky twilight at Boulogne.
He bought some food, and sitting with it in his
hands and his thermos between his knees, he<span id="Page_90" title="[90]"></span>
watched the grey Picard day strengthen over
those endless camps and hospitals, dumps and
training grounds.</p>
<p class="calibre3">He was retracing his steps of the day before,
but he was a step farther on. As he looked at
the hundreds of thousands of khaki-clad figures,
he realized something of what he had to do.
With no name or number he had to find one of
them, who could be proved to have been at a
certain place a year ago. He didn’t want to,
but if he didn’t, would he ever get rid of the
business?</p>
<p class="calibre3">The “rag” of the previous evening stuck in
his head. How true it was. The man who did
the thing was “Nobby” with the number 6494
that was beginning to be folk-lore. Of course
he was. He was any or every soldier. Madeleine
Vanderlynden was the heroine. O.C. 469
T.M.B. was the hero. The Mayor of Hondebecq
was the comic relief, and he, Dormer, was
the villain. He was indeed Jack Ketch, the
spoiler of the fun, the impotent figure-head of
detested “Justice,” or “Law and Order.” And
finally, as in all properly conducted Punch and
Judy shows, the Devil came and took the lot.
What had Dendrecourt said: “The Devil had
taken the whole generation.” Well, it was all
in the play. And when he realized this, as he
slid on from Étaples down to Abbeville, he began
to feel it was not he who was pursuing some unknown
soldier in all that nation-in-arms that had<span id="Page_91" title="[91]"></span>
grown from the British Expeditionary Force, but
the Army—no, the War—that was pursuing him.</p>
<p class="calibre3">When he got out at Doullens, and scrounged
a lift from a passing car, he found himself looking
at the driver, at the endless transport on either
side of the road, at the sentry on guard over the
parked heavies in the yard of the jam factory,
at the military policeman at the cross-roads. One
or other of all these hundreds of thousands knew
all about the beastly business that was engaging
more and more of his mind. One or other of
them could point to the man who was wanted.</p>
<p class="calibre3">He found himself furtively examining their
faces, prepared for covert ridicule and suspicion,
open ignorance or stupidity. He had, by now,
travelled a long way from the first feelings he
had about the affair, when he had thought of
the perpetrator of the damage at Vanderlynden’s
as a poor devil to be screened if possible. He
wouldn’t screen him now. This was the effect
of the new possibility that had arisen. He,
Dormer, did not intend to be ridiculous.</p>
<p class="calibre3">On reaching the Head-quarters of the Division,
he found the War in full progress. That is to
say, every one was standing about, waiting to do
something. Dormer had long discovered that
this was war. Enlisting as he had done at the
outbreak of hostilities, with no actual experience
of what such a set of conditions could possibly
be like, he had then assumed that he was in
for a brief and bitter period of physical discomfort<span id="Page_92" title="[92]"></span>
and danger, culminating quite possibly
in death, but quite certainly in a decisive victory
for the Allies within a few months. He had
graduated in long pedestrian progress of Home
Training, always expecting it to cease one fine
morning. It did. He and others were ordered
to France. With incredible slowness and difficulty
they found the battalion to which they
were posted. Now for it, he had thought, and
soon found himself involved in a routine, dirtier
and more dangerous, but as unmistakably a routine
as that in which he had been involved at home.</p>
<p class="calibre3">He actually distinguished himself at it, by
his thoroughness and care, and came to be the
person to whom jobs were given! Thus had
he eventually, after a twelvemonth, found another
false end to the endless waiting. He was sent
to help the Q. office of Divisional Staff. He
had felt himself to be of considerable importance,
a person who really was winning the War. But
in a few weeks he was as disabused as ever. It
was only the same thing. Clerking in uniform,
with no definite hours, a few privileges of food
and housing, but no nearer sight of the end of
it. The Somme had found him bitterly disillusioned.
And yet even now, after being two
days away from the Head-quarters where his
lot was cast, he was dumbfounded afresh to find
everything going on just as he had left it.</p>
<p class="calibre3">They were all waiting now for orders to go
into a back area and be trained. For, as sure<span id="Page_93" title="[93]"></span>
as the snowdrop appeared, there sprang up in
the hearts of men a pathetic eternal hopefulness.
Perhaps nothing more than a vernal effusion,
yet there it was, and as Dormer reported to
Colonel Birchin, in came the messenger they had
all been expecting, ordering them, not forward
into the line, but backward to Authun, for
training. It was some time before he could get
attention, and when he did, it seemed both to
him and to the Colonel that the affair had lessened
in importance.</p>
<p class="calibre3">“You’ve asked 3rd Eccleton to give you the
posting of this Chirnside?”</p>
<p class="calibre3">“Yessir!”</p>
<p class="calibre3">“Very well. That’s all you can do for the
moment. Now I want you to see that everything
is cleared up in the three Infantry Brigade
camps, and don’t let us have the sort of chits
afterwards that we got at Lumbres, etc.”</p>
<p class="calibre3">So the Vanderlynden affair receded into the
background, and Dormer found before his eyes
once more that everlasting mud-coloured procession,
men, men, limbers, cookers, men, lorries,
guns, limbers, men.</p>
<p class="calibre3">He looked at it this time with different eyes.
His Division was one-fiftieth part of the British
Army in France. It took over a day to get on
the move, it occupied miles of road, absorbed
train-loads of supplies, and would take two days
to go thirty miles. The whole affair was so
huge, that the individual man was reduced and<span id="Page_94" title="[94]"></span>
reduced in importance until he went clean out
of sight. This fellow he was pursuing, or
Chirnside, or anyone who could have given any
useful information about the Vanderlynden claim,
might be in any one of those cigarette-smoking,
slow-moving columns, on any of those springless
vehicles, or beside any of those mules.</p>
<p class="calibre3">He gazed at the faces of the men as they
streamed past him, every county badge on their
caps, every dialect known to England on their
lips, probably the best natured and easiest to
manage of any of the dozen or so national armies
engaged in the War. He was realizing deeply
the difficulty of discovering that particular
“Nobby” who had broken the front of the shrine
at Vanderlynden’s. It was just the thing any
of them would do. How many times had he
noticed their curious tenderness for uncouth
animals, stray dogs or cats, even moles or hedgehogs,
and above all the brazen, malevolent army
mule. He was no fancier of any sort of beast,
and the mule as used in France he had long
realized to have two virtues and two only—cheapness
and durability. You couldn’t kill
them, but if you did, it was easy to get more.
He had been, for a long while now, a harassed
officer, busy shifting quantities of war material,
human, animal, or inanimate, from one place to
another, and had come to regard mules as so
much movable war stores. Added to the fact
that he was no fancier, this had prevented him<span id="Page_95" title="[95]"></span>
from feeling any affection for the motive power
of first-line transport. But he was conscious
enough that it was not so with the men—the
“other ranks” as they were denominated in all
those innumerable parade states and nominal
rolls with which he spent his days in dealing.</p>
<p class="calibre3">No, what the fellow had done was what most
drivers would do. That queer feeling about
animals was the primary cause of the whole affair.
Then, balancing it, was the natural carelessness
about such an object as a shrine—this same brown-clothed
nation that defiled before him, he knew
them well. As a churchwarden, he knew that
not ten per cent of them went inside a place of
worship more than three or four times in the
whole of their lives. Baptism for some, marriage
for a good proportion, an occasional assistance
at the first or last rite of some relative, finally,
the cemetery chapel, that was the extent of their
church-going.</p>
<p class="calibre3">A small number, chiefly from the North or from
Ireland, might be Catholics, but also from the
north of Ireland was an equal number of violent
anti-Catholics, and it was to this latter section
that he judged the perpetrator of the outrage
to belong. No, they would see nothing, or at
best something to despise, in that little memorial
altar, hardly more than an enlarged tombstone,
in the corner of a Flemish pasture. It was
strange if not detestable, it was foreign; they
never saw their own gravestones, seldom those of<span id="Page_96" title="[96]"></span>
any relative. He sympathized with them in
that ultra-English sentimentality, that cannot
bear to admit frankly the frail briefness of human
life. And so the thing had happened, any of
them might have done it, most of them would
do it, under similar circumstances.</p>
<p class="calibre3">The tail of the last column wound out of
B camp, the N.C.O. he took with him on these
occasions was reporting all clear, and might he
hand over to the advance party of the incoming
Division. Dormer gave him exact orders as to
what to hand over and obtain a signature for,
and where to find him next, for he did not believe
in allowing an N.C.O. any scope for imagination,
if by any possibility such a faculty might have
survived in him.</p>
<p class="calibre3">The weather had broken, and he jogged along
in the mud to C camp and found it already
vacated, but no advance party ready to take over,
and resigned himself to the usual wait. He
waited and he waited. Of course, he wasn’t
absolutely forced to do so. He might have left
his N.C.O. and party to hand over. He might
have cleared them off and left the incoming
Division to shift for itself. That had been done
many a time in his experience. How often, as
a platoon commander, had he marched and
marched, glancing over his shoulder at tired
men only too ready to drop out, marched and
marched until at length by map square and horse
sense, and general oh-let’s-get-in-here-and-keep-any-one-else-out,<span id="Page_97" title="[97]"></span> 
he had found such a camp, a
few tents subsiding in the mud, a desolate hut
or two, abandoned and unswept, places which
disgusted him more than any mere trench or
dug-out, because they were places that people
had lived in and left unclean.</p>
<p class="calibre3">He had never experienced such a thing before
he came into the army. His nice middle-class
upbringing had never allowed him to suspect
that such places existed. And now that he was
Captain Dormer, attached H.Q. Nth Division,
he endeavoured to see that they did not. So he
hung about intending to see the thing done
properly. He got no encouragement. He knew
that when he got back to the Division Colonel
Birchin would simply find him something else
to do, and the fact that no complaints followed
them, and that the incoming Division had a
better time than they would otherwise have had,
would be swallowed up in the hasty expedience
of the War. Still, he did it, because he liked
to feel that the job was being properly done.
To this he had been brought up, and he was not
going to change in war-time.</p>
<p class="calibre3">As he hung about the empty hut, he had
plenty of time for reflection. His feet were
cold. When would he get leave? What a
nuisance if these d——d people who were relieving
him didn’t turn up until it was dark.
The February day was waning. Ah, here they
were. He roused himself from the despondent<span id="Page_98" title="[98]"></span>
quiescence of a moment ago, into a crisp authoritative
person from Divisional Head-quarters.
Never was a camp handed over more promptly.
He let his N.C.O. and men rattle off in the limber
they had provided themselves with. He waited
for a car. There was bound to be no difficulty
in getting a lift into Doullens, and if he did not
find one immediately there, he would soon get
a railway voucher. As he stood in the gathering
dusk his ruminations went on. If it were not
for the War, he would be going home to tea,
real proper tea, no chlorine in the water, milk
out of a cow, not out of a tin, tea-cakes, some
small savoury if he fancied it, his sister with
whom he lived believing in the doctrine. “Feed
the beast!” After that, he would have the
choice of the Choral Society or generally some
lecture or other. At times there was something
on at the local theatre, at others he had Vestry
or Trust meetings to attend. Such employments
made a fitting termination to a day which he
had always felt to be well filled at a good, safe,
and continuous job, that would go on until he
reached a certain age, when it culminated in a
pension, a job that was worth doing, that he
could do, and that the public appreciated.</p>
<p class="calibre3">Instead of all this, here he was, standing
beside a desolate Picard highway, hoping that
he might find his allotted hut in time to wash in
a canvas bucket, eat at a trestle table and finally,
having taken as much whisky as would wash<span id="Page_99" title="[99]"></span>
down the food, and help him to become superior
to his immediate circumstances, to play bridge
with those other people whom he was polite
to, because he had to be, but towards whom he
felt no great inclination, and whom he would
drop without a sigh the moment he was demobbed.</p>
<p class="calibre3">Ah! Here was the sort of car. He stepped
into the road and held up his hand. The car
stopped with a crunch and a splutter. They
were going as far as Bernaville. That would
suit him well. He jammed into the back seat
between two other people, mackintoshed and
goggled, and the car got under way again. Then
he made the usual remarks and answered the
usual inquiries, taking care to admit nothing,
and to let his Divisional weight be felt. Finally
he got down at a place where he could get a
lorry lift to H.Q.</p>
<p class="calibre3">His servant had laid out some clean clothes
in the Armstrong hut. For that he was thankful.</p>
<p class="calibre3">The Division now proceeded to train for
the coming offensive. “Cultivators” had been
warned off a large tract of land, which was partly
devoted to “Schools,” at which were taught
various superlative methods of slaughter, partly
to full-dress manœuvres over country which
resembled in physical features the portion of
the German line to be attacked. The natural<span id="Page_100" title="[100]"></span>
result was that if any area larger than a tennis
court was left vacant, the “cultivators” rushed
back and began to cultivate it. Hence arose
disputes between the peasants and the troops,
and the General commanding the 556 Brigade
had his bridle seized by an infuriated female
who wanted to know, in English, why he couldn’t
keep off her beans.</p>
<p class="calibre3">The matter was reported to G. office of Divisional
Head-quarters, who told the A.P.M., who
told the French Liaison Officer, who told an
Interpreter, who told the “cultivators” to keep
off the ground altogether, whether it were in use
or not. In revenge for which conduct the
“cultivators” fetched the nearest gendarme, and
had the Interpreter arrested as a spy, and tilled
the land so that the C.R.A. Corps couldn’t find
the dummy trenches he was supposed to have
been bombarding, because they had all been
filled up and planted. So that he reported the
matter to Corps, who sat heavily on Divisional
Head-quarters, G. office, for not keeping the
ground clear. The A.P.M. and French Mission
having been tried and failed, Q. office had the
brilliant idea of “lending” Dormer to G., upon
the well-tried army principle that a man does a
job, not because he is fit, but because he is not
required elsewhere.</p>
<p class="calibre3">So Dormer patrolled the manœuvre area,
mounted on the horses of senior officers, who
were too busy to ride them. He did not object.<span id="Page_101" title="[101]"></span>
It kept him from thinking. He was, by now,
well acquainted with manœuvre areas, from near
Dunkirk to below Amiens. It was the same
old tale. First the various schools. The Bombing
Instructor began with a short speech:</p>
<p class="calibre3">“It is now generally admitted that the hand-grenade
is the weapon with which you are going
to win this War!”</p>
<p class="calibre3">The following day in the bayonet-fighting
pitch, the instructor in that arm began:</p>
<p class="calibre3">“This is the most historic weapon in the
hands of the British Army. It still remains the
decisive factor on the field!”</p>
<p class="calibre3">And the day following, on the range, the
Musketry expert informed the squad:</p>
<p class="calibre3">“Statistics show that the largest proportion
of the casualties inflicted on the enemy are
bullet wounds.”</p>
<p class="calibre3">Dormer was not unkind enough to interrupt.
He did not blame those instructors. Having,
by desperately hard work, obtained their positions,
they were naturally anxious to keep them. But
his new insight and preoccupation, born of the
Vanderlynden affair, made him study the faces
of the listening squads intently. No psychologist,
he could make nothing of them. Blank,
utterly bored in the main, here and there he
caught sight of one horrified, or one peculiarly
vindictive. The main impression he received
was of the sheer number of those passive listening
faces, compared with the fewness of the N.C.O.’s<span id="Page_102" title="[102]"></span>
and instructors. So long as they were quiescent,
all very well. But if that dormant mass came to
life, some day, if that immense immobility once
moved, got under way, where would it stop?</p>
<p class="calibre3">It was the same with the full-dress manœuvres.
Dormer had never been taken up with the honour
and glory of war. He was going through with
this soldiering, which had been rather thrust upon
him, for the plain reason that he wanted to get
to the end of it. He considered that he had
contracted to defeat the Germans just as, if he
had been an iron firm, he might have contracted
to make girders, or if he had been the Post
Office, he would have contracted to deliver letters.
And now that he watched the final processes of
the job, he became more than ever aware that
the goods would not be up to sample. How
could they be? Here were men being taught
to attack, with the principal condition of attack
wanting. The principal condition of an attack
was that the other fellow hit you back as hard
as he could. Here there was no one hitting
you back. He wondered if all these silent and
extraordinarily docile human beings in the ranks
would see that some day. He looked keenly
at their faces. Mask, mask; mule-like stupidity,
too simple to need a mask; mask and mask
again; one with blank horror written on it,
one with a devilish lurking cunning, as if there
might be something to be made out of all this
some day; then more masks.</p>
<p class="calibre3"><span id="Page_103" title="[103]"></span>He wondered, but he did not wonder too
unhappily. He was beginning to feel very well.
Away from the line, the hours were more regular,
the food somewhat better, the horse exercise did
him good. There was another reason which
Dormer, no reader of poetry, failed altogether
to appreciate. Spring had come. Furtive and
slow, the Spring of the shores of the grey North
Sea came stealing across those hard-featured
downs and rich valleys. Tree and bush, blackened
and wind-bitten, were suddenly visited with
a slender effusion of green, almost transparent,
looking stiff and ill-assorted, as though Nature
were experimenting.</p>
<p class="calibre3">Along all those ways where men marched to
slaughter, the magic footsteps preceded them,
as though they had been engaged in some beneficent
work, or some joyful festival. To Dormer
the moment was poignant but for other reasons.
It was the moment when the culminating point
of the Football Season marked the impending
truce in that game. He did not play cricket. It
was too expensive and too slow. In summer he
sailed a small boat on his native waters. Instead,
he was going to be involved in another offensive.</p>
<p class="calibre3">The Division left the manœuvre area and went
up through Arras. Of course, the weather broke
on the very eve of the “show.” That had
become almost a matter of routine, like the shelling,
the stupendous activities of railways and
aeroplanes, the everlasting telephoning. Again<span id="Page_104" title="[104]"></span>
Dormer saw going past him endlessly, that stream
of men and mules, mules and men, sandwiched
in between every conceivable vehicle, from tanks
to stretchers. When, after what communiqués
described as “continued progress” and “considerable
artillery activity,” it had to be admitted
that this offensive, like all other offensives, had
come to a dead stop, Dormer was not astonished.
For one thing, he knew, what no communiqué
told, what had stopped it. The Germans? No,
capable and determined as they were. The thing
which stopped it was Mud. Nothing else. The
shell-fire had been so perfect, that the equally
perfect and necessarily complicated preparations
for going a few hundred yards farther, could not
be made. The first advance was miles. The next
hundreds of yards. The next a hundred yards.</p>
<p class="calibre3">Then the Bosche got some back. Then everything
had to be moved up to make quite certain
of advancing miles again. And it couldn’t be
done. There was no longer sufficient firm
ground to bear the tons of iron that alone could
help frail humanity to surmount such efforts.</p>
<p class="calibre3">For another thing, he could not be astonished.
For weeks he worked eighteen hours a day, ate
what he could, slept when he couldn’t help it.
Astonishment was no longer in him. But one
bit of his mind remained, untrammelled by the
great machine of which he formed an insignificant
part. It was a bit of subconsciousness that was
always listening for something, just as, under<span id="Page_105" title="[105]"></span>
long-range, heavy-calibre bombardment, one
listened and listened for the next shell. But the
particular detached bit of Dormer was listening
and listening for something else. Watching and
watching, too, all those faces under tin helmets,
and just above gas-mask wallets, all so alike
under those conditions that it seemed as difficult
to pick out one man from another as one mule
from another. Listening for one man to say
“I am the one!” to be able to see him, and
know that at last he had got rid of that job at
Vanderlynden’s. But nothing happened. It was
always just going to happen.</p>
<p class="calibre3">At length the Division moved right up into
the coal-fields and sat down by a slag heap near
Béthune. Then Colonel Birchin called to him
one morning across the office: “I say, Dormer,
I’ve got the whereabouts of that fellow Chirnside.
He’s near Rheims. You’ll have to go.”</p>
<p class="calibre3">Dormer went. For two whole days he travelled
across civilian France. France of the small
farm, the small town, and the small villa. Far
beyond the zone of the English Army, far beyond
the zone of any army, he passed by Creil to
Paris, and from Paris on again into a country
of vine-clad hills above a river. He was in a
part where he had never been as a soldier, never
gone for one of those brief holidays to Switzerland
he had sometimes taken. It caused him much
amusement to think of the regular Calais—Bâle
express of pre-War days. If they would only<span id="Page_106" title="[106]"></span>
run that train now, how it would have to zig-zag
over trenches, and lines of communication.</p>
<p class="calibre3">He entered the zone of a French Army. On
all sides, in the towns and villages, in the camps
and manœuvre areas, he saw blue-coated men,
and stared at them, with the same fascinated
interest as he now felt, in spite of himself, in
spite of any habit or tradition or inclination, in
his own khaki variety. These fellows carried
more on their backs, had far less transport.
His general impression was of something grimmer,
more like purgatory, than that which English
troops gave him. The physical effort of the
individual was greater, his food, pay and accommodation
less. And there was none of that
extraordinary volunteering spirit of the Kitchener
Armies, the spirit which said: “Lumme, boys,
here’s a war. Let’s have a go at it!” The
French had most of them been conscripted, had
known that such a thing might, probably would
happen to them, had been prepared for it for
years. They had not the advantage of being
able to say to themselves: “Well, I jolly well
asked for it. Now I’ve got it!”</p>
<p class="calibre3">A saturnine fate brooded over them. He
noticed it in the railway and other officials he
met. They were so much more official.
R.T.O.’s and A.P.M.’s—or the equivalent of
them, he supposed—who surveyed his credentials,
and passed him on to the place where he was
going, did so with the cynical ghost of amusement,<span id="Page_107" title="[107]"></span>
as who should say: “Aha! This is you.
You’re going there, are you? You might as
well go anywhere else.”</p>
<p class="calibre3">Eventually, in a stony village, beneath a pine-clad
ridge, he found the familiar khaki and brass,
the good nature and amateurishness of his own
sort. He stepped out of the train and across a
platform and with a curious pang, almost of
home-sickness, found himself in England. Here
was the superior corporal in slacks from the
orderly room. Here were the faultless riding
horses, being exercised. There was nothing like
them in all the blue-coated armies through which
he had passed. The Commandant to whom he
reported, treated him partly as an officer reporting,
partly as a nephew, asked amused questions
about billets in Flanders, who was doing such-and-such
a job with Corps, what were the prospects
of leave, and above all, did Dormer play
bridge? He did. Ah! Then the main necessities
of modern warfare were satisfied.</p>
<p class="calibre3">And as he found his billet and changed his
clothes, Dormer reflected how right it all was.
What was the good of being officious and ill-tempered?
What was the good of being energetic
even? Here we all were, mixed up in this
inferno. The most sensible, probably the most
efficient thing to do, was to forget it every night
for a couple of hours, and start fresh in the
morning. Chirnside was away with his detachment,
but would be back shortly. In the meantime<span id="Page_108" title="[108]"></span>
the Commandant hoped Dormer would join
his Mess. The billet was comfortable and
Dormer made no objection. On the contrary,
he settled down for a day or two with perfect
equanimity. It was always a day or two nearer
the inevitable end of the War, which must come
sometime, a day or two without risk, and actually
without discomfort. What more could one ask?</p>
<p class="calibre3">The Commandant, Major Bone, was a fine-looking
man, past middle age, with beautiful grey
hair and blue eyes with a twinkle. His height
and carriage, a certain hard-wearing and inexpensive
precision about his uniform, suggested an
ex-guards Sergeant-major. It was obvious that
he had spent all his life in the army, took little
notice of anything that went on outside it, and
felt no qualms as to a future which would be
provided for by it. He was one of those men
with whom it was impossible to quarrel, and
Dormer pleased him in the matter of blankets.
The Major offered some of those necessities to
Dormer, who was obliged to reply that he had
six and feared his valise would hold no more.
He had won the old man’s heart.</p>
<p class="calibre3">The Major had fixed his billet in a little house
belonging to the representative of some firm
auxiliary to the wine trade. The little office had
become his office. Orders, nominal rolls, lists of
billets and maps hung over the advertisements of
champagne, and photographs of Ay and Epernay.
On the other side of the hall, the little dining-room<span id="Page_109" title="[109]"></span> 
suited the Major admirably, as his Mess.
It had just that substantial stuffiness that he
considered good taste. The chairs and table
were heavy, the former upholstered in hot crimson,
as was the settee. Upon the mantelpiece, and
upon pedestals disposed wherever there was room
and sometimes where there was not, were bronze
female figures named upon their bases “Peace,”
“Chastity,” “The Spirit of the Air.” Dormer
did not admire them. They were nude. As if
this were not enough they had their arms either
before them or behind them, never at their sides,
which seemed to him to aggravate the matter.
Together with a capacious sideboard, full of
glass and china, <i class="calibre9">couronnes de noces</i> and plated
ware, all securely locked in, these decorations
made it almost impossible to move, once the
company was seated at table.</p>
<p class="calibre3">Indeed, during the winter, the Major complained
he had been in the position of having
one place frozen at the door, and one roasted
next the Salamander anthracite stove. But with
the milder weather, things were better, for the
two big casement windows could be opened, and
filled the room with sweet country air in a moment;
they gave on to the street which was merely a
village street, and across the road, over the wall
was a vineyard. The Mess consisted of the
Major, Doctor, Ordnance Officer, and Chirnside,
whose place Dormer temporarily took.
There they were a happy little family, removed<span id="Page_110" title="[110]"></span>
far from the vexations attending larger and smaller
formations, isolated, with their own privileges,
leave list, and railway vouchers, as pretty a corner
as could be found in all that slow-moving mass
of discomfort and ill-ease that was the War.</p>
<p class="calibre3">On the third day, Dormer’s conscience made
him inquire how long Chirnside would be.
“Not long,” was the reply. “You can hear
what’s going on?” He could indeed. For two
days the earth and air had been atremble with
the bombardment. French people in the village,
and the French soldiers about the place had a
sort of cocksure way of saying “<i class="calibre9">Ça chauffe?</i>”
Indeed, the offensive had been widely advertised
and great things were expected of it.</p>
<p class="calibre3">Then finally Chirnside did return. Dormer
had been doing small jobs for the Major all day,
because idleness irked him, and on coming back
to change, found a grizzled oldish man, thin
and quiet, a slightly different edition of the Major,
the same seniority, the same ranker traditions,
but memories of India and Egypt instead of
Kensington and Windsor. Dormer listened
quietly while the two old soldiers discussed the
offensive. There was no doubt that it was an
enormous and costly failure. That hardly impressed
him. He was used to and expected it.
But he had never before seen an offensive from
outside. He had always been in them, and too
tired and short of sleep, by the time they failed,
to consider the matter deeply. But this time<span id="Page_111" title="[111]"></span>
he listened to the conversation of the two old
men with wonder mixed with a curious repulsion.
They were hard working, hospitable, but they
had the trained indifference of the regular soldier
that seemed to him to be so ominous. In the
regular army, where every one shared it, where
it was part of a philosophy of life derived from
the actual conditions, and deliberately adopted
like a uniform, all very well. But no one knew
better than Dormer that none of the armies of
1917 contained any appreciable percentage of
regulars, but were, on the other hand, composed
of people who had all sorts of feelings to be
considered and who had not the slightest intention
of spending their lives in the army. Not for
the first time did he wonder how long they
would stand it.</p>
<p class="calibre3">The Doctor and Ordnance Officer being busy
sorting casualties and replacing stores, there was
no bridge that evening and he was able to approach
Chirnside as to the object of his journey. The
old man heard him with a sort of quizzical interest,
but was evidently inclined to be helpful, twisted his
grey moustache points and let his ivory-yellow
eyelids droop over his rather prominent eyes.</p>
<p class="calibre3">“Spanish Farm. April 1916. Oh, aye!”</p>
<p class="calibre3">“Could you recall an incident that occurred
there. Damage to a little chapel in the corner
of the pasture where the roads met. A driver
wanted to shelter his mules and broke into the
place?”</p>
<p class="calibre3"><span id="Page_112" title="[112]"></span>Chirnside thought hard, looking straight at
Dormer. It was obvious to Dormer that the
old man was thinking, with army instinct, “Here,
what’s this I’m getting involved in? No you
don’t,” and hastened to reassure him.</p>
<p class="calibre3">“It’s like this. The case has become unfortunately
notorious. The French have taken
it up very strongly. You know what these
things are, once they become official test cases.
We’ve got to make an arrest and probably pay
compensation as well, but at present our people
at Base are sticking out for treating it as a matter
of discipline. The unit was the 469 T.M.B.,
but there have been so many casualties that no one
can tell me the name of the driver who did it.”</p>
<p class="calibre3">Dormer was thinking: “There, that’s the
umpteenth time I’ve told the yarn, and what
good is it?” When suddenly he had a stroke
of genius:</p>
<p class="calibre3">“Of course, they’ve got hold of your name.”</p>
<p class="calibre3">It succeeded remarkably well. A sort of
habitual stiffening was obvious in the Army-worn
old face in front of him. Chirnside shifted
his legs.</p>
<p class="calibre3">“I can’t tell y’much about it. I don’t know
the chap’s name or number, and I expect all
the rolls are destroyed. Anyway he might not
be on them, for he wasna’ a driver!”</p>
<p class="calibre3">Chirnside was relapsing into his native Scotch,
but Dormer didn’t notice. He had got a clue.</p>
<p class="calibre3">“What was he then?”</p>
<p class="calibre3"><span id="Page_113" title="[113]"></span>“He had been servant to young Fairfield,
who was killed.”</p>
<p class="calibre3">“You don’t remember Fairfield’s regiment.
That might help us?”</p>
<p class="calibre3">“No, I don’t, and it wouldn’t help you, for
he came out to Trench Mortars, and not with
his own crowd. This servant of his he picked
up at Base, or from some employment company.”</p>
<p class="calibre3">“What on earth was he doing with those
mules?”</p>
<p class="calibre3">“What could you do with ’em? The driver
was killed and the limber smashed to matchwood.
The feller had nothing to do, so he did that!”</p>
<p class="calibre3">“You don’t remember what happened to him
after that?”</p>
<p class="calibre3">“Um—I think he went as young Andrews’
servant.”</p>
<p class="calibre3">“Ah! What did he come from?”</p>
<p class="calibre3">“Andrews? Gunner, he was!”</p>
<p class="calibre3">“Thanks. That may help. You saw the
row when the Mayor of the village came to
certify the damage?”</p>
<p class="calibre3">“Aye, there was some blethers about the business.
You couldna’ wonder. The old feller
was got up like a Tattie Bogle. The men had
had no rest, and were going straight back to the
line. They marched all right, but you couldn’t
keep them from calling names at such a Guy—young
troops like that!”</p>
<p class="calibre3">“You couldn’t describe Andrews’ servant to
me?”</p>
<p class="calibre3"><span id="Page_114" title="[114]"></span>“No. He looked ordinary!”</p>
<p class="calibre3">A mistake, of course, no use to ask old Chirnside
things like that. A third of a century in
the army had long ago drilled out of him any
sort of imagination he might ever have had. He
was just doing a handsome thing by a brother
officer in remembering at all. His instinct was
obviously to know nothing about it. But, piqued
by the novelty of Commissioned rank, he went
on: “Yes, I can tell you something. That
feller had a grievance. I remember something
turning up in one of his letters, when we censored
’em. Lucky spot when you think how most of
the censoring was done.”</p>
<p class="calibre3">“I should think so. What was it?”</p>
<p class="calibre3">“Couldn’t say now. Grievance of some sort.
Didn’t like the army, or the War, or something.”</p>
<p class="calibre3">Dormer sat down and wrote out the information
obtained and made his preparations to rejoin the
Division. The Major said: “Oh, no hurry,
stop another day, now you’re here!” And all
that evening, as he thought and wrote, and
tried to believe this fatal business a step nearer
completion, he heard the two old soldiers, like
two good-natured old women, gossiping. Each
expected the other to know every camp or barrack
in which he had lain, each named this or that
chance acquaintance, made any time those thirty
years, anywhere in the world, as though the other
must know him also. Often this was the case,
in which they both exclaimed together, “Ah,<span id="Page_115" title="[115]"></span>
nice feller, wasn’t he?” Or, if it were not the
case, the other would rejoin, “No, but I knew
So-and-so, of the sappers,” and probably the
second shot would hit the mark. It could hardly
fail to do so in the old close borough of the
Regular Army. And then they would exclaim
in unison again.</p>
<p class="calibre3">Dormer was as impressed as he ever was by
any member of the Professional Army. They
knew how to do it. He would never know.
The army was their God and King, their family
and business. In a neat circle they went, grinding
out the necessary days to their pensions.
The present state of Europe, while verbally regretted
or wondered at, did not scratch the
surface of their minds. How could it? It had
been a golden opportunity for them. It made
the difference to them and to any human wife
or family they might have accreted, between
retiring on Commissioned pay-scale, or taking a
pub or caretaker’s place, as the ex-Sergeant-major
they would otherwise have been. But
there was charm in their utter simplicity. Nothing
brutal, very little that was vain, and some
nicely acquired manners.</p>
<p class="calibre3">The offensive of the French Army, in the
machinery of which they had their places, moved
them not at all. Chirnside casually mentioned
that he gathered it had been a big failure. Dormer
expected to hear him recite some devastating
tale of misdirected barrage, horrible casualties<span id="Page_116" title="[116]"></span>
or choked communications. Nothing so graphic
reached him. The old man had simply attended
to his job, and when he found that the troops
were returning to the same billets, drew his
own conclusions. That was all. Dormer was
horrified, but no one could be horrified long
with Chirnside. Of course, he didn’t mind how
long the War went on.</p>
<p class="calibre3">Having completed his preparations, Dormer
went up to his little room and was soon asleep.
He was in fine condition and thoroughly comfortable,
and was astonished after what appeared
to be a very short interval, to find himself wide
awake. There was no mistaking the reason. It
was the row in the street. He pulled on his
British Warm and went to look. It was quite
dark, but he could make out a confused crowd
surging from side to side of the little street,
could see bayonets gleaming, and could hear a
clamour of which he could not make out a word.
It was like nothing he had ever heard in the
War, it recalled only election time in his native
city, the same aimless shuffling feet, the same
confusion of tongues, the same effervescence,
except that he had instinct enough to know
from the tones of the voices that they were raised
in lamentation, not triumph. He was extremely
puzzled what to do, but clear that no initiative
lay with him. For ten minutes he waited, but
the situation did not change. He opened his
door very quietly. Not a sound from the Major.<span id="Page_117" title="[117]"></span>
From Chirnside, opposite, heavy regular breathing.
Above, in the attics, the low cockney brevity of
soldier servants discussing something with the
detachment of their kind. Reassured, he closed
the door, and got back into his blankets. The
noise was irritating but monotonous. He fell
asleep. He next awoke to the knocking of his
servant bringing his morning tea, and clean boots.</p>
<p class="calibre3">“What was all that row in the night?”</p>
<p class="calibre3">“Niggers, sir.”</p>
<p class="calibre3">“What do you mean?”</p>
<p class="calibre3">“French coloured troops, sir. They got it
in the neck seemingly. They don’t half jabber.”</p>
<p class="calibre3">Major Bone was more fully informed. There
was no doubt that the French had had a nasty
knock. Black troops were coming back just
anyhow, out of hand, not actually dangerous, the
old soldier allowed it to be inferred, but a nuisance.
What struck him most forcibly was the dislocation
of the supply services. Defeat he accepted, but
not unpunctuality.</p>
<p class="calibre3">“These Africans are besieging the station,
trying to board the trains, and get taken back
to Africa. I can’t get hold of an officer, but
Madame says they’re all killed. She’s in an
awful state. I don’t suppose you’ll get away
to-day!”</p>
<p class="calibre3">He was right enough. Dormer’s servant
shortly returned, humping the valise. The
station was closed, the rolling stock had been
removed. The black troops were swarming<span id="Page_118" title="[118]"></span>
everywhere, collapsing for want of food and sleep,
disorganized and incoherent. Dormer went out
shortly after and verified the state of affairs. He
was not molested, so far had the breakdown
gone, but was the object of what appeared to
him most uncomplimentary allusions, but all
in pidgin-French, too colonial for his fair, but
limited, knowledge of the language. There was
clearly nothing to be done, so far as transport
went, that day, and he resigned himself to spending
his time in the little Mess.</p>
<p class="calibre3">The Doctor and Ordnance Officer appeared
at dinner with reassuring news. The failure of
the offensive had been bad, but the French had
never really lost control and were getting their
people in hand immediately. There was a rumour
that a General who tried to restore order had been
thrown into the river, but it might be only a
tale. Major Bone was contemptuous of the
whole thing. Do—what could they do, a lot
of silly blacks? The French would cut off their
rations and reduce them to order in no time.
Thus the old soldier. But he did not prevent
Dormer going to bed with a heavy heart. To
him it was not so much a French offensive that
had failed. It was another Allied effort, gone
for nothing. His life training in apprehension
made him paint the future in the gloomiest
colours. Where would fresh men be obtained
from? Whence would come the spirit—what
they called morale in military circles—to make<span id="Page_119" title="[119]"></span>
another attempt? If neither men nor morale
were forthcoming, would the War drag out to a
stalemate Peace? He had no extravagant theories
for or against such an ending to it. To
him it meant simply a bad bargain, with another
war to make a better one looming close behind
it. And his recent military training had also
received an unaccustomed shock. A new army
enlistment, he had seen nothing of the retreat
from Mons, and was far from being able to
picture March or April 1918, still twelve months
in the future.</p>
<p class="calibre3">For the first time in his life he had seen panic,
confusion, rout. True, it was already stopped,
but that did not expunge from his mind the sight,
the noise, the smell even, of that crowd of black
soldiers who had suddenly ceased to be soldiers,
numbers standing in line, and had so dramatically
re-become men. The staring eyeballs, the
physical collapse, the officer-less medley of uncertain
movement were all new to him, and all
most distressing. Of course, the fellows were
mere blacks, not the best material, and had
probably been mishandled. But under a more
prolonged strain, might not the same thing
happen to others? The Germans were the least
susceptible he judged, the Russians most. What
would he not see, some day, if the War dragged
on?</p>
<p class="calibre3">Whatever narrow unimaginative future his
unadventurous mind conjured up, his far stronger<span id="Page_120" title="[120]"></span>
faculty for getting on with the matter in hand
soon obliterated. He was no visionary. Contemplation
was not in him. Directly the trains
were running he left that cosy little Mess of
Major Bone’s to rejoin. He left off thinking
about the War, and took up his job where he had,
for a moment, allowed it to lie, disregarded under
the stress of new events and strange emotions.</p>
<p class="calibre3">As the train moved on and on through French
lines of communication he was wondering again
about the fellow who had done the trick at
Vanderlynden’s, of how he was to be found, of
how the whole thing would frame itself. These
French chaps, whose transport he saw each side of
him, Army Corps after Army Corps. Biggish men,
several of them, in a round-shouldered fashion,
due partly to their countrified occupation, partly to
their uniform, with its overcoat and cross-straps.
Browner skinned, darker of hair and eye than
our men, they confirmed his long-established
ideas about them, essentially a Southern people,
whose minds and bodies were formed by Biscayan
and Mediterranean influences. They would not
be sentimental about mules, he would wager.
On the other hand, they would not laugh at a
Mayor. They did not laugh much as a rule,
they frowned, stared, or talked rapidly with
gestures, and then if they did laugh, it was uproariously,
brutally, at some one’s misfortunes.
Satire they understood. But they missed entirely
the gentle nag, nag, nag of ridicule, that he used<span id="Page_121" title="[121]"></span>
to hear from his own platoon or company, covering
every unfamiliar object in that foreign land,
because it was not up to the standard of the upper-middle-classes.
To the French, life was a hard
affair, diversified by the points at which one was
less unfortunate than one’s neighbour.</p>
<p class="calibre3">To the English, life was the niceness of a
small class, diversified by the nastiness of everything
else, and the nastiness was endlessly diverting.
For the French were mere men, in their
own estimation. Not so the poorer English of
the towns. They were gentlemen. If they
lapsed (and naturally they lapsed most of the
time) they were comic to each other, to themselves
even. How well he remembered, on the march,
when the battalion had just landed, passing
through a village where certain humble articles
of domestic use were standing outside the cottage
doors, waiting to be emptied. A suppressed
titter had run all along the column.</p>
<p class="calibre3">A Frenchman would never have thought them
funny, unless they fell out of a first-floor window
on to some head and hurt it. Again, to a
Frenchman, Mayor and Priest, Garde Champêtre
and Suisse were officials, men plus authority and
therefore respectworthy. To Englishmen, they
were officials, therefore not gentlemen, therefore
ridiculous. If a big landowner, or member of
Parliament, or railway director had walked into
Vanderlynden’s pasture, just as 469 T.M.B. fell
in for their weary march back to the line, would<span id="Page_122" title="[122]"></span>
they have laughed? Not they. But then those
members of England’s upper classes would not
have worn tricolour sashes to enforce authority.
So there you were. With this philosophic reflection
he fell asleep.</p>
<p class="calibre3">Dormer returned to an army which was at
its brightest. It had held the initiative in the
matter of offensives for over a year and a half,
and if no decision had been come to, a wide
stretch of ground had been won, and hope on
the whole was high. From time to time there
were rumours of a queer state of things in Russia,
but it was far off and uncertain. The matter of
the moment was Messines, the famous ridge
which had been lost at the very beginning of the
War and which was now to be regained. In
this affair Dormer found himself busily engaged.
Here were no waste downs of the Somme, but
some of the most fertile land in the world.</p>
<p class="calibre3">Among other matters confronting the Generals
was the problem of how to keep civilians from
rushing back to cultivate land of which they had
been deprived for three years. The day came, the
explosion of the great mines, so Dormer was told,
was heard in London. If he did not hear it,
it was because a well-directed long-range artillery
bombardment, complicated by a bombing that
was German and German only in its thoroughness,
deafened and bewildered him, took his
sleep, killed his servant, and stampeded the horses
of all the divisional ammunition columns near<span id="Page_123" title="[123]"></span>
him, so that his tent was trampled down, his
belongings reduced to a state hardly distinguishable
from the surrounding soil. However, the
blow, such as it was, was successful. Irish and
Scotch, Colonial and London divisions took that
battered hillock that had defied them so long,
and Dormer in spite of all his experience could
not help thinking: “Oh, come, now we are really
getting on.”</p>
<p class="calibre3">But nothing happened. Dormer heard various
reasons given for this, and twice as many surmises
made about it, but well aware how much
importance to attach to the talk that floated
round Divisional Offices and Messes, relied upon
his own experience and arithmetic. According
to him, nothing could happen, because each
offensive needed months of preparation. Months
of preparation made possible a few weeks of
activity. A few weeks of activity gained a few
square miles of ground. Then more months of
preparation, grotesquely costly, and obvious to
every one for a hundred miles, so that the enemy
had just as long to prepare, made possible a few
more weeks’ activity and the gain of a few miles
more.</p>
<p class="calibre3">This was inevitable in highly organized
mechanical war, fought by fairly matched armies,
on a restricted field, between the sea and the
neutral countries. He admitted it. But then
came his lifelong habit of reducing the matter to
figures. He roughed out the area between the<span id="Page_124" title="[124]"></span>
“front” of that date and the Rhine, supposing
for the sake of argument that we went no farther,
and divided this by the area gained, on an average,
at the Somme, Vimy and Messines. The result
he multiplied by the time taken to prepare and
fight those offensives, averaged again. The result
he got was that, allowing for no setbacks,
and providing the pace could be maintained, we
should arrive at the Rhine in one hundred and
eighty years.</p>
<p class="calibre3">For the only time in his life Dormer wished
he were something other than Dormer. For a
few moments after arriving at his conclusion,
he desired to be a person of power and influence,
some one who could say with weight that the
thing ought to stop here and now. But this
very unusual impulse did not last long with him.</p>
<p class="calibre3">All that remained of Belgium and wide tracts
of French Flanders adjoining it, became one
huge ant-heap. Never had there been such a
concentration, Corps next to Corps, Services
mosaiced between Services, twenty thousand men
upon roads, no one could count how many
handling munitions, as, from Ypres to the sea,
the great offensive of 1917 slowly germinated.</p>
<p class="calibre3">Dormer was soon caught up and landed in the
old familiar blackly-manured soil of the Salient.
He was not disgusted or surprised. He was
becoming increasingly conscious of a sensation
of going round and round. Now, too, that
troops were always pouring along a road before<span id="Page_125" title="[125]"></span>
him, he had again the feeling that his head was
an empty chamber, round which was painted
a frieze, men, men, mules, men, limbers, guns,
men, lorries, ambulances, men, men, men. It
might be just worry and overwork, it might be
that he was again forced to share his limited
accommodation with Kavanagh. They were in
a dug-out on the canal bank, just by one of those
fatal causeways built to make the passage of the
canal a certainty, instead of the gamble it had
been in the days of the pontoon bridges. The
passage became, like everything else in the War,
a certainty for the Germans as much as for the
Allies. The place was registered with the utmost
precision and hit at all times of the day and night.
It probably cost far more than the taking of
any trench.</p>
<p class="calibre3">Amid the earth-shaking explosions that seldom
ceased for long, in the twilight of that narrow
cavern in the mud, Kavanagh was as unquenchable
as he ever had been on the high and airy downs
of the Somme. During the daylight, when nothing
could be done outside, he bent over his
map of cables while Dormer perfected his plan
for getting first-line transport past that infernal
canal. He purposed to send an N.C.O. a good
two miles back, with small square pieces of card,
on which were written 9.0 p.m., 9.5 p.m., and
so on, the times being those at which the unit
so instructed was to arrive beside his dug-out.
He thought rather well of this idea, no jamming<span id="Page_126" title="[126]"></span>
and confusion, and if the enemy made a lucky hit,
there would be fewer casualties and less to clear
away. In the middle of his calculations he heard</p>
<div class="poetry-container">
<div class="poetry">
<div class="first">“Why, soldiers, why</div>
<div class="verse">Should we be melancholy,</div>
<div class="verse">Whose duty ’tis to die!”</div>
</div>
</div>
<p class="calibre3">He could not resist saying:</p>
<p class="calibre3">“If you must make that d——d noise, I wish
you’d put some sense into it.”</p>
<p class="calibre3">“Sense. I was trying to cheer you up!”</p>
<p class="calibre3">“‘Duty ’tis to die’ is jolly cheering, and quite
untrue.”</p>
<p class="calibre3">“Oh, is it? What is our duty then?”</p>
<p class="calibre3">“Our duty is to live if we possibly can. And
I mean to do it. It’s the people who keep alive
who will win the War.”</p>
<p class="calibre3">“According to that, all one has got to do is to
get to Blighty, or preferably the United States,
and stay there?”</p>
<p class="calibre3">“Not a bit. You exaggerate so. All I said
was, that it is foolish to make it a duty to become
a casualty.”</p>
<p class="calibre3">“Dormer, I shall never get you to see things in
the proper light. You’re like a lamb trying to
leap with joy, and never able to get its hind legs off
the ground.”</p>
<p class="calibre3">“This is all rot. What connection is there
between lambs and leaping, and our jobs? Mine
is to see that various people and things are in the
position where they will be wanted, at the moment<span id="Page_127" title="[127]"></span>
at which they will have most effect in winning the
War. Yours is to see that they can speak and be
spoken to when required.”</p>
<p class="calibre3">“Lovely, lovely! What a teacher you would
have made.”</p>
<p class="calibre3">“I had a better job.”</p>
<p class="calibre3">“There is no better job, except perhaps the one
we are doing. I do admire your descriptions of
them. All you want is to put in a personal
allegorical note. You might condense the whole
thing by saying that you will be Minerva if I will
be Mercury. Yep?”</p>
<p class="calibre3">“Whatever are you talking about?”</p>
<p class="calibre3">“Yours to see that all is in order. That is a
matter of reason. You are the Goddess. I am
merely a lesser God. Mercury was God of
Communications. I wonder whether they’d let
me design a cap badge for signallers. Mercury
playing on a buzzer. You may have your Owl!”</p>
<p class="calibre3">“Oh, shut up.”</p>
<p class="calibre3">“I fear I must, the bugle calls, and I must
follow, or my watch shows it is time I was looking
after my chaps. But you’ve had a brilliant idea,
Dormer.”</p>
<p class="calibre3">“I?”</p>
<p class="calibre3">“You’ve had the idea of fighting the War
allegorically. Wisdom and Light we are. That
would do away with half the horror. So long!”</p>
<p class="calibre3">Then queerly, instead of feeling relieved from
an annoyance, Dormer felt more despondent than
ever. What could it be? Was the fellow right?<span id="Page_128" title="[128]"></span>
Surely not! All that nonsense! And yet—and
yet what would not he, Dormer, conscious of
his own probity, have given to be conscious
instead, of Kavanagh’s lightness of heart? That
very probity drove him out in the all-too-late
summer dusk to see that everything was going
right. Yes, here they were; details of transport,
parties to dig, parties to carry, details of services,
engineers of all their various grades. Punctual,
incredibly docile, honest English in their gestureless
manner of getting on with the job. They
took care of their mules, look at these beasts
pulling as though they were English too (instead
of the Argentine crossbreds he knew them to be),
not because it was a duty, although it was, and
not because the mule was a miracle, like a tank or
an aeroplane, but just because it was a mule, that
meant, to English soldiers, and to English soldiers
only, a fellow-creature, a human being. On they
went, reporting to him, and pushing on, sometimes
with a hurried question as to map square, or other
crucial uncertain detail, sometimes with only a
grunt. That endless procession had not been in
progress many minutes before, amid the considerable
and gently growing shell-fire, there came a
bang that seemed to go right through his head.
He knew from old trench experience what it was.
Nothing but a gun pointing straight at you could
make that particular hrrmph.</p>
<p class="calibre3">He set his feet, not a moment too soon. It
was a five-nine, the sort the French called “<i class="calibre9">Grande<span id="Page_129" title="[129]"></span>
Vitesse</i>.” A whirlwind, a small special whirlwind
pointed like an arrow, hit the causeway so that it
shook and then went up with a wheel of splintered
bits. He was glad he had devised his patent
card system. The units were not too close together.
He had time to shout to the next, “Come
on, you’ve two minutes to get over!” and over
they went, as if the Devil were after them, instead
of a lump of Krupp steel fitted with lethal chemicals.
They were hardly over before the second
came, whump! To say that Dormer was
frightened, was to fail to describe the matter.
He was stiffened all over, his hair stood up, his
heart thumped so that it hurt him, his feet were
stone cold, but he knew his job and did it.</p>
<p class="calibre3">The next lot to come was a whole field company
to do some special duty, and although he hurried
them, the tail of the brown column was still high
and exposed when the shell came. They ducked
and darted into any cover that was available, and
he heard his voice, as the voice of some one far
away speaking to a public meeting, like a voice on
the wireless, saying:</p>
<p class="calibre3">“Come on. Get out of that and come on. If
I can stand here, surely you can get out of it.”</p>
<p class="calibre3">They did so. Behind them came a special
party to dig in the Meteorological Officer.
What a menagerie it was! Every trade, every
nation too, Chinese, Zulu, West Indian, Egyptian.
He did not blame the Germans who had chalked
in blue on the bare back of a Portuguese, whom<span id="Page_130" title="[130]"></span>
they captured and stripped, “The Monkey
House is full,” before they drove him back into
English lines.</p>
<p class="calibre3">Even truer did Dormer find it when he had to
go back for any reason, to Corps H.Q. or beyond.
French and Belgians he knew, he had found them
in the trenches beside him years before. Portuguese
he had become accustomed to, Americans
he looked forward to with anticipation. But
farther back, he found Chinese, Africans of all
descriptions, Indians, East and West, while the
French, in addition to their black troops, had
Spanish and Italian labour.</p>
<p class="calibre3">It did not please his parochial mind. He felt
increasingly that there was something wrong
when you had to drag in all these coloured people
from every remote quarter of the globe, without
even the excuse the French had, that they were
“Colonials.” But no one could tell, least of all
Dormer himself, whether his feelings were the
result of a strong belief in the Colour Bar, or
whether it were merely the futility of it all. For
in spite of the omnium gatherum of race, tongue
and religion, the offensive failed. As a matter of
routine, the weather broke on Z day. Within
forty-eight hours it was obvious that the affair
had stuck. Apart from a feeling of the hand of
Fate in it, a sinister feeling of great incomprehensible
forces working out his destiny for him,
without his having the least power to influence the
matter for better, for worse, which was so desolating<span id="Page_131" title="[131]"></span>
to his pre-War habit of mind, where a certain
line of unostentatious virtue had always carried a
reward that could be reckoned on with the greatest
exactitude, there were other disturbing elements
in the situation.</p>
<p class="calibre3">Of course the Bosche was ready. He was
bound to be ready, couldn’t avoid it. He had
immensely thickened his depth of defence, which
was now composed not of the old obvious trenches
full of men, all of which could be blown to pieces,
but of small isolated turrets of ferro-concrete,
where two or three machine gunners (and who
made better machine gunners than the careful
Germans) could hold an army at bay, until dislodged
by a direct hit by a shell of six-inch calibre
or over, or laboriously smoke-screened and
bombed out, at the rate of perhaps a mile a day,
on good days. He saw his computation of one
hundred and eighty years altogether insufficient
for getting to the Rhine. Moreover, for such
work this medley of nations was of no good at all.
It reminded him of a book by Anatole France he
had been compelled by a friend to read, wherein a
great conqueror enlisted in his army all the men of
his nation, then all the men of the neighbouring
nations, then all the savages at the end of the
earth, and finally the baboons and other combatant
animals. That was all very well. That
was just story telling. But it horrified Dormer
all the more to see such story telling coming true
before his eyes. As coloured-labour company<span id="Page_132" title="[132]"></span>
after coloured-labour company filed past his tent,
guttural and straggling, he was able to pull himself
together, and see that it was not true after all.</p>
<p class="calibre3">These people, little better than beasts, uglier
in some cases and far more troublesome, were no
good. They couldn’t fight. You couldn’t trust
them to stand the shelling or to obey an order.
Then just as he was feeling rather relieved, he saw
the logical result of his conclusion. All the
fighting would have to be done by those very men
who had volunteered or been conscripted and
who had been so generously wasted ever since.
They were sticking it, and sticking it well, but
this new offensive that had just opened promised
to try them pretty high. Would they stick that?
Would the day ever come when he would see
them a mere mob, like those French black troops
he had seen in May? Perhaps peace would be
made. Such is the eternal hopefulness of men,
that he even hoped, against all previous experience.
That quenchless gleam common to all human
souls, one of the basic things that makes war so
long, and peace, where it is so much less necessary,
just that much less attractive, added to work
for fifteen hours a day, kept Dormer sane and
healthy for weeks, in spite of worsening conditions,
and the steady increase in enemy shelling.
It was with a return of that uncanny feeling of
being haunted that he found himself called up
to Divisional Head-quarters. He knew quite
well what it was, but he had relied on the difficulty<span id="Page_133" title="[133]"></span>
of finding Andrews, on the tremendous strain of
this most costly and urgent of all offensives, to
keep the matter out of his path, or rather to keep
him out of its path, for he had long dropped into
the habit of feeling himself as in a nightmare,
pursued by something he could not see or even
imagine, but which was certainly sinister and personally
fatal to him.</p>
<p class="calibre3">When he got to the office his feeling of nightmarishness
was rather aggravated than allayed.
Colonel Birchin was talking to the A.D.M.S.
The fact was that the A.D.M.S. was a new one,
patently a Doctor who had been fetched out from
Doctoring, had been found capable of organization
and had been shoved into the job vice some one
else gone higher up. Beside him Colonel
Birchin shone, as it were, with the glamour of
another world. Dormer had seen him in camp
and hut, and château and Mairie for a year and a
half, just like that, handsome and sleek, filling his
plain but choice khaki with a distinction that no
foreign officer could gain from all the blues and
reds and yellows and greens and blacks, varnished
belts and metal ornaments of other armies.
And in that moment of sharpened nerves and
unusual power of vision Dormer seemed to see
why. Colonel Birchin was not an officer of a
national army in the sense that any French,
German, Italian or Russian Colonel was. There
was nothing of the brute and nothing of the
strategian about those nice manners, that so<span id="Page_134" title="[134]"></span>
easily and completely excluded everything that
was—what? Unmilitary? Hardly. There
was nothing consciously, offensively military
about the Colonel, “regular” or professional
soldier that he was. He would never have
swaggered in Alsace, massacred in Tripoli,
Dreyfused in France. He would never have
found it necessary. For Colonel Birchin was not
a state official. He was an officer of the Watch,
the small band of paid soldiers that Stuart and
subsequent kings kept to defend themselves
from mobs, national armies and other inconvenients.
Colonel Birchin might write himself as
of “The Herefordshire Regiment,” but it made
no difference. His chief, inherited, and most
pronounced quality was that he was a courtier.
He represented the King. Preferably, at home,
of course, where one could live in all that thick
middle-class comfort that had ousted the old land-owning
seignorial dignity and semi-starvation.
But upon occasion, Colonel Birchin could betake
himself to Africa, India, and now even to this
France, sure that even in this most tedious and
unpleasant of wars, he would be properly fed and
housed.</p>
<p class="calibre3">So here he was, representing the King even
more exactly than before he was seconded from
the King’s Own Herefordshire Regiment. He
spoke and looked, in fact, rather as if he were the
King. Ignorant and unused to the immense
transport, the complicated lists of highly scientific<span id="Page_135" title="[135]"></span>
equipment, he judged rightly enough that his
one safe line was to represent authority, and see
that these semi-civilians who did understand such
things got on with the War. So he listened in a
gentlemanly way to the A.D.M.S. (who wore
beard and pince-nez) explaining at great length a
difficult alternative as to the siting of Forward
Dressing Stations, and contributed:</p>
<p class="calibre3">“You do what is best, Doctor, and we shall
back you up!”</p>
<p class="calibre3">Then he turned to Dormer, hunted a moment
among the papers on the table, and spoke:</p>
<p class="calibre3">“Look here, Dormer, about this affair of
yours?”</p>
<p class="calibre3">It took all Dormer’s training to keep his
mouth shut. He saw more clearly than ever how
Colonel Birchin and all like him and all he represented,
were divesting themselves of any connection
with what looked like a nasty, awkward,
tedious and probably discreditable business. But
he had not grasped it.</p>
<p class="calibre3">“They’ve found Andrews—this—er—gunner,
who will be able to give you information. And—look
here, Dormer—this affair must be cleared
up, do you understand? Andrews is in hospital.
You can go by car to Boulogne, but we expect
you to get it done this time. Corps are most
annoyed. There’s been a nice how-d-y-do
with the French.”</p>
<p class="calibre3">Dormer swallowed twice and only said:</p>
<p class="calibre3">“Really, sir.”</p>
<p class="calibre3"><span id="Page_136" title="[136]"></span>“Yes. Car starts at seven.”</p>
<p class="calibre3">Accordingly at seven, the big Vauxhall moved
off from that little group of huts, in the meadow
that was so regularly bombed every night.
Dormer, sitting next to Major Stevenage, did not
mind. As well Boulogne as anywhere, while this
was going on. All the roads were full of transport,
all the railways one long procession of
troop and supply trains. It was about as possible
to hide it all from the Germans, as to conceal
London on a Bank Holiday. In fact it was rather
like that. The population was about the same,
if the area were rather larger, the effect of the
crowd, the surly good humour, the air of eating
one’s dinner out of one’s hand was the same.</p>
<p class="calibre3">There was very little sign of any consciousness
of the shadow that hung over it all. Hospital
trains and ambulances abounded, going in the
opposite direction, but no one noticed them, so far
as Dormer could see. The type of man who now
came up to fight his country’s battles was little
changed. The old regular was hardly to be
found. The brisk volunteer was almost gone.
Instead there had arisen a generation that had
grown used to the War, had had it on their minds
so long, had been threatened with it so often that
it had lost all sharpness of appeal to their intellects.</p>
<p class="calibre3">Right back to St. Omer the crowd stretched.
Beyond that it became more specialized. Air
Force. Hospitals. Training grounds. Then,<span id="Page_137" title="[137]"></span>
across high windy downs, nothing, twenty miles
of nothing, until a long hill and the sea.</p>
<p class="calibre3">Up there on those downs where there was no
one, never had been anybody ever since they were
pushed up from the bed of some antediluvian
ocean, and covered with short turf, Dormer had
one of his rare respites from the War. Briefer
perhaps, but more complete than that which he
experienced on his rare leaves, he felt for a while
the emancipation from his unwilling thraldom.
It was the speed of the car that probably induced
the feeling. Anyhow, on the level road that runs
from Boulogne to Étaples—the ETAPPS of the
Army in France—he lost it. Here there was no
escaping the everlasting khaki and transport,
that State of War into which he had been induced,
and out of which he could see no very great
possibility of ever emerging. He had no warning
of what was to come, and was already well among
the hospitals and dumps that extended for miles
beside the railway, when a military policeman
held up a warning hand.</p>
<p class="calibre3">“What’s the matter, Corporal?”</p>
<p class="calibre3">“I should not go into Etapps this morning, if
I were you, sir.”</p>
<p class="calibre3">“Why not?”</p>
<p class="calibre3">The man shifted his glance. He did not like
the job evidently.</p>
<p class="calibre3">“Funny goings-on, there, sir.”</p>
<p class="calibre3">“Goings-on, what does that mean?”</p>
<p class="calibre3">Dormer was capable of quite a good rasp of<span id="Page_138" title="[138]"></span>
the throat, when required. He had learned it as
a Corporal.</p>
<p class="calibre3">“The men are out of ’and, sir!”</p>
<p class="calibre3">“Are they? The A.P.M. will see to that, I
suppose.”</p>
<p class="calibre3">“Very good, sir.”</p>
<p class="calibre3">“Drive on!”</p>
<p class="calibre3">Dormer didn’t like it, to tell the truth. But he
was so used to bluffing things he didn’t like, and
his own feelings, and other people’s awkwardness,
that he could not do otherwise than go on.
Also he didn’t realize what was on foot. A certain
amount of daily work was being done in among the
dumps and sidings where the population was of
all sorts of non-combatant, Labour Corps units,
medical formations, railway people, and others.
But from the rise by the Reinforcement Officers’
hut, he began to see. The whole of the great
infantry camp on the sandhill—and it was very
full, he had heard people say that there were a
hundred thousand men there—seemed to have
emptied itself into the little town. Here they
sauntered and talked, eddying a little round the
station and some of the larger estaminets, in
motion like an ant-hill, in sound like a hive of bees.
The car was soon reduced to a walking pace,
there were no police to be seen, and once entered
there was no hope of backing out of that crowd,
and no use in appearing to stop in it.</p>
<p class="calibre3">“Go slow,” Dormer ordered, glancing out of
the corner of his eye at the wooden face of the<span id="Page_139" title="[139]"></span>
chauffeur. Nothing to be seen. Either the
man didn’t like it, or didn’t feel the necessary
initiative to join in it, or perhaps considered himself
too superior to these foot-sloggers to wish to be
associated with them. Most probably he hadn’t
digested the fact that this mob, through which he
drove his officer, was Mutiny, the break-up of
ordered force, and military cohesion. It might
even be the end of the War and victory for the
Germans. All this was apparent enough in a
moment to Dormer, who was careful to look
straight again to his front, unwinking and mute,
until, with a beating heart, he saw that they were
clear of the jam in the Market Place, and well
down the little street that led to the bridge across
which were the farther hospitals, and various
sundry Base Offices, in the former of which he
was to find Andrews. Now, therefore, he did
permit himself to light a cigarette. But not a
word did he say to his chauffeur. Now that it
was behind him he had the detachment to reflect
that it was a good-humoured crowd. He had
heard a gibe or so that might have been meant for
him or no, but in the main, not being hustled, all
those tens of thousands that had broken camp,
chased the police off the streets, and committed
what depredations he did not know, were peaceful
enough, much too numerous and leaderless to
make any cohesive threat to an isolated officer, not
of their own unit, and therefore not an object of
any special hatred, any more than of any special<span id="Page_140" title="[140]"></span>
devotion, just a member of another class in the
hierarchy, uninteresting to simple minds, in
which he caused no immediate commotion.</p>
<p class="calibre3">Here, on the road that ran through the woods
to Paris Plage, there were little knots of men,
strolling or lying on the grass. They became
fewer and fewer. By the time he arrived at the
palace, mobilized as a hospital, for which he was
bound, there remained no sign of the tumult.
Here, as on the other flank, by the Boulogne road,
Medical and Base Units functioned unmoved.
But the news had been brought by Supply and
Signal services and the effect of it was most
curious.</p>
<p class="calibre3">Dormer had to pass through the official routine,
had to be announced, had to have search made for
young Andrews, and finally was conducted to a
bed in Ward C., on which was indicated Captain
Andrews, R.G.A. Dormer of course wanted to
begin at once upon his mission, but the other, a
curly-haired boy, whose tan had given place to a
patchy white under loss of blood from a nasty
shrapnel wound in the leg, that kept on turning
septic, had to be “scraped” or “looked at,”
each of these meaning the operation table, and
was only now gradually healing, would not let him.</p>
<p class="calibre3">Once away from the theatre and the knife,
Andrews, like any other healthy youngster, soon
accumulated any amount of animal spirit, lying
there in bed, adored by the nursing sisters,
admired by the men orderlies. He was not going<span id="Page_141" title="[141]"></span>
to listen to Dormer’s serious questions. He
began:</p>
<p class="calibre3">“Cheerio! Sit on the next bed, there’s no
corpse in it, they’ve just taken it away. Anyhow,
it isn’t catching. Have a cigarette, do for
God’s sake. They keep on giving me the darned
things, and they all end in smoke!”</p>
<p class="calibre3">“Sorry you got knocked out.”</p>
<p class="calibre3">“Only fair. Knocked out heaps of Fritzes.
I gave ’em what for, and they gave me some back.
I say, have you just come from the town?”</p>
<p class="calibre3">“I have just motored through.”</p>
<p class="calibre3">“Is it true that our chaps have broke loose?”</p>
<p class="calibre3">“There’s a certain amount of disorder, but no
violence that I could see.”</p>
<p class="calibre3">Dormer was conscious of heads being popped
up in all the surrounding beds. So that was how
it took them! Of course, they were bored stiff.</p>
<p class="calibre3">“How topping. Is it true that they’ve killed
all the red-caps?”</p>
<p class="calibre3">“I didn’t see any signs of it.”</p>
<p class="calibre3">“Cleared up the remains had they? Picked
the bones, or fallen in proper burying parties.”</p>
<p class="calibre3">“I don’t think there was anything of that sort.”</p>
<p class="calibre3">“Oh, come now, first we heard they had set on a
police-corporal that had shot a Jock.”</p>
<p class="calibre3">“What did he do that for?”</p>
<p class="calibre3">“Dunno. It isn’t the close season for Jocks,
anyhow. Then it was ten police-corporals. The
last rumour was that they’d stoned the A.P.M.
to death——”</p>
<p class="calibre3"><span id="Page_142" title="[142]"></span>And so it went on. Lunch-time came. A
Doctor Major, impressed by Dormer’s credentials,
invited him into the Mess, and asked a lot of
questions about the front, the offensive, and the
state of Étaples. Dormer always liked those
medical messes. It seemed so much more worth
while to mend up people’s limbs, rather than to
smash them to bits. The Doctors had their professional
“side” no doubt, but they had a right
to it.</p>
<p class="calibre3">After lunch Dormer made his way back to
Ward C. He was met by a hush, and by a little
procession. The Sergeant-major came first and
after him bearers with a stretcher covered by the
Union Jack. The hush in the ward was ominous.
They were all so close to what had happened. It
was not like the open field where the casualty is a
casualty and the living man a different thing.
Here the dead were only different in degree, not
in kind. They were worse “cases”—the worst,
that was all. So there were no high spirits after
lunch. They had gibed about Death in the
morning, but Death had come and they had
ceased to gibe. In the silence, Dormer felt
awkward, did not know how to begin. When he
had made up his mind that he must, he looked up
and found Andrews was asleep. So the day wore
on to tea-time, and after tea he was not wanted in
the ward, and was wanted in the Mess. He himself
was not hurrying to return to any regularly
bombed hut near Poperinghe. The Commanding<span id="Page_143" title="[143]"></span>
Officer was even more emphatic. Étaples was not
safe. Dormer let it go at that, and got a good
game of bridge.</p>
<p class="calibre3">In the morning he found young Andrews as
young as ever and got down to his job at once:</p>
<p class="calibre3">“Do you remember joining 469 T.M.B.?”</p>
<p class="calibre3">“Yes, sh’d think I do.”</p>
<p class="calibre3">“Do you remember the man you had as servant
while you were with them?”</p>
<p class="calibre3">“I do. Topping feller. Gad, I was sorry
when I had to leave him behind. Of course, I
dropped him when I went to hospital. Never was
so done!”</p>
<p class="calibre3">At last!</p>
<p class="calibre3">“You couldn’t give me his name and number,
I suppose?”</p>
<p class="calibre3">“I must have got a note of it somewhere. I
say, what’s all this about? Do you want to get
hold of him?”</p>
<p class="calibre3">“I do. He’s wanted, over a question of
damage in billets. They’ve sent me to find him
out.”</p>
<p class="calibre3">“Then I’m damned if I’ll tell you. Because
he was a topping chap!” rejoined Andrews,
laughing.</p>
<p class="calibre3">“You’d better tell me, I think. The matter
has gone rather high up, and it might be awkward
if I had to report that the information was
refused.”</p>
<p class="calibre3">“Lord, you aren’t going to make a Court of
Inquiry affair of it, are you?”</p>
<p class="calibre3"><span id="Page_144" title="[144]"></span>“It may come to that, and they’ve got hold of
your name.”</p>
<p class="calibre3">“Gee whizz! I don’t like landing the chap.
I may not have got any particulars of him, now, my
things have been so messed about.”</p>
<p class="calibre3">“Well, look and see!”</p>
<p class="calibre3">“All right.”</p>
<p class="calibre3">Andrews fumbled out from the night-table
beside his bed, the usual bedside collection.
Letters in female handwriting, some young, some
old—from one or more sweethearts and a mother,
thought Dormer. Paper-covered novels. The
sort (English) that didn’t make you think.
The sort (French) that make you feel, if you were
clever at the language. Cigarettes, bills. One
or two letters from brother officers.</p>
<p class="calibre3">“Blast. It’s in my Field Note Book, in my
valise, in store here. I shall have to send to have
it got out. Wait half a mo’ and I’ll get an
orderly.”</p>
<p class="calibre3">As they waited, he went on:</p>
<p class="calibre3">“What’s he wanted for? Some dam’ Frenchman
going to crime him for stealing hop-poles?”</p>
<p class="calibre3">“Something of that sort. You wouldn’t remember
it, it happened before you joined the
Battery.”</p>
<p class="calibre3">“Then it jolly well wasn’t my man Watson.
He’d only just come up from Base!”</p>
<p class="calibre3">“Come, the man was of middle size and
ordinary to look at, and had been servant to an
officer of the name of Fairfield, who was killed!”</p>
<p class="calibre3"><span id="Page_145" title="[145]"></span>“Oh, that chap. I know who you mean now.
I don’t call him my servant. I only had him for a
day or two. His name was Smith, as far as I
can recollect. We were in the line, and I never
got his number. He disappeared, may have been
wounded, or gone sick of course, we were strafed
to Hell, as usual. I should have got rid of him in
any case. He was a grouser!”</p>
<p class="calibre3">“Didn’t like the War?”</p>
<p class="calibre3">“I should say not.”</p>
<p class="calibre3">Hopeless, of course. When Andrews saw
Dormer rise and close his notebook, he
apologized:</p>
<p class="calibre3">“Beastly sorry. Afraid I’m no good.”</p>
<p class="calibre3">“That’s all right. I don’t want to find the
fellow, personally. It’s simply my job.”</p>
<p class="calibre3">“Fair wear and tear, so to speak?”</p>
<p class="calibre3">“Yes. Good morning.”</p>
<p class="calibre3">“Don’t go—I say, don’t. You’re just getting
interesting!” Heads popped up in the surrounding
beds. “Do tell us what it’s all
about.”</p>
<p class="calibre3">“Merely a matter of damage in billets as I
said.”</p>
<p class="calibre3">“Go on. There’s always damage in billets.
You must ha’ done heaps, haven’t you? I have.
There’s something more in it than that.”</p>
<p class="calibre3">“Well, there is. Perhaps it will be a lesson to
you not to go too far with other people’s property.”</p>
<p class="calibre3">“I say, don’t get stuffy. What did the feller
do?”</p>
<p class="calibre3"><span id="Page_146" title="[146]"></span>“He broke into a shrine.”</p>
<p class="calibre3">“I say, that’s a bit thick.”</p>
<p class="calibre3">“It was!”</p>
<p class="calibre3">“What did he do it for? Firewood?”</p>
<p class="calibre3">“No. He wanted to shelter a couple of
mules!”</p>
<p class="calibre3">“Good man. Don’t blame him!”</p>
<p class="calibre3">“No!”</p>
<p class="calibre3">“But they can’t crime him for a thing like
that?”</p>
<p class="calibre3">“They will if they can catch him.”</p>
<p class="calibre3">“Go on!”</p>
<p class="calibre3">“It didn’t stop at that.” Once more it
seemed to Dormer that a good lesson might do no
harm to the light-headed youth that Andrews
represented, and several of whom were listening,
anxiously from that corner of the ward.</p>
<p class="calibre3">“Did G.H.Q. take it up?”</p>
<p class="calibre3">“Yes. They had to. The Mayor of the
village came to make an official inquiry and the
Battery made fun of him.”</p>
<p class="calibre3">“Lumme! I bet they did!”</p>
<p class="calibre3">“They should not have done so. That made
the French authorities take it up. Goodness
knows where it will end!”</p>
<p class="calibre3">“End in our fighting the French,” said some
one.</p>
<p class="calibre3">Dormer felt that it was high time to put his foot
down. “You may be privileged to talk like that
while you’re in hospital. But I don’t recommend
you to do so outside. You ought to have the<span id="Page_147" title="[147]"></span>
sense to know that we don’t want to fight anyone,
we most certainly don’t want to fight some one
else after Germans. In any case, we don’t want
to do the fighting in England!”</p>
<p class="calibre3">There was a dead silence after he had spoken,
and he rose, feeling that he had impressed them.
He stumped out of the ward without another
word, went to the Mess, rang and demanded his
car. The Orderly Officer would have liked to
detain him, insisted on the possible state of
Étaples, but he would not hear of it. In those
few hours he had had enough and more than
enough of the Base—the place where people
talked while others Did—the place where the
pulse of the War beat so feebly. He felt he
would go mad if he stayed there, without sufficient
occupation for his mind. His car appeared and
he soon left the palace and the birchwoods and
was rattling over the bridge into Étaples. “Now
for it!” he thought. But no policeman warned
him off this time. He soon saw why. The
streets had resumed their normal appearance.
He might have known. That fancy of his,
about the Headless Man, came back to him with
its true meaning. What could they do, all
those “Other Ranks,” as they were designated?
Just meander about, fight the police, perhaps.
But they had no organization, no means of
rationing or transport. Of course, they had had
to go back to their respective camps with their
tails between their legs in order to get fed.</p>
<p class="calibre3"><span id="Page_148" title="[148]"></span>There was nothing to show for the whole business
but a few panes of broken glass and some
splintered palings. By the time he got to St.
Omer and stopped for lunch, no one seemed to
have heard of it. By tea-time, he was back at
Divisional H.Q. And none too soon. A fresh
attack was to be made the following day. He
went straight up to the canal bank, where
Kavanagh was as busy as ever, and dropped into
his work where he had left it. There was just
the same thing to do, only more of it. A desperate
race against time was going on. It was
evident enough that this most enormously costly
of all offensives must get through before
November finally rendered fighting impossible.
There was still some faint chance of a week or two
of fair weather in October. Fresh Corps were
massed and flung into the struggle. Engineers,
Labour Corps, anyone who could throw a bomb
or fire a rifle must do so. What had been roads
of stone <i class="calibre9">pavé</i>, had been so blown about with shell-fire
that they were a honeycomb of gaping holes,
repaired with planks. More and more searching
were the barrages, denser the air fighting.
Progress there undoubtedly was, but progress
enough?</p>
<p class="calibre3">Through the sleepless nights and desperate
days that followed, Dormer’s feelings toward
Kavanagh were considerably modified. The
fellow still talked, but Dormer was less sorry to
hear him. He even recited, and Dormer got into<span id="Page_149" title="[149]"></span>
the way of listening. They were now in an
“Elephant” hut. No dug-out was possible in
that sector, where eighteen inches below the surface
you came to water. No tent could be set,
even had they wished for one. Their frail house
was covered with sandbags, of a sufficient thickness
to keep off shrapnel, and presumably they
were too insignificant to be the object of a direct
hit, but in order to leave nothing to chance they
had had the place covered with camouflage netting.
Outside lay mile after mile of water-logged
runnels that had been trenches, on the smashed
and slippery parapets of which one staggered to
some bit of roadway that was kept in repair at
gigantic cost in lives and materials, guided by the
lines of wire that either side had put up with such
difficulty, and which were all now entirely useless,
a mere hindrance to free movement. But they
were “in” for a long spell, and could not get
away—did not want to, they were less bombed here
than farther back. Rations reached them, that
was as much as they had time to care about.
Otherwise, the night was well filled for the one
with counting off the parties that filed past into
this or that attack, for the other in picking up
those signal lines that had been smashed by shell-fire
during the day, and replacing them.</p>
<p class="calibre3">As that endless procession went past him once
more, Dormer felt that he now knew of what its
component parts were thinking. Australians,
Canadians, Welsh, Scotch, Irish, English, they<span id="Page_150" title="[150]"></span>
were thinking of nothing in particular. Like the
mules that went with them, they went on because
they couldn’t stop. Food and sleep each day was
the goal. To stop would mean less food and
sleep, mules and men knew that much, without
use of the reasoning faculty. It had become an
instinct. All the brilliant casuistry that had
induced men to enlist was forgotten, useless,
superseded. Even English soldiers were conscripts
now, the War had won, had overcome any
and every rival consideration, had made itself
paramount, had become the end and the means as
well.</p>
<p class="calibre3">A man like Dormer, accustomed to an ordered
and reasoned existence, who could have explained
his every act up to August, 1914, by some good
and solid reason, was as helpless as any. Stop
the War? You wanted to go back half a century
and alter all the political and business cliques in
which it had been hatching. To alter those you
wanted to be able to alter the whole structure of
society in European countries, which kept those
cliques in power, was obliged to have recourse to
them, to get itself governed and financed. To
do that you wanted to change Human Nature.
Here Dormer’s imagination stopped dead. He
was no revolutionary. No one was farther than
he from being one. He only hated Waste. He
had been brought up and trained to business, in
an atmosphere of methodical neatness, of carefully
foreseen and forestalled risks. Rather than have<span id="Page_151" title="[151]"></span>
recourse to revolution he would go on fighting the
Bosche. It was so much more real.</p>
<p class="calibre3">Somewhere about the point at which he reached
this conclusion, he heard, among the noise of the
sporadic bombardment, Kavanagh’s voice:</p>
<div class="poetry-container">
<div class="poetry">
<div class="first1">“‘Now that we’ve pledged each eye of blue</div>
<div class="verse">And every maiden fair and true,</div>
<div class="verse">And our green Island Home, to you</div>
<div class="verse">The Ocean’s wave adorning,</div>
<div class="verse">Let’s give one hip, hip, hip hurrah!</div>
<div class="verse">And drink e’en to the coming day,</div>
<div class="verse">When squadron, square,</div>
<div class="verse">We’ll all be there,</div>
<div class="verse">To meet the French in the morning!’</div>
</div>
</div>
<p class="calibre3">That’s the stuff to give the troops, Dormer!”</p>
<p class="calibre3">But Dormer, although cheered, was not going
to admit it. “You’d better go and sing it to the
Seventy-Worst. They go in at dawn!”</p>
<p class="calibre3">“Good luck to them. Listen to this:</p>
<div class="poetry-container">
<div class="poetry">
<div class="first1">“‘May his bright laurels never fade</div>
<div class="verse">Who leads our fighting Fifth Brigade,</div>
<div class="verse">These lads so true in heart and blade,</div>
<div class="verse">And famed for danger scorning;</div>
<div class="verse">So join me in one hip hurrah!</div>
<div class="verse">And drink e’en to the coming day,</div>
<div class="verse">When squadron, square,</div>
<div class="verse">We’ll all be there,</div>
<div class="verse">To meet the French in the morning!’</div>
</div>
</div>
<p class="calibre3">How’s that for local colour. Is there a Fifth
Brigade in to-morrow’s show? They’d like that.”</p>
<p class="calibre3"><span id="Page_152" title="[152]"></span>“I bet they wouldn’t. Anyhow, it’s silly to
repeat things against the French.”</p>
<p class="calibre3">“Man, it’s a hundred years old.”</p>
<p class="calibre3">“Like my uncle’s brandy.”</p>
<p class="calibre3">“You and your uncle!”</p>
<p class="calibre3">“I had an uncle once who had some brandy.
It was called ‘Napoleon,’ and was supposed to
date from 1815. When he opened it, it was
gone!”</p>
<p class="calibre3">“There you are. That’s your materialism.
But you can sing a song a hundred years old and
find it’s not gone!”</p>
<p class="calibre3">“It’s not a bad song. Only silly!”</p>
<p class="calibre3">“Well, try something older:</p>
<div class="poetry-container">
<div class="poetry">
<div class="first1">“‘We be</div>
<div class="verse">Soldiers three,</div>
<div class="verse">Lately come from the Low-Countree,</div>
<div class="verse"><i class="calibre9">Pardonnez moi, je vous en prie</i>;</div>
<div class="verse">We be</div>
<div class="verse">Soldiers three.’</div>
</div>
</div>
<p class="calibre3">That’s nearer three hundred years old. That’s
what fellows used to sing coming back from
Ypres in those days!”</p>
<p class="calibre3">“You talk as if we’d always been in and out of
that mangey hole.”</p>
<p class="calibre3">They both leaned on their elbows and gazed out
of the tiny aperture, under the sacking, away over
the sea-like ridges of pulverized mud, into the
autumn evening. Between the rain-clouds, torn
and shredded as if by the shell-fire, watery gleams<span id="Page_153" title="[153]"></span>
were pouring, as though the heavens were
wounded and bled. They spilled all over the
jagged stonework of that little old medieval
walled town, compact within its ramparts, for the
third time in its history garrisoned by an English
army. Kavanagh told him of it, but Dormer
remained unimpressed. The history of the world
that mattered began after the battle of Waterloo,
with Commerce and Banking, Railway and Telegraph,
the Education and Ballot Acts. Previous
events were all very well, as scenery for Shakespeare’s
plays or Wagner’s Operas. But otherwise,
negligible. Yet the interlude did him good.
He felt he had brought Kavanagh up short, in an
argument, and he went to his night’s work with a
lighter heart, and a strengthened confidence in
himself.</p>
<p class="calibre3">Of course, a few weeks later, the offensive was
over, with the results he had foreseen, and with
another result he was also not alone in foreseeing.
Once back in rest, near Watten, he heard people
talking in this strain, in G. office:</p>
<p class="calibre3">“I suppose, sir, we shall go on fighting next
year?”</p>
<p class="calibre3">“Um—I suppose we shall. But perhaps some
arrangement may be come to, first. There’s
been a good deal of talk about Peace!”</p>
<p class="calibre3">That was the mood of Divisional Head-quarters.
A growing scepticism as to the continuance
of the War. At the moment, Dormer
missed the motive at the back of it. Away from<span id="Page_154" title="[154]"></span>
H.Q. while the Division was in action, he had
lost a good deal of ominous news. The talk
about the transference of German Divisions from
one front to another was old talk. He had
heard it for years. He did not at the moment
grasp that it had now a new significance. Then
something happened that put everything else out
of his head. He was not feeling too well,
though he had nothing to complain of worse than
the usual effects of damp and loss of sleep.
Colonel Birchin had got himself transferred to a
better appointment, and his place was taken by a
much younger officer, glad to take it as a “step”
up from a dangerous and difficult staff-captaincy.
They had been out at rest less than a week and
Dormer had assumed as a matter of course that
he would be put in charge of organized sports for
the winter, as usual. But he was only just
becoming sensible of the change that had come
over H.Q. Colonel Birchin used to have a
certain pre-War regular soldier’s stiffness and
want of imagination (which Dormer had privately
deplored), but he had kept the Q. office well in
hand. This new man, Vinyolles, very amicable
and pleasant, and much nearer to Dormer’s new
army view of the War (he was in fact younger
than Dormer, and than most of the clerical
N.C.O.’s in the office), had nothing like the standoff
power of his predecessor. Also, the office, like
everything else, had grown, half a dozen odd-job
officers were now attached, and without wearing<span id="Page_155" title="[155]"></span>
red, sat and worked with Dormer. So that when
Dormer went to show his Football Competition
Time Table and his schedule for use of the Boxing
Stadium, he found that he had to explain
how these things were usually done. Colonel
Vinyolles had no idea. Dormer ought to have
been warned. But his head was not working at
its very best. He had a temperature, he thought,
and wanted to go and lie down at his billet for
a bit and take some aconite, a remedy he had
carried with him throughout the War. Colonel
Vinyolles was quite nice about the Sports, and
just as Dormer was turning to go, said to him:</p>
<p class="calibre3">“Perhaps you can help me in this matter. I
see your name occurs in the correspondence!”</p>
<p class="calibre3">Of course, he might have known. It was the
familiar <i class="calibre9">dossier</i>, as the French called it, the sheaf
of papers, clipped together, at the bottom the
original blue Questionnaire form that old Jerome
Vanderlynden had signed. At the top a fresh
layer of official correspondence, “Passed to you
please, for necessary action.” “This does not
appear to concern this office.” “Kindly refer to
A.Q.M.G.’s minute dated July 1916.” And so
on. Dormer knew quite a lot of it by heart and
the remainder he could have “reconstructed”
with no difficulty. The only fresh thing that
had happened was a minute from the new chief of
the French Mission enclosing a cutting from a
newspaper—a French newspaper of all conceivable
rags—from which it appeared that some<span id="Page_156" title="[156]"></span>
deputy or other had “interpellated” a minister
about the matter, asked a question in the
“House” would be the English of it, Dormer
supposed.</p>
<p class="calibre3">“What am I to tell the Mission?” Colonel
Vinyolles was asking.</p>
<p class="calibre3">Dormer was not a violent man by habit, but he
felt that he was getting to his limit with this
affair. He thought a moment, wanting to say:
“Tell them to go to the Devil!” but held it in
reserve, and substituted: “Tell them the matter
has attention!”</p>
<p class="calibre3">“Thanks very much!”</p>
<p class="calibre3">Dormer went and rested.</p>
<p class="calibre3">The following day he felt no better and did not
do much. He had the Sports well in hand, and
there was no movement of troops. The day
following that he felt queerer than ever, and
jibbed at his breakfast. He went along to see the
D.A.D.M.S., always a friend of his, who put a
thermometer under his tongue, looked at it,
shook it, looked at Dormer, gave him an aspirin,
and advised him to go and lie down for a bit.
On his way to his billet Dormer put his head into
Q. office to tell the Sergeant-major where he was
to be found if wanted. He was called by Colonel
Vinyolles from the farther room. It was again
full of people he considered (as rank counted for
less than experience) to be his juniors. He could
see something was “up.” They were all highly
amused except Vinyolles.</p>
<p class="calibre3"><span id="Page_157" title="[157]"></span>“I say, Dormer, I consider you let me down on
this.”</p>
<p class="calibre3">“What’s the trouble?”</p>
<p class="calibre3">“Trouble! I’ve got a nice chit back, in
reply to my saying ‘the matter has attention.’
They say that any further delay is ‘inadmissible’
and that they will be obliged to carry the matter
higher.”</p>
<p class="calibre3">“Let ’em!”</p>
<p class="calibre3">“Oh, that won’t do at all. The General has
seen this, and he wants to know what you mean
by it.”</p>
<p class="calibre3">“He ought to know by this time!”</p>
<p class="calibre3">“Captain Dormer!”</p>
<p class="calibre3">Of course he was wrong, but he felt rotten. It
wasn’t Vinyolles’ fault. He pulled himself together.</p>
<p class="calibre3">“Sorry, sir. I mean that the case has been
going on for nearly two years, and has certainly
not been neglected. I think every one who
counts is familiar with it.”</p>
<p class="calibre3">He meant it for a snub for some of those chaps
who were sitting there grinning. He saw his
mistake in a moment. Vinyolles was as new as
any of them, and naturally replied: “I’m afraid
I have no knowledge of it. Perhaps you will
enlighten me?”</p>
<p class="calibre3">“It must have been June, 1916, when we first
received the claim. The late A.P.M., Major
Stevenage, took it up as a matter of discipline,
but on investigation considered that it was rather<span id="Page_158" title="[158]"></span>
a case for compensation, as damage in billets.
The French Mission insisted that an arrest must
be made, and I have made every possible effort
to trace the soldier responsible. But formations
change so quickly, during offensives especially,
that it is impossible.”</p>
<p class="calibre3">“I see. What exactly did he do, to cause such
a rumpus?”</p>
<p class="calibre3">At the prospect of having to retell the whole
story, Dormer got an impression that something
was after him, exactly like the feeling of trying to
get cover in a barrage, and wondering which
moment would be the last. He put his hand to
his head and found some one had pushed a chair
against his knees. He sat down vaguely conscious
of the D.A.D.M.S. standing near by.</p>
<p class="calibre3">“An officer of 469 T.M.B. was wounded and
his servant was given two mules, sick or wounded,
to lead. He got to the billet mentioned and seems
to have taken a dislike to the horse-lines. He
found one of those little memorial chapels that
you often see, in the corner of the pasture, and
knocked in the front of it to shelter the beasts.
The farmer didn’t like it and sent for the Mayor
to make a <i class="calibre9">procès-verbal</i>. By the time the Mayor
got there, the Battery was on the move again.
It was about the time of one of those awkward
little shows the Bosche put up to contain us during
Verdun. The Battery had been badly knocked
about, and the men were excited and made some
sort of a scene! The Mayor told his Deputy and<span id="Page_159" title="[159]"></span>
his Deputy told some one at French G.H.Q. It
all keeps going round in my head. I don’t want
to find the chap who did it. He’s no worse than
you or I. He was just making the best of the
War, and I don’t blame him. I blame it. You
might as well crime the whole British Army.”</p>
<p class="calibre3">What had he said? He fancied he had given
the facts concisely, but was not sure of himself,
his head felt so funny, and he was aware that
people—he could no longer be sure who they were—Q.
office seemed crowded—were tittering!—Some
one else was talking now, but he was not
interested. He rested his head on his hand and
heard Vinyolles: “Well, Dormer, you go along
to your billet, and we’ll see what can be done!”</p>
<p class="calibre3">He got up and walked out. The D.A.D.M.S.
was at his elbow, saying to him:</p>
<p class="calibre3">“Get into this ambulance, I’ll run you
across!” but he never got to his billet. He got
into a train. He did not take much notice, but
refused the stuff they wanted him to eat. After
that he must have gone to sleep, but woke up,
under a starlit sky, with an unmistakable smell of
the sea. They were lifting him under a canvas
roof. Now, from the motion, he perceived he was
at sea, but it did not seem greatly to matter. He
was out of it, he had cut the whole disgusting
show. He had done his bit, now let some one else
take a turn.</p>
<p class="calibre3">Dormer had not been home on leave since early<span id="Page_160" title="[160]"></span>
spring, and the leave that he got for convalescence
gave him not only some idea of the vast changes
going on in England, while he, in France, had
been engaged in the same old War, but a notion
of changes that had gone on in that old War
without his having perceived them. He was let
loose from Hospital just before Christmas, at
that unfortunate period when the public at home
were still feeling the reaction from the Bell-ringing
of Cambrai, were just learning the lengths
to which the collapse of Russia had gone and were
to be confronted with the probable repercussion
of that collapse upon the prospects of the campaign
in the West. There was no escaping these conclusions
because his own home circumstances
had so changed as to throw him back completely
on himself. His father having died while he was
in France, his mother had taken a post under
one of the semi-official War organizations that
abounded. The old home in which he had grown
up had been dispersed, and he found his only
near relative in his native town was his sister, a
teacher by profession, who had moved the
remnants of the old furniture and his and her
own small belongings to a new house in one of
the high, healthy suburbs that surrounded the
old town. She was, however, busy all day, and
he fell into the habit, so natural to anyone who
has lived in a Mess for years, of dropping in at one
of the better-class bars, before lunch, for an
<i class="calibre9">apéritif</i>, and a glance at the papers. Here he<span id="Page_161" title="[161]"></span>
would also pickup some one for a round of golf,
which would keep him employed until tea-time,
for he could not rid himself of the War-time habit
of looking upon each day as something to be got
through somehow, in the hopes that the morrow
might be better.</p>
<p class="calibre3">These ante-prandial excursions were by far the
closest contact he had had with anything like a
normal, representative selection of his fellow-countrymen,
since they and he had become so
vitally altered from the easy-going, sport-loving
England of pre-War, and he had to readjust his
conception considerably. He soon grasped that
there was a lot of money being made, and a lot of
khaki being worn as a cover for that process.
There was plenty of energy, a good deal of fairly
stubborn intention to go on and win, but a clear
enough understanding that the War was not going
to be won in the trenches. And when he had got
over some little spite at this, his level habit of
mind obliged him to confess that there was a good
deal in it. There were many signs that those who
held that view were right.</p>
<p class="calibre3">Sipping his drink, smoking and keeping his
nose carefully in his newspaper, in those bars
lighted by electric light, in the middle of the dark
Christmas days, he listened and reflected. The
offensives he had seen? How had they all
ended? How did he say himself they always
must end? Exactly as these chaps had made up
their minds! Would he not see if there did not<span id="Page_162" title="[162]"></span>
remain some relative who could get him one of
these jobs at home, connected with supplying some
one else with munitions? No, he would not.
He understood and agreed with the point of view,
but some very old loyalty in him would keep him
in France, close up to the guns, that was the place
for him. He had no illusions as to that to which
he was returning. He knew that he had never
been appointed to Divisional Staff, had merely
been attached. There was no “establishment”
for him, and directly he had been sent down as
sick, his place had been filled, some one else was
doing “head housemaid” as he had been called,
to young Vinyolles, and he, Dormer, would go
shortly to the depôt of his regiment, from thence
to reinforcement camp, and thus would be posted
to any odd battalion that happened to want him.
The prospect did not worry him so much as
might have been supposed. He felt himself
pretty adept at wangling his way along, and
scrounging what he wanted, having had a fine
first-hand experience of how the machinery
worked. He did not want to go into the next
offensive, it was true, but neither did he want the
sort of job he had had, and even less did he want
to be at Base, or in England. Boredom he feared
almost as much as physical danger. Accustomed
to having his day well filled, if he must go to War
he wanted to be doing something, not nothing,
which was apparently a soldier’s usual occupation.
But he did not feel his participation in the next<span id="Page_163" title="[163]"></span>
offensive very imminent. He had heard them
all talking about “Not fighting any more,” and
now here was Russia out of it and America not yet
in, and Peace might be patched up.</p>
<p class="calibre3">The most striking thing therefore that he
learned was this new idea of the Bosche taking the
initiative, and attacking again. A new army
officer, his knowledge of the Western Front
dated from Loos, and was of allied offensives
only. He had never seen the earlier battles of
Ypres, the retreat from Mons was just so much
history to him. When he heard heated arguments
as to which particular point the Bosche
would select for their offensive, in France, or (so
nervous were these people at home) in England
even, he was astonished, and then incredulous.
The level balance of his mind saved him. He
had no superfluous imagination. He had never
seen a German offensive, didn’t want to, and therefore
didn’t think he would. As usual, the bar-parlour
oracles knew all about it, gave chapter
and verse, could tick off on their fingers how many
German Divisions could be spared from the
Eastern Front. He had heard it all before. He
remembered how nearly the cavalry got through
after Vimy, how Moorslede Ridge was to give us
command of the country up to Courtrai, how
Palestine or Mespot were to open an offensive
right in the Bosche rear, not to mention all the
things these Russians had always been said to be
going to do. This might be another of what the<span id="Page_164" title="[164]"></span>
French so well called “Canards”—Wild Ducks.
He would wait and see.</p>
<p class="calibre3">He was impressed in a different way by the
accounts that now began to filter through, of
what had been happening in Russia. Officers
shot, and regiments giving their own views on
the campaign. That was what happened when
the Headless Man got loose! No doubt the
Russians, from all he had heard, had suffered
most, so far as individual human suffering went.
And then, Russians were, to him, one of these
over-brainy people. Had anyone acquainted
with his ruminations taxed him to say if English
people were under-brainy, he would have said
no, not necessarily, but brainy in a different way.
Left to himself he felt that all the opinions he
had ever formed of the Russians were justified.
Look at their Music. Some of it was pretty
good, he admitted, but it was—awkward—beyond
the reach of amateurs, in the main. This
appeared to him, quite sincerely, to be a grave
defect. He was conscious—more, he was proud—of
being an amateur soldier, and knowing himself
to be modest, he did not fear any comparison
between the actual results obtained by English
amateurs like himself, and the far more largely
professional armies of other countries. And now
these over-brainy ones had gone and done it.
He knew as well as anyone the hardships and
dangers of soldiering, had experienced them,
shared them with the ranks, in the trenches.<span id="Page_165" title="[165]"></span>
Why even in this beastly Vanderlynden affair,
it would have puzzled him to say if he were more
sorry than glad that the private soldier had never
been brought to Justice. But English—and
even Frenchmen—as he had seen with his own
eyes, if they mutinied, got over it, and went on.
It was only people like the Russians that went and
pushed things to their logical conclusion.</p>
<p class="calibre3">He had a hatred of that, being subconsciously
aware that the logical conclusion of Life is Death.
Naturally, from his upbringing and mental outlook,
he had no sympathy with the alleged objects
and achievements of the Russian Revolution. He
could not see what anyone wanted with a new
social order, and as for the domination of Europe
by the Proletariat, if he understood it, he was all
against it in principle. He was against it because
it was Domination. That was precisely the thing
that had made him feel increasingly antagonistic
to Germany and German ideas. It had begun
long ago, during brief continental holidays. He
had met Germans on trains and steamers, in
hotels and on excursions. He had grudged them
their efficient way of sight-seeing, feeding and
everything else. But he had grudged them most
their size and their way of getting there first.
If it had not been for that, he had a good deal
more sympathy with them, in most ways, than
with the French. Subsequently he had found
Germans infringing on the business of his native
town, selling cheaper, better-tanned hides than<span id="Page_166" title="[166]"></span>
its tanners, more scientifically compounded
manures than its merchants. Then they invaded
politics and became a scare at election times.
And after the false start of 1911, in 1914 they
had finally kicked over the tea-table of the old
quiet comfortable life. He did not argue about
this. He had felt it simply, truly, directly.
Under all the hot-air patriotism and real self-sacrifice
of August, 1914, it had been this basic
instinct which had made him and all his sort
enlist. The Germans had asked for it, and they
should darn-well have it. If they didn’t they
would go on asking. They were after Domination.</p>
<p class="calibre3">That craze had started something that would
be difficult now to stop. Dormer saw very well
that other people besides Russians might find
grievances and the same wrong-headed way of
venting them. The Russians would probably
go on with their propaganda, all over the world.
The Germans, on the other hand, had probably
set the Japanese off. And so we should go on, all
the aristocratic classes calling for Domination by
their sort, all the ultra-brainy democracies calling
for their particular brand.</p>
<p class="calibre3">So when he was passed as fit and told to rejoin
the depôt of his regiment, at a seaport town, he
went without any panic fear of the future, German
or otherwise. He went with a deep conviction
that whatever happened, life had been cheapened
and vulgarized. It was not by any means mere<span id="Page_167" title="[167]"></span>
theory. He had seen what sort of a home he
might hope to make after the Peace, with his
mother or sisters, or if, conceivably, he married.
Not a bad home, his job would always be there,
and certain remnants of that bourgeois comfort
that had grown up in all the old quiet streets of
the provincial towns of England during the
nineteenth century, privileged, aloof from the
troubles of the “continent,” self-contained. But
remnants only, not nearly enough. He and all his
sort had been let down several pegs in the social
scale. Without any narrow spite, or personal
grievance, he felt that the Germans had caused
this upset and the Russians had put the finishing
stroke to it, made it permanent, as it were. He
happened to be opposite the Germans in the
particular encounter that was not yet ended, and
he was able to draw upon an almost inexhaustible
supply of obstinate ill-will.</p>
<p class="calibre3">He went to the depôt in its huts on a sandy
estuary. It was commanded by a Major of the
usual type, and no one knew better than Dormer
how to keep on the right side of such a one. He
was, of course, a Godsend to the Major. He had
all the practical experience and none of the
fussiness. He merely wanted the job finished.
That suited the Major exactly, who didn’t want
it to finish in a hurry, but wanted even less to
have to find ideas for training troops. Dormer,
with his two and a half years in France, was the
very man. He looked trustworthy. He was set<span id="Page_168" title="[168]"></span>
to instructing the raw material, of which the camp
was full. He disliked it intensely, but, as
always, took what was given him in his sober
fashion and did his limited best with it. He
was amazed to find such reserves of men still
untouched. His own recollections of early 1915
were of camps filled with an eager volunteer
crowd of all ages and conditions, who were
astounded when it was suggested to them that
certain of them ought to take a commission.
Now he found that his sort went a different way,
direct to O.T.C. or Cadet Corps. There was a
permanence about the camp staff that he had never
seen in the old days. But most of all he was
impressed with the worn appearance of the camp.
Thousand after thousand had passed through it,
been drafted overseas, and disappeared. Thousand
after thousand had followed. In the town
and at the railway, there were no longer smiles
and encouragement. People had got painfully
used to soldiers, and from treating them as heroes,
and then as an unavoidable, and profitable
incident, had come to regard them chiefly as a
nuisance. He forgot how he had wondered if
the men would stand it, he forgot how often he
had heard the possibility of an early Peace discussed.
He began to wonder now if people at
home would stand it—the lightless winter nights,
the summer full of bombing, the growing scarcity
of comforts, the queues for this, that, and the
other, the pinch that every gradually depleted<span id="Page_169" title="[169]"></span>
family was beginning to feel, as one after another
of its members had to go. He had been so long
out of all this, up against the actual warfare,
glad enough of small privileges and of the experience
that enabled him to avoid the more onerous
duties, the worst sorts of want, that he only now
began to realize what he had never grasped, in
his few short leaves, that there was still quite a
considerable, probably the greater portion of the
nation, who did not share his view of the necessity
of going on. Another avenue of speculation
was opened to him. What if all the people
at home made Peace behind the backs of the
Armies. Yet, being Dormer, he did not submit
to this home-grown philosophy. He just went
on and did the next thing that his hand found
to do.</p>
<p class="calibre3">Of one thing he became pretty certain. All
these people at home had “got the wind up.”
He didn’t know which were the worst, the lower
middle class, who were beginning to fear invasion,
as a form of damage to their shops and
houses. He thought of those ten departments
of France that were either occupied by, or shot
over, by the Germans. Or again the newspapers,
with their scare-lines, their everlasting attempt to
bring off this or that political coup. Or again
the people in power, who were keeping this
enormous number of troops in England, presumably
to defend the beaches of the island from
an armed landing. He had become during the<span id="Page_170" title="[170]"></span>
three years that had contained for him an education
that he could not otherwise have got in
thirty, a more instructed person.</p>
<p class="calibre3">An offensive was an offensive, could be nothing
more or less. Every offensive had been a failure
except for some local or temporary object, and in
his opinion, always must be a failure. The idea
of an offensive conducted across a hundred leagues
of sea made him smile. It was hard enough to
get a mile forward on dry land, but fancy the job
of maintaining communications across the water!
He attended enough drills to fill in the time,
organized the football of the Brigade to his liking
and let it go at that. At moments he was
tempted to apply to be sent to France, at others to
try and join one of these Eastern expeditions,
Salonika, Palestine or Mespot. But the certainty
of being more bored and of being farther than
ever from the only life he cared for, made him
hesitate. He hesitated for two long months.</p>
<p class="calibre3">Then on the 21st March he was ordered by
telegram to proceed to France. He felt, if anything,
a not unpleasant thrill. With all his care,
he had not been able to dodge boredom altogether.
The depôt camp had also been much too near the
scenes of his pre-War life. He had gone home,
as a matter of duty, for several week-ends and
had always returned finely exasperated, it was so
near to and yet so far from home as he had
pictured it, in his dreams. Now, here was an end
to this Peace-time soldiering. The news, according<span id="Page_171" title="[171]"></span>
to the papers, seemed pretty bad, but he
remembered so well the awful scurry there was for
reinforcements on the morning that the nature of
the Second Battle of Ypres became known.
This could not be so desperate as that was.
Practically the whole of the rank and file in the
depôt were under orders. He took jolly good
care not to get saddled with a draft, and spent the
night in London. People were in a rare stew
there. He had a bath and a good dinner and
left it all behind. He took a little more note
of the traffic at the port of embarkation. On
the other side, he found lorries waiting and went
jolting and jamming away up to Frecourt,
forty miles. He rather approved. It looked as
though our people were waking up.</p>
<p class="calibre3">At Corps reinforcement camp—a new dodge
evidently—he got posted to a North Country
battalion; and proceeded to try and find their
whereabouts. He was told that they were going
to Bray, but it took him some time to understand
that they were falling back on that place. When,
by chance, he hit upon the Division to which they
belonged, they were on the road, looking very
small, but intact and singing. He soon found
plenty to do, for he grasped that practically the
whole battalion was composed of reinforcements,
and had only been together two or three days.
They set to work at once to strengthen some half-completed
entrenchments, but after two days were
moved back again.</p>
<p class="calibre3"><span id="Page_172" title="[172]"></span>It was during those two days that he saw what
he had never to that moment beheld, an army in
retreat. The stream of infantry, artillery and
transport was continuous—here in good formation,
there a mere mass of walking wounded mixed
up with civilians, as the big hospitals and the
small villages of the district turned out before
the oncoming enemy. He thought it rotten luck
on those people, many of whom had been in
German hands until February, 1917, and had
only had a twelvemonth in their small farms,
living in huts, and had now to turn out before a
further invasion. The bombardment was distinctly
nasty, he never remembered a nastier, but
as usual, the pace of the advance soon outdistanced
the slow-moving heavy artillery, whose
fire was already lessening. He had no feelings of
sharp despair, for as he had foreseen, a modern
army could not be crumpled up and disposed of.
What he did now anticipate, was any amount of
inconvenience.</p>
<p class="calibre3">Amiens, he gathered, was uninhabitable, that
meant many good restaurants out of reach. New
lines of rail, new lateral communications would be
necessary, that meant marching. Just when
they had begun to get the trenches fairly reliable,
they were entrained and sent wandering all round
the coast. The wonderful spring weather broke
with the end of March, as the weather always did,
when it had ceased to be of any use to the Bosche,
and had he been superstitious, he might have<span id="Page_173" title="[173]"></span>
thought a good deal of that. It was in a cold and
rainy April that he found himself landed on the
edge of the coal-fields, behind a canal, with a slag
heap on one side of him, and a little wood on the
other, amid an ominous quiet.</p>
<p class="calibre3">The company of which he had been given
command was now about a hundred and fifty
strong and he had done what little he could to
equalize the four platoons. He had one officer
with him, a middle-aged Lieutenant called
Merfin, of no distinguishable social status, or local
characteristics. The day when a battalion came
from one town or corner of a county, under
officers that were local personages in the civil life
of its district, was long past. Dormer placed his
second-in-command socially as music-hall, or
pawnbroking, but the chap had been out before
and had been wounded, and probably knew something
of the job. The men were satisfactory
enough, short, stumpy fellows with poor teeth,
but exactly that sort of plainness of mind
that Dormer appreciated. They would do all
right. Perhaps a quarter of them had been out
before, and the remainder seemed fairly efficient
in their musketry and bombing, and talked
pigeons and dogs in their spare time, when not
gambling.</p>
<p class="calibre3">The bit of line they held was Reserve, a bridge-head
over the canal, a strong point round a half-demolished
château in the wood, and some wet
trenches to the right, where the next battalion<span id="Page_174" title="[174]"></span>
joined on. Battalion Head-quarters was in a
farm half a mile back. Dormer and Merfin
improvised a Mess in the cellar of the Château,
saw that the cooker in the stables was distributing
tea, and let all except the necessary guards
turn in. He had some machine gunners at the
strong point, and across the canal were two guns,
whose wagons had just been up with rations and
ammunition. His own lot of rations came soon
after and he told Merfin to take the first half of
the night, and rolled himself in his coat to sleep.</p>
<p class="calibre3">As he lay there, listening to the scatter of
machine-gun fire, and the mutter of officers’
servants in the adjoining coal-hole, watching the
candle shadows flicker on the walls that had been
whitewashed, as the draught stirred the sacking
over the doorway, his main thought was how little
anything changed. Two and a half years ago
he had been doing exactly the same thing, a few
miles away, in the same sort of cellar, in front of
an enemy with the same sort of advantage in
ground and initiative, machine guns and heavy
artillery. He was as far from beating the Germans
as ever he had been. He supposed that
practically all the gains of 1916 and 1917 south of
Arras had been lost. On the other hand, the
Germans, so far as he could see, were equally
far from winning. What he now feared was,
either by prolonged War or premature Peace, a
continuance of this sort of thing. And slowly,
for he was as mild and quiet-mannered a man as<span id="Page_175" title="[175]"></span>
one could find, his gorge began to rise. He began
to want to get at these Germans. It was no
longer a matter of principle, a feeling that it was
his duty as it had been in the days when he
enlisted, took a commission, and had come to
France. He was no longer worrying about the
injustice of the attack on Belgium or the danger
of a Germany paramount in Europe. He had
now a perfectly plain and personal feeling. But
being Dormer, this did not make him cry out for
a <i class="calibre9">sortie en masse</i> like a Frenchman, nor evolve a
complicated and highly scientific theory as to how
his desire was to be realized. The French and
Portuguese who fought beside him would have
found him quite incomprehensible. The Germans
actually invented a logical Dormer whom
they had to beat, who was completely unlike him.
If he had any ideas as to what he was going to do,
they amounted to a quiet certainty that once the
enemy came away from his heavy and machine
guns, he, Dormer, could do him in.</p>
<p class="calibre3">So he went on with the next thing, which was
to turn over and sleep. He woke, sitting bolt
upright, to the sound of two terrific crashes.
One was right over his head. The candle had
been blown out, and as he struggled out of the
cellar, barking his shins and elbows, he was aware
that the faint light of the sky was obscured by a
dense cloud all round him. Instinctively he
pulled up his gas mask, but the sound of falling
masonry and the grit he could taste between his<span id="Page_176" title="[176]"></span>
lips, reassured him. It was a cloud of brick dust.
Across the canal, the barrage was falling on the
front lines with the thunder of a waterfall. The
Bosche had hit the Château, and if he were not
mistaken, had put in another salvo, somewhere
near by. At the gate of the little park-like garden
he ran into a figure he recognized for Merfin, by
the red light of the battle, just across the canal.</p>
<p class="calibre3">“What is it?”</p>
<p class="calibre3">“Aw—they’ve knocked in the bridge!”</p>
<p class="calibre3">“Every one standing-to?”</p>
<p class="calibre3">“Can’t help ’emselves.”</p>
<p class="calibre3">They went to look at the damage. The bridge
was a small, one vehicle affair, with steel lattice
sides, and an asphalt roadway. The bridge piers
at the near end had been blown away, and the
whole had settled down some four or five feet,
on to the mud of the tow-path.</p>
<p class="calibre3">“Can you get across?”</p>
<p class="calibre3">“Aw—yes—easy!”</p>
<p class="calibre3">“Better get across and wait a bit!”</p>
<p class="calibre3">He himself went back to find up his stretcher
bearers, who, he had always noticed, wanted an
order to get them in motion. The guard on the
bridge was dead so far as he could see, but some
one was shouting, behind, at the Château.</p>
<p class="calibre3">He found the C.S.M with two men digging
out the servants whose coal-cellar had been
blocked. One of them was badly crushed, but
his own man only shaken. Then there were
horses on the road. Gunners, trying to get their<span id="Page_177" title="[177]"></span>
teams up to the advanced guns. Hopeless, of
course. Then came a runner from battalion.
Send Merfin with two platoons. He saw to that,
and rearranged his depleted company. It took
some time. The barrage appeared to be creeping
nearer. The ground shook with the continuous
concussion and whiffs of gas were more
and more noticeable, but the heavier stuff was
already falling farther to the rear. Then came a
runner from across the bridge. There was a
crowd on the road. Dormer went and found just
what he expected. Walking wounded and those
who wanted to be treated as such. He sorted
them out, directing the former down the road to
the dressing station, and setting the others to dig.
If he had got to hang on to this place, and he
supposed he had, he meant to have some cover.
The stream of people across the broken bridge
increased. Trench mortars and machine gunners,
platoons of his own regiment. The
Bosche was “through” on the left, and they were
to come back behind the canal. The barrage
died out, to confirm this. The machine-gun fire
came nearer and nearer.</p>
<p class="calibre3">In the cold grey light of a wet April dawn, a
tin-helmeted figure dashed up on a borrowed
motor-cycle. It was the Brigade Major. What
had Dormer got? He heard and saw, and took a
platoon and all the sundries. His last words
were: “Hang on here, whatever you do!”
Dormer heard the words without emotion. He<span id="Page_178" title="[178]"></span>
realized that it meant that he was expected to
gain time. He got hold of his Sergeant, and
overhauled the rations and ammunition. They
were not too badly off, and the cooker lay stranded
in the stable yard. That meant hot water, at
least. He took a turn round the place. The
Château grounds had once been wired as part of
some forgotten scheme of defence of 1915 or
early 1916. That was all right. On the other
hand, the “bridge-head”—a precious half-boiled
concoction—was full of gas and the barrier on the
road blown away.</p>
<p class="calibre3">He got his few men out of it, with their several
casualties, and started them carting brick rubble
from the dilapidations of the Château to make an
emplacement for a machine-gun on the near side
of the bridge. He stood looking at the road by
which the Bosche must come—a mere lane that
led from one of the neighbouring coal-pits, and
was used, he imagined, for transport of coal that
was required locally. It meandered out of sight,
among low fenceless fields, until the shallow
undulations of the ground hid it. In the distance
was the steamy reek of last night’s battle, but
nothing that moved, amid the silence broken
only by long-distance shots, and fusillade somewhere
on the left. Then, down that road he
saw a party advancing, led by an officer. There
was no doubt that they wore khaki. He waited
by the bridge for them, and shouted directions to
them how to cross. He got an answer:</p>
<p class="calibre3"><span id="Page_179" title="[179]"></span>“Hallo, you old devil, what are you doing?”</p>
<p class="calibre3">It was that Kavanagh. There had been an
advanced signal exchange, and he had gone to
bring his men in. They were tired, hungry
and disgusted, but Kavanagh had the jauntiness
of old. He wasn’t going back to Division, he
was going to stay with dear old Dormer, and see
this through. Dormer thought a moment, then
said: “All right.”</p>
<p class="calibre3">“All right. I should think so. I don’t
suppose I could catch Division, even on a motor-byke.
They must be nearly at Calais. It’s all
rot. The Bosche are done!”</p>
<p class="calibre3">“Are they?”</p>
<p class="calibre3">“Sure. What are they waiting for now?”</p>
<p class="calibre3">“Bringing up their artillery?”</p>
<p class="calibre3">“That won’t blow the water out of the
canal.”</p>
<p class="calibre3">“Possibly not. But we may as well have some
food while it’s possible.”</p>
<p class="calibre3">“You old guts. Always eating!”</p>
<p class="calibre3">“Yes, when I can. Aren’t you?”</p>
<p class="calibre3">“Now, Dormer. You know me better than
that. Glory is my manna.”</p>
<p class="calibre3">“Will you take cold bully and tea with it?”
asked Dormer as they dropped into the cellar.</p>
<p class="calibre3">Kavanagh made no objection, and they ate in
silence, fast, for ten minutes. Then they saw
the men were being fed, and relapsed, in their
hiding-place, into pipes, and whisky out of
Kavanagh’s flask.</p>
<p class="calibre3"><span id="Page_180" title="[180]"></span>“How did you get into this show?” Dormer
asked.</p>
<p class="calibre3">“The Division—your old Division, my boy,
left me here to hand over! They might have
spared themselves the trouble. But I’d got a most
lovely scheme of lateral communication. Corps
gave me a lot of sweet words about it. I suppose
I shall get the M.C. Now the silly old Hun has
gone and blown it all to bits. What about you?”</p>
<p class="calibre3">“You know I got wrong and was sent home
sick.”</p>
<p class="calibre3">“I heard all that. It was about that Vanderlynden
affair, wasn’t it?”</p>
<p class="calibre3">“It was!”</p>
<p class="calibre3">“Well, you’ve no idea what a sensation you
created. Vinyolles got simply wet behind the
ears with it. Some French Deputy said, after the
Somme show, that English troops did more
damage to France than to Germany. Of course
every one on Divisional H.Q. has changed in the
last few months. They all established an alibi
or Habeas Corpus or something. It was one of
the things that made the French Press go for
unity of command! You were a boon to them!”</p>
<p class="calibre3">“I wish them joy of the business. I don’t
know why you mix me up with it.”</p>
<p class="calibre3">“Why, it was your pet show, wasn’t it?”</p>
<p class="calibre3">“It got fathered on to me because I could
understand what it was about.”</p>
<p class="calibre3">“Yes, you told Vinyolles, didn’t you?”</p>
<p class="calibre3">“The ignorant brute asked me.”</p>
<p class="calibre3"><span id="Page_181" title="[181]"></span>“I know. He’s all fresh. I find him trying
also. Well, he knows all about it now.”</p>
<p class="calibre3">“Tell you the truth, I’ve no idea what I said,
Kavanagh! I was feeling queer!”</p>
<p class="calibre3">“Vinyolles thought you’d gone potty.”</p>
<p class="calibre3">“He wasn’t far wrong.”</p>
<p class="calibre3">“He said you told him the whole British Army
was guilty of the Kerrime at Vanderlynden’s!”</p>
<p class="calibre3">It was the first time Dormer had heard it called
that.</p>
<p class="calibre3">“Well, in a sense, so they are.”</p>
<p class="calibre3">“In a sense, War is a foolish business!”</p>
<p class="calibre3">“I thought you liked it?”</p>
<p class="calibre3">“I was trying to talk like you——”</p>
<p class="calibre3">Before Dormer could reply, the sacking over
the door was lifted, by Dormer’s Sergeant.</p>
<p class="calibre3">“Cop’l Arbone is back, sir!”</p>
<p class="calibre3">“Very good. Did he get in touch with the
Major?”</p>
<p class="calibre3">“He only found a Lewis-gun section, sir. The
Major moved most of the men along the canal,
where there’s more trouble!”</p>
<p class="calibre3">“All right!”</p>
<p class="calibre3">“Well, I suppose I may as well go and have a
look at my lot,” Kavanagh stretched himself.</p>
<p class="calibre3">“I told ’em to hunt round and see if they could
get this place wired up!”</p>
<p class="calibre3">“Umpteenth Corps ought to have thought of
that, long ago!”</p>
<p class="calibre3">“Did you ever know Corps think of anything.”</p>
<p class="calibre3">While Kavanagh was so engaged, Dormer took<span id="Page_182" title="[182]"></span>
a turn round the various guards and posts he had
established. There appeared to be fair cover
from view, and even from small-arm and field-gun
fire. Of course when the Bosche really wanted to
get the place, nothing Dormer and Kavanagh and
some forty men could do would stop it. In
coming round to the stables behind the Château
he found his Sergeant with two men, laboriously
trundling on a hand cart what he soon verified
to be slabs of marble. What would they think
of next? The explanation was, “There was a
champion bathroom, sir, an’ I thought we could
set up our Lewis better with these!”</p>
<p class="calibre3">When Kavanagh saw what was going on, he
laughed.</p>
<p class="calibre3">“More damage in billets, Dormer!”</p>
<p class="calibre3">“Well, the stuff will be smashed up anyhow,
won’t it?”</p>
<p class="calibre3">“Two blacks don’t make a white. I understand
why you told Vinyolles the whole army was
guilty. You’re doing just what your friend did
about his mules.”</p>
<p class="calibre3">“Why will you drag in that beastly business?
This has nothing in common with it.”</p>
<p class="calibre3">“To the common all things are common.
You tell the owner of the Château that when he
finds out.”</p>
<p class="calibre3">Dormer was going to say “He won’t find
out!” but refrained. He disliked arguing.
This seemed a particularly bad argument. Also,
at that moment, a Lewis gun began, just below.<span id="Page_183" title="[183]"></span>
Then another. He went to the garden wall,
and peered out. Nothing visible, as usual. He
thought of all the battle pictures he had ever seen.
The prancing horses, the gay uniforms, the
engrossing action of figures that pointed muzzle
or bayonet at each other, that wielded sword or
lance. Here he was, an incident in one of the
biggest battles in the world. All he could see
was neglected arable, smashed buildings, a broken
bridge and a blocked by-road, all shrouded in
steamy vapour. He made out that it was the
Lewis opposite the end of the bridge that was
firing. He crawled along the gully that had been
dug from the Château gate to the roadway, and
so to the emplacement by the step-off of the
bridge. The Corporal in charge of the section
turned to him.</p>
<p class="calibre3">“Got ’im, sir!”</p>
<p class="calibre3">“What is it?”</p>
<p class="calibre3">“Bosche in the ditch, under them bushes!”</p>
<p class="calibre3">Dormer waited a moment, but nothing happened.
He crawled back, and sent his Sergeant
round to see that every one was under cover.
Back in the cellar he found Kavanagh, and told
him.</p>
<p class="calibre3">“I know. Once more into the breach!”</p>
<p class="calibre3">“It’s not poetry, Kavanagh. This is the
start. Once they find we’re stopping them here,
they’ll shift us, you may bet!”</p>
<p class="calibre3">“I shouldn’t wonder. My lot are trying to
get into touch with Brigade. They’re running<span id="Page_184" title="[184]"></span>
a line back behind the wood. There’s no one on
our left, as far as can be found.”</p>
<p class="calibre3">“Must be some one.”</p>
<p class="calibre3">“Why should there be? Brigade have probably
moved by this time.”</p>
<p class="calibre3">“Ah, well, can’t be helped.”</p>
<p class="calibre3">No use telling the chap that it was all useless.
He just sat down and lit his pipe. He perceived
clearly enough that they were being sacrificed—just
left there to hold the Bosche up for a
few hours, while the Division went back.</p>
<p class="calibre3">During the day there was sporadic machine
gunning. The Bosche was feeling his way for
crossing the canal, but had found it far less easy
than in the sectors farther north. Tolerably
certain that the main attack would come at dawn,
Dormer and Kavanagh got what rest they could,
though proper sleep was out of the question.
Their servants had found a well-upholstered sofa,
and a superior brass bedstead, which now adorned
the cellar, causing Kavanagh to gibe about
damage in billets. Their vigil was lightened by
the sounds of song from the stables where
such men as they had set apart as reserves were
lodged.</p>
<div class="poetry-container">
<div class="poetry">
<div class="first">“Old soldiers never die,</div>
<div class="verse">They only fade away.”</div>
</div>
</div>
<p class="calibre3">to a well-known hymn tune, made Dormer home-sick,
but delighted Kavanagh.</p>
<p class="calibre3">“Listen to that!”</p>
<p class="calibre3"><span id="Page_185" title="[185]"></span>“I can’t help it, unless I send and stop them.”</p>
<p class="calibre3">“Never, man, never stop men who can sing
at such a moment. It means philosophy and
courage!”</p>
<p class="calibre3">“It means foolishness and rum!”</p>
<p class="calibre3">“Dormer, I fear you are no born leader!”</p>
<p class="calibre3">“No, of course I wasn’t.”</p>
<p class="calibre3">“But you’ve got to lead men now, and lead
’em to victory.”</p>
<p class="calibre3">“I don’t mind much so long as I lead ’em to
Peace!”</p>
<p class="calibre3">“Yes, but don’t you see, mere Peace will mean
Revolution!”</p>
<p class="calibre3">“I don’t believe it. I saw that affair at
Étaples. I saw the trouble among the French
troops in May. Those chaps prefer to take
orders from you and me rather than from their
own sort.”</p>
<p class="calibre3">“How do you account for Russia, then?”</p>
<p class="calibre3">“I can’t. But it’s an object lesson rather than
an example, I should say.”</p>
<p class="calibre3">“You used not to talk like that. You used to
say that the men wouldn’t stand it.”</p>
<p class="calibre3">“I’ve lived and learned!”</p>
<p class="calibre3">“Both, I am sure.”</p>
<p class="calibre3">“You needn’t be so superior. No one knew
what any of this would be like until it was tried.
We’ve something to go by, now! This War
depends on turning a crank. The side that goes
on turning it efficiently the longer will win.
Our chaps look like lasting!”</p>
<p class="calibre3"><span id="Page_186" title="[186]"></span>“So do the Bosche. No, Dormer, you’re all
wrong——”</p>
<p class="calibre3">At that moment a fresh burst of song came
from the stables. A Cockney voice to a waltz
tune:</p>
<div class="poetry-container">
<div class="poetry">
<div class="first">“Orl that I wawnt is larve,</div>
<div class="indent">Orl that I need is yew——”</div>
</div>
</div>
<p class="calibre3">“There,” cried Kavanagh, his voice rising
into his excited croak. “That’s what we want!”</p>
<p class="calibre3">Dormer did not reply. With dusk came a few
long-range shots, gradually broadening and
deepening into a bombardment towards dawn.
Both of them had to be out and about all night.
They had several casualties, and the whole place
reeked with gas. As the grey light of another
day began to change the texture of the shadows,
movement was discernible about the road. It
was their chance and with a higher heart and the
feeling of relief, they were able to let loose the
Lewis guns, which they had managed to save
intact. For more than an hour, Dormer crawled
from one to the other, seeing that they did not
overheat or jam, for the fact that they were
killing Germans pleased him. Then there was a
slackening of fire on both sides.</p>
<p class="calibre3">They waited and the suspense from being
irksome, became tolerable. There was a good
deal of noise each side of them, and Dormer began
to wonder if his detachment were surrounded,
especially as the servants whom he had sent back<span id="Page_187" title="[187]"></span>
to get into touch with Brigade, had not returned.
It was a dull rainy afternoon prematurely dark,
and the rain as it increased, seemed to beat down
the gunning, as water quenches a fire. He must
have been in that half-waking state that often
superimposed on sleeplessness and the awful din,
when he was thoroughly roused by trampling in
the trees round the Château. He called to
Kavanagh but got no reply. Then there was a
pushing and scrambling at the wall behind the
stable, and English cavalrymen came swinging
over it. Dormer and Kavanagh were relieved,
and were shortly able to hand over and prepare
to march their command back to rejoin their
Division, which, depleted by four weeks of continual
mauling, was being taken out of the line.</p>
<p class="calibre3">The battle was by no means over. They next
went in farther north, and Dormer had the queer
experience of going into trenches where Corps
H.Q. had been, of billeting in rooms where Major-Generals
had slept. Gradually he became aware
of lessening tension, reduced shelling, and
slackened machine-gun fire, but it was the end
of May before he found, when sent to raid an
enemy post, that there was no one there. He
had been right after all. The German offensive
also had failed. Anticlimax was the rule of the
War. He was glad that he had parted from
Kavanagh, who had gone back to his proper job
with his Division, goodness knew where. He
felt that the fellow would remind him that for<span id="Page_188" title="[188]"></span>
several hours while they lay together in those
scratched-out trenches round that little Château
by the canal, he had given up hope. He need not
have bothered. If the Bosche could not win on
that day, he never would. Slowly now the British
lines were creeping forward. Then he found
American troops behind him.</p>
<p class="calibre3">It was during this phase of things that he
found himself upon familiar ground. Except on
Kavanagh’s lips, he had not heard of the crime at
Vanderlynden’s since before Christmas. It was
now September. Here he was, detrained and
told to march to Hondebecq. He passed what
had been Divisional Head-quarters in 1916 and
noticed the shell-holes, the open, looted, evacuated
houses. He passed along the road which he
and Major Stevenage had traversed all those years
ago. The Brigade were in Divisional Reserve,
and were quartered in a string of farms just outside
the village. He looked at the map squares
attentively, but on the larger scale map he found
it actually marked Ferme l’Espagnole. Being
Dormer, he just saw to the billeting of his company
and then learned that the Battalion Head-quarters
were located at the Vanderlyndens’, and
had no difficulty in finding good reason to walk
over there, after tea.</p>
<p class="calibre3">The place was not much changed. It was
soiled, impoverished, battered by War, but the
German advance, which had stopped dead a few
miles short of it, had been spent by the time it<span id="Page_189" title="[189]"></span>
reached its limits in this sector, and had early
been pushed back. Trenches had been dug and
camouflage erected all round the place, but it had
not suffered damage except by a few long-distance
shots, the routine of trench warfare had never
reached it. In the kitchen, darkened by the
fact that the glass was gone from the windows,
which were blinded with aeroplane fabric, stood
the familiar figure of Mademoiselle Vanderlynden.
He asked for the Colonel, and was civilly directed
to the parlour on the other side of the door. Not a
word of recognition, hardly a second glance. He
did not know if he were sorry or glad. He would
have felt some relief to hear that the claim that
had caused all that trouble had been settled. But
he did not know what he might bring down upon
his head by inquiry and held his tongue. His
business with the Colonel was the usual regimental
routine, nominal and numerical rolls, reinforcements
and indents, training and movements. It
did not take long. On his way out he passed the
kitchen door and said just:</p>
<p class="calibre3">“Good night, Mademoiselle!”</p>
<p class="calibre3">“Good night, M’sieu!” And then calmly:
“They are going to pay us for the damage to
<i class="calibre9">La Vierge</i>!”</p>
<p class="calibre3">“I am glad to hear it.”</p>
<p class="calibre3">“I thought you would like to know. It has
been a long time.”</p>
<p class="calibre3">“Yes, a long time. I hope it will soon be
settled.”</p>
<p class="calibre3"><span id="Page_190" title="[190]"></span>“Ah, not yet. I know these offices at
Boulogne! They have a good deal to pay for, no
doubt.”</p>
<p class="calibre3">“No doubt. Good night, Mademoiselle!”</p>
<p class="calibre3">“Good night, <i class="calibre9">mon capitaine</i>.”</p>
<p class="calibre3">Walking back to his billet, he had once more
that sensation of escape. Was he really going to
get away from that business, this time, for ever?
True, Mademoiselle Vanderlynden seemed little
enough inclined to be vindictive. He could not
help feeling that her view of the affair was after all
reasonable and just. She bore no malice, she
wanted things put right. Money would do it.
She was going to get the money, or so she seemed
to think. She had no animus against the man
who had broken a piece of her property. She had
neither animus against nor consideration for himself,
the representative of the British Army, who
had so signally failed to hasten the question of
compensation. She took it all as part of the War,
and she was seeing it correctly. It was the British
Army that had done it. Her home, where she
was working so peacefully in 1914, had become
first a billet, then all but a battlefield. The Crime
at Vanderlynden’s was the War, nothing more nor
less. That was exactly what he felt about it.
No damage had been done to any furniture or
valuables that he owned, but he had still to get
out of it with his body intact, and resume the
broken thread of existence, where it had been
snapped off, all those four years ago. True he<span id="Page_191" title="[191]"></span>
had not been badly paid, but he had taken a considerable
risk—it was much more dangerous to
be an officer than a private, more dangerous to be
a private than a civilian. She had gauged the
whole thing correctly, right down to the necessarily
slow and complicated process of getting it
adjudicated by some set of fellows down by the
coast, who ran these things off by the hundred
and had a whole set of rules that had to be complied
with. He turned at the end of the farm
road and took a look back at the old place. There
were worse billets than the Spanish Farm and
people more awkward to deal with than the
Vanderlyndens. In the Somme he had come
across farms where they charged you for the water
and people who removed everything right down
to the bedsteads. Vanderlynden had only wanted
to be paid for what was wantonly damaged.
They were French, you couldn’t expect them to be
sympathetic about other people’s mules. What
a queer world it was, he would never have suspected
all the crotchets that human nature could
present, had he not been thrust nose-foremost
into this infernal show.</p>
<p class="calibre3">All his philosophy forsook him, however, on
entering the billet where his company was lodged.
The woman had been selling not merely beer,
which was connived at, but spirits, to the men.
Two of them had got “tight” and had been
arrested, and he would have them up before him
in the morning. Then there would be the question<span id="Page_192" title="[192]"></span>
as to where she got the spirits from, whether
some Quartermaster-sergeant had been making
away with the rum, or whether she had induced
some one to buy it for her at the Expeditionary
Force Canteen. It all came back to the same
thing. Men kept under these conditions too
long.</p>
<p class="calibre3">No one had been more surprised than Dormer,
when the Allied Armies took up the initiative
again in July, and appeared to keep it. With a
lugubrious satisfaction he found himself retracing
the advances in the Somme district of 1916. It
was an ironical comment on his hard-earned War-wisdom,
two years devoted to doing precisely the
same thing at precisely the same place. Of
course, he had learned some lessons, but his
estimate of one hundred and eighty years was still
too small. But when the movement became perpetual
and he found himself on ground he no
longer recognized, among villages that showed
all the signs of methodical German occupation,
he began to wonder. A slight wound in the
forearm threw him out of touch for a week or two,
and when he went back, he found himself in a
more northern sector again, and for the first time
found cavalry in front of him. It suited him all
right, he didn’t want to have the job of bombing
out little nests of machine gunners, that marked
each step in the line of advance. His feelings
were pretty generally shared. Men began to ask
themselves whether there was any glory in being<span id="Page_193" title="[193]"></span>
knocked out at the moment of victory. When
his battalion was again obliged to move in advance
of the cavalry, against obstacles which, although
always evacuated, were out of the sphere of cavalry
tactics, he found for the first time a definite unwillingness
among his command to obey orders in
any but the most perfunctory manner.</p>
<p class="calibre3">He had sufficient sense to see that it was very
natural. In the early days the job had been to
keep men under cover, to avoid useless and wasteful
casualties. The lesson had been learned at
length with a thoroughness that he could never
have instilled. The old, old boast of the Territorial
Colonel who had first enlisted him, and
whose tradition was actually of pre-Territorial
days, from the period of the Volunteers of before
the Boer War, was far better founded than he had
ever supposed. He had been inclined to scoff
when he had heard the old boy talk: “Our
motto was Defence not Defiance!” He did not
scoff now. It was deeply, psychologically true.
The army that had survived was an army that had
been made to fight without much difficulty, while
its back was to the sea, with the knowledge that
trenches lost meant worse, if possible, conditions
of existence, and it was moved by some rags of
sentiment, as to holding what one had got; an
army which displayed all the slowly aroused,
almost passive pugnacity of the English working
class, so docile, yet so difficult to drive out of a
habit of mind, or an acquired way of living.<span id="Page_194" title="[194]"></span>
They had no real imperialism in them, none of the
high-faultin’ Deutschland über Alles, none of the
French or Italian bitter revengefulness, nor
peasant passion for acquisition. The Rhine had
never figured in their primary school education.
They had no relatives groaning under Austrian
or German domination—no rancorous feelings
bred from the attempt to force alien language or
unassimilated religious forms down their throats.</p>
<p class="calibre3">He had always regarded the boast about an
Englishman’s House being his Castle as so much
claptrap. He knew by daily experience of
business, that any Englishman was governed
by economic conditions. Religious and racial
tyranny were so far removed from the calculations
of all his sort, and all above and below it, that the
very terms had ceased to have any meaning.
This War had no effect on the lightly borne if real
tyranny of England, the inexorable need to get
a permanent job if possible and keep it, with constant
anxiety as to the tenure of one’s lodging,
and the prospect of old age. These fellows who
fell in with blank unmeaning faces, in which there
was no emotion, and who marched with the same
old morose jokes, and shyly imitated the class
standards which he and those like him handed
down to them from the fount of English culture
and fashion in the Public Schools, had done what
they had promised to do, or had (the late comers)
been conscripted to do. They had engaged or
been called up for duration. That was a typically<span id="Page_195" title="[195]"></span>
English slogan for a European War. Their
Anglia Irridenta lay in the football fields and
factories, the music-halls and seaside excursions
that they talked of, and now hoped to see once
again. Their Alsace Lorraine lay in the skilled
occupations or soft jobs that women or neutrals
had invaded. When he listened to their talk in
billets, and occasionally caught some real glimpse
of them, between their mouth-organ concerts, and
their everlasting gamble at cards, it was of the
keen Trades Unionists who were already talking
of purging this, that or the other skilled industry
from all the non-union elements that had been
allowed to flow into it, behind their backs, while
they were chasing Fritz across this b——
country, where Belgium, France, or Luxembourg
were simply “billets,” and the goal was “dear
old Blighty”—behind them, over the Channel,
not in front, still ringed about by German trenches.</p>
<p class="calibre3">There were elements of hesitation, he noticed,
in all the Allies. The French felt they had done
much too much, and wanted to be back at their
farms and little shops. The Belgians wanted
to march into their country without the tragic
necessity of knocking flat all its solidly built,
hard-working little towns. All three nations
shared the inevitable sense that grew upon men
with the passage of years, of the mechanical nature
of the War. Thus the cavalry, where the greatest
proportion of regular soldiers lingered, were still
keen on exploiting their one chance. The<span id="Page_196" title="[196]"></span>
artillery, buoyed up by the facilities that their
command of transport gave them, fired away their
now all abundant ammunition. The machine
gunners, containing some proportion of picked
men, and able to feel that they could easily produce
some noticeable effect with their weapon, were
still game. But the mass of infantry, tired enough
of the bomb and the rifle, and probably unfitted
by generations of peace, for any effective use of
the bayonet, were rapidly adopting the attitude,
unexpressed as always with the humbler Englishman,
of “Let the gunners go on if they like.
We don’t mind!”</p>
<p class="calibre3">On a grey November morning, Dormer went
to his billet in the suburb of a manufacturing
town. It was the most English place he had
set eyes on in all his three years. It was not
really suburban, very nearly, not quite. There
was no garden before the door, it was close to the
factories and workshops where the wealth that
had built it was made, instead of being removed
a decent mile or so. In fact, it just lacked the
proper pretentiousness. Its owner had made
money and was not in the least ashamed of admitting
it, was rather prone to display the fact and
his house looked like it. It was a villa, not a
château. It was the home of a successful manufacturer
who did not want in the least to be taken
for a country gentleman. He, poor fellow, had
been called up and promptly killed, and his home,
with its stained-glass windows, expensive draping<span id="Page_197" title="[197]"></span>
and papering, clumsy if efficient sanitation, was
inhabited only by his widow.</p>
<p class="calibre3">Dormer thought there could not be in the
world any person so utterly beaten. Broken-hearted,
exposed during four years to considerable
bodily privation, being in the occupied area,
she was no Mademoiselle Vanderlynden of the
Army zone that Dormer knew, making a bold
front against things. She was a delicate—had
been probably a pretty woman—but it was not
from any of her half-audible monosyllabic replies
that Dormer was able to discover to what sort
of a country he had come. A little farther down
the street was the factory, long gutted by the
Germans and used as a forage store, where his
company were billeted. The old caretaker in
the time-keeper’s cottage told Dormer all that
was necessary, and left him astonished at the
moderation of tone and statement, compared with
the accounts of German occupation given by the
Propagandist Press. Possibly, it was because he
addressed the old man in French—or because
he had never parted with his English middle-class
manners—or because the old fellow was
nearly wild with delight at being liberated. This
was what Dormer heard:</p>
<p class="calibre3">“Enter, my Captain. It is a Captain, is it
not, with three stars? The insignia of Charles
Martell!” (Here wife and daughter joined in
the laugh at what was obviously one of the best
jokes in father’s repertory.) “You will find that<span id="Page_198" title="[198]"></span>
the Bosches removed everything, but that makes
less difficulty in the workshop. You have only
to divide the floor space between your men. I
know. I was a corporal in the War of ’Seventy.
Ah! a bad business, that, but nothing to what
we have now supported. You will do well to
make a recommendation to your men not to drink
the water of the cistern. The Bosches have made
beastliness therein. Ah! You have your own
watercart? That is well done, much better than
we others used to have, in Algeria. It is always
wise to provide against the simple soldier, his
thoughts have no connection. You say you are
accustomed to Germans and their mannerisms?
I do not wonder. We too, as you may judge,
have had cause to study them. I will tell you
this, my Captain, the German is no worse than
any other man, but he has this mania for Deutschland
über Alles. It comes from having been a
little people and weak, and so often conquered
by us others. So that to give him some idea
of himself, since he cannot invent a culture like
us other French, he must go to put all above
below, and make a glory of having a worse one.
That shows itself in his three great faults—he has
no sentiment of private property—what is others’,
is his. He must be dirtier than a dog in his
habits—witness our court-yard—and he has to
make himself more brute than he really is. You
see, therefore, he has stripped the factory, and
even our little lodging, down to my daughter’s<span id="Page_199" title="[199]"></span>
sewing-machine, and the conjugal bed of mother
and myself. You see also, that we had our
grandchildren, our dog Azor, our cat Titi. Now
many of the Bosches who lodged here were
certainly married and had their little ones and
domestic animals. Yet if they found a child or
a beast playing in the entry when they entered
or left, they must give a kick of the foot, a cut
with the riding-whip. Not from bad thoughts,
I assure you. It is in their code, as it is in that
of us others, English and French, to lift the hat,
to make a salutation. The officers are the worst,
because in them the code is stronger. For the
German simple soldier, I have respect. They
sang like angels!” (Here the old man quavered
out the first bars of:</p>
<p class="center">“Ein feste Bourg ist unser Gott.”)</p>
<p class="calibre3">Dormer wanted to get away, but could scarcely
forbear to listen when the daughter broke in:</p>
<p class="calibre3">“But, Papa, recount to the officer the droll
trick you played upon those who came to demolish
the factory!”</p>
<p class="calibre3">“Ah, yes. Place yourself upon a chair, my
officer, and I will tell you that. Figure to yourself
that these Bosches, as I have explained, were
not so bad as one says in the papers. They
had orders to do it. I know what it is. I
have had orders, in Algeria, to shoot Arabs. It
was not my dream, but I did it. I will explain
to you this.</p>
<p class="calibre3"><span id="Page_200" title="[200]"></span>“It was the day on which they lost the ridge.
One heard the English guns, nearer and nearer.
Already there were no troops in the factory,
nothing but machine gunners, always retreating.
A party of three came here with machinery in
a box. One knew them slightly, since they also
had billeted here. They were not dirty types;
on the contrary, honest people. Sapper-miners,
they were; but this time one saw well that they
had something they did not wish to say. They
deposit their box and proceed to render account
of the place. They spoke low, and since we
have found it better to avoid all appearance of
wishing to know their affairs, we did not follow
them. Only, my daughter had a presentiment.
Woman, you know, my officer, it is sometimes
very subtle. She put it in her head that these
would blow up the factory. She was so sure
that I lifted the cover of their box and looked in.
It was an electric battery and some liquids in
phials. I had no time to lose. I placed myself
at the gate and ran as fast as I can to where they
were, in the big workshop. I am already aged
more than sixty. My days for the race are over.
Given also that I was experiencing terrible sentiments—for
you see, while we keep the factory
there is some hope we may be able to work
when the War is finished, but if it is blown up,
what shall we others go and do—I was all in a
palpitation, by the time I reached them. I
cried: ‘There they go!’</p>
<p class="calibre3"><span id="Page_201" title="[201]"></span>“‘Who goes?’ they asked.</p>
<p class="calibre3">“‘The cavalry,’ I cried.</p>
<p class="calibre3">“They ran to the entry, and seeing no one,
they feared that they were already surrounded.
I saw them serpentine themselves from one doorway
to another all down the street. The moment
they were lost to sight I flung their box into the
big sewer!”</p>
<p class="calibre3">Dormer billeted his company in the factory.
He did not fear shell-fire that night. He himself
slept in a bed at the villa. It was the first time
he had left the night guard to a junior officer.
In the morning, he paraded his company, and
proceeded, according to plan, to await the order
to move. The days were long gone by when a
battalion was a recognizable entity, with a Mess
at which all the officers saw each other once a
day. Depleted to form Machine Gun Companies,
the truncated battalions of the end of the War
usually worked by separate companies, moving
independently. There was some desultory firing
in front, but his own posts had seen and heard
nothing of the enemy. About nine he sent a
runner to see if his orders had miscarried. Reply
came, stand to, and await developments. He
let his men sit on the pavement, and himself
stood at the head of the column, talking with the
two youngsters who commanded platoons under
him. Nothing happened. He let the men
smoke. At last came the order: “Cease fire.”</p>
<p class="calibre3"><span id="Page_202" title="[202]"></span>When he read out the pink slip to his subordinates,
they almost groaned. Late products of
the at last up-to-date O.T.C.’s of England, they
had only been out a few months and although
they had seen shell-fire and heavy casualties,
yet there had always been a retreating enemy,
and fresh ground won every week. The endless-seeming
years of Trench Warfare they had missed
entirely. The slow attrition that left one alone,
with all one’s friends wounded or killed, dispersed
to distant commands or remote jobs, meant nothing
to them. They had been schoolboys when
Paschendaele was being contested, Cadets when
the Germans burst through the Fifth Army.
They wanted a victorious march to Berlin.</p>
<p class="calibre3">Dormer read the message out to the company.
The men received the news with ironical silence.
He had the guards changed, and the parade
dismissed, but confined to billets. He heard
one of his N.C.O.’s say to another: “Cease fire!
We’ve got the same amount of stuff on us as
we had two days ago!”</p>
<p class="calibre3">It made him thoughtful. Ought he to
crime the chap? Why should he? Had the
Armistice come just in time? If it hadn’t come,
would he have been faced with the spectacle of
two armies making peace by themselves, without
orders, against orders, sections and platoons and
companies simply not reloading their rifles,
machine gunners and Trench Mortars not unpacking
their gear, finally even the artillery<span id="Page_203" title="[203]"></span>
keeping teams by the guns, and the inertia
gradually spreading upwards, until the few at
the top who really wanted to go on, would have
found the dead weight of unwillingness impossible
to drag? The prospect, though curious, was not
alarming. In a country so denuded and starved,
one could keep discipline by the simple expedient
of withholding rations. He had already seen, a
year before at Étaples, the leaderless plight of
all those millions of armed men, once they were
unofficered. He was not stampeded by panic,
and his inherited, inbred honesty bade him ask
himself: “Why shouldn’t they make Peace
themselves?” The object that had drawn all
these men together was achieved. The invasion
of France was at an end, that of Belgium a
matter of evacuation only. “Cease fire.” It
almost began to look like an attempt to save
face. Was it the same on the German side too?</p>
<p class="calibre3">In the afternoon he proposed to walk over to
Battalion H.Q. and have a word with the Colonel.
He knew quite well he should find the other
company commanders there. Naturally every
one would want to get some idea of what was
to be expected under these totally unprecedented
circumstances. He was met at the door of his
billet by a message from the youngster he had
left in charge. He had got a hundred and forty
prisoners.</p>
<p class="calibre3">Dormer went at once. He could see it all
before he got there. All along the opposite<span id="Page_204" title="[204]"></span>
side of the street, faultlessly aligned and properly
“at ease” were men in field grey. At either
end of the line stood a guard of his own company,
and not all Dormer’s pride in the men he had
led with very fair success, with whose training
and appearance he had taken great pains, could
prevent his admitting to himself that the only
point at which his lot could claim superiority
was in a sort of grumpy humour. The machinery
of War had conquered them less entirely than
it had conquered the Germans.</p>
<p class="calibre3">In the little time-keeper’s box, turned into
the company office, he found a tall, good-looking
man, who immediately addressed him in perfect
English, giving the rank of Feld Webel, the
quantity and regiment of his party and adding:
“I surrender to you, sir.” Dormer gave instructions
that the party should be marched to
Brigade Head-quarters. He wanted to send some
report as to the capture, but his subordinate
replied: “We didn’t capture ’em. They just
marched up the street. The post at the bridge
let ’em through.” Dormer let it go at that,
and having seen the street cleared, he walked
over to see his Colonel, who was billeted in a
big school in a public park. His story was
heard with that sort of amusement that goes with
the last bottle of whisky, and the doubt as to
when any more will be obtainable. The Adjutant
said: “Simply gave ’emselves up, did they?”</p>
<p class="calibre3">But the Captain commanding C Company,<span id="Page_205" title="[205]"></span>
a man of about Dormer’s own sort and service,
voiced Dormer’s thought.</p>
<p class="calibre3">“I believe, in another week, we’d have had
both sides simply laying down their arms.”</p>
<p class="calibre3">“Oh, nonsense, soon stop that!” The
Colonel spoke without real conviction. He had
to say that officially.</p>
<p class="calibre3">With regard to the object for which he had
come, Dormer found every one in his own difficulty.
No one knew what was to happen, except
that arrangements were already on foot for
enormous demobilization camps. But the immediate
steps were not even known at Brigade.
Every one, of course, aired some pet idea, and
were interrupted by noise outside, shouts and
cries, the sound of marching, and orders given
in German. The room emptied in a moment.
The park was at one end of the town, and abutted
on the smaller streets of artisans’ dwellings that,
in every town of the sort, goes by the name of
Le Nouveau Monde. This quarter had apparently
emptied itself into the park, to the number
of some hundreds, mostly people of over military
age, or children, but one and all with those thin
white faces that showed the long years of insufficient
and unsuitable food, and the spiritual
oppression that lay on “occupied” territory.
They were shouting and shaking their fists round
the compact formation of Dormer’s prisoners,
who had just been halted, in front of the house.
The N.C.O. in charge had been ordered by<span id="Page_206" title="[206]"></span>
Brigade to bring them back. A chit explained
the matter: “Prisoners taken after 11.0 a.m. to
be sent back to their own units, on the line of
retreat.”</p>
<p class="calibre3">The Feld Webel enlightened the Colonel’s
mystification: “We refuse to obey the order,
sir. Our regiment is twenty miles away. All
the peasants have arms concealed. We shall just
be shot down.”</p>
<p class="calibre3">It was a dilemma. Dormer could not help
thinking how much better the Feld Webel
showed up, than his own Colonel. The latter
could not shoot the men where they stood. Nor
could he leave them to the mercies of the natives.
How difficult War became with the burden of
civilization clogging its heels. The first thing
to do was obviously to telephone to the A.P.M.
for police. In the meantime a French Liaison
Officer made a speech, and Dormer grinned to
hear him. Fancying apologizing for the War.
But what else could the fellow do. He did it
well, considering. The crowd quieted, thinned,
dispersed. The police arrived, and had a discussion
with the Adjutant. Still no conclusion.
The Feld Webel strode up and down in front
of his men, master of the situation. At length,
some one had an idea. Six lorries rolled up
in the dark, an interpreter was put on board,
and the party moved off in the November dusk.
The Commander of C Company and Dormer
left H.Q. together. Parting at the corner that<span id="Page_207" title="[207]"></span>
separated their scattered companies, they both
exclaimed together:</p>
<p class="calibre3">“What a War!” and burst out laughing.</p>
<p class="calibre3">It was perhaps, to a certain degree, Dormer’s
fault, that during the remainder of November
he became conscious of a dreary sense of anti-climax.
No doubt he was that sort of person.
The emergencies of the War had considerably
overstrained his normal powers, which he had
forced to meet the need. The need had ceased,
and he had great difficulty in goading himself
up to doing the bare necessary routine of Company
office parades. He managed to avoid being
sent up to the Rhine, and even secured a reasonable
priority in demobilization, but beyond this
there was nothing for it but to “continue the
motion” of waiting for the next thing to
happen.</p>
<p class="calibre3">His principal job was to extract from an unwilling
peasantry, enough ground for football.
How often did he go to this farm and that village
shop, with his best manner, his most indirect
approach, liberal orders for any of the many
commodities that could be bought, and in the
last resort, cheerful payment of ready money out
of his own pocket in order to obtain a grudging
leave to use this or that unsuitable meadow,
not to the extent that the game of football
demanded, but to the extent that the small proprietors
considered to be the least they could<span id="Page_208" title="[208]"></span>
make him accept for the most money that he
could possibly be made to pay.</p>
<p class="calibre3">Then, in the long dark evenings, there was
the job of keeping the men away from the worse
sorts of estaminet. His own abilities, limited
to singing correctly the baritone part of Mendelssohn’s
Sacred Works, or Sullivan’s humorous
ones, was not of any practical service. What
was wanted was the real star comic, the red-nosed
man with improbable umbrella, the stage clergyman
with his stage double-life and voice that
recalled with such unintentional faithfulness, the
affected mock-culture of the closed and stereotyped
mind. Any deformity was welcome, not, Dormer
observed, that they wanted to laugh at the helplessness
of the bandy leg or the stutterer, the dwarf
or the feeble-minded. On the contrary, the
sentimentality of the poorer English had never
stood out in brighter relief than on the edge of
those devastated battlefields, where in their useless
khaki, the men who had perpetuated the
social system that had so blindly and wantonly
used so many of them, waited patiently enough
for the order of release from the servitude that
few of them had chosen or any of them deserved.
No, they liked to see the cunning and prowess
of the old lady, or the innocent boy, applauded
the way in which all those characters portrayed
as having been born with less than normal
capabilities showed more than normal acquisitiveness
or perspicacity.</p>
<p class="calibre3"><span id="Page_209" title="[209]"></span>Dormer could not help reflecting how different
they were from the New Army in which he had
enlisted. In the squad of which, at the end of
three months’ violent training and keenly contested
examinations, he had become the Corporal,
there had been one or two labourers, several
clerks from the humbler warehouses and railways,
others in ascending scale from Insurance Offices
and Banks, one gorgeous individual who signed
himself a Civil Servant, three persons of private
means, who drove up to the parade ground in
motor-cars. He well remembered one of these
latter going surreptitiously to the Colonel and
applying for a commission, and being indignantly
refused, on the grounds that the Colonel didn’t
know who (socially) he (the applicant) was. But
when the news got out, the section were even
more disrespectful to that unfortunate individual
because they considered he had committed a
breach of some sort of unwritten code that they
had undertaken to observe. So they went on
together, the immense disparity of taste and
outlook cloaked by shoddy blue uniforms and
dummy rifles, equal rations and common fatigues.</p>
<p class="calibre3">But the first offensive of the spring of 1915
had brought new conditions. The loss in
infantry officers had been nothing short of
catastrophic. Very soon hints, and then public
recommendation to take commissions reached
them. The section meanwhile had altered. Two
of the more skilled labourers had got themselves<span id="Page_210" title="[210]"></span>
“asked for” by munition works. Of the remainder
Dormer and four others applied and
got commissions. He could see nothing like it
now. There was more of a mix-up than ever.
For some men had been exempted from the
earlier “combings out” of the unenlisted for
skill, and others for ill-health. There was now
only one really common bond, the imperative
necessity to forget the War and all that had to
do with it. This was the general impetus that
had replaced the volunteering spirit, and it was
this that Dormer had to contend with. He
mastered the business of amusing the men pretty
well, and his subordinates helped him. A more
serious difficulty was with the skilled mechanics.
Fortunately, an infantry battalion demanded little
skill, and except for a few miners who had been
out no time at all, and were at present making
no fuss, there was plenty of grumbling but no
organized obstruction.</p>
<p class="calibre3">He found a more advanced state of affairs
when he went at the appointed time, to supervise
a football match between a team representing
his own Brigade and that of a neighbouring
Brigade of Heavy Artillery. Crossing the Grand’
Place of the village to call on the Gunner Mess
he found a khaki crowd, but it took him some
minutes to realize that a full-dress protest meeting
was in progress. Senior N.C.O.’s were mounted
upon a G.S. wagon. These, he gathered, were
the Chairman and speakers. Another soldier,<span id="Page_211" title="[211]"></span>
whose rank he could not see, was addressing
the meeting. More shocked than he had ever
been in his life, he hastily circled the square,
and got to the Mess. He found most of the
officers in; there was silence, they were all
reading and writing. After the usual politenesses
came a pause. He felt obliged to mention the
object of his visit. Silence again. Eventually
the Captain with whom he had arranged the
preliminaries of the match said rather reluctantly:</p>
<p class="calibre3">“I’m afraid we shan’t be able to meet you
this afternoon.”</p>
<p class="calibre3">Dormer forebore to ask the reason, but not
knowing what else to do, rose and prepared to
take his leave. Possibly he spoke brusquely,
he was nervous in the atmosphere of constraint,
but whatever may have prompted the Gunner
Captain, what he said was a confession:</p>
<p class="calibre3">“Our fellows are airing their views about
demob.”</p>
<p class="calibre3">“Really!”</p>
<p class="calibre3">“Yes, perhaps you noticed it, as you came
along?”</p>
<p class="calibre3">“Well, I did see a bit of a crowd.”</p>
<p class="calibre3">“You didn’t hear the speeches?” The other
smiled.</p>
<p class="calibre3">“I heard nothing definitely objectionable, but
it’s rather out of order, isn’t it?”</p>
<p class="calibre3">“Well, I suppose so, but we get no help from
up-atop!” The Captain nodded in the direction
of the Local Command.</p>
<p class="calibre3"><span id="Page_212" title="[212]"></span>“No, I suppose not,” Dormer sympathized.</p>
<p class="calibre3">The young Colonel interposed. “It’s very
difficult to deal with the matter. There’s a high
percentage of skilled men in our formation.
They want to be getting back to their jobs.”</p>
<p class="calibre3">“It’s really rather natural,” agreed the Captain.</p>
<p class="calibre3">Dormer tried to help him. “We all do, don’t
we?”</p>
<p class="calibre3">There was a sympathetic murmur in the Mess
which evidently displeased the Colonel.</p>
<p class="calibre3">“I’m not accustomed to all this going home
after the battle. Time-expired men I understand,
but the New Army enlistments——” He left
it at that, and Dormer felt for him, probably,
with the exception of a few servants and N.C.O.’s,
the only pre-War soldier in the Mess, uncertain
of himself and trying not to see the ill-suppressed
sympathy if not envy with which most of the
officers around him regarded the affair.</p>
<p class="calibre3">“Awfully sorry, Dormer,” the Captain concluded,
“we simply can’t get our crowd together.
You see how it is. When this has blown over
I’ll come across and see you, and we will fix
something up.”</p>
<p class="calibre3">Dormer went.</p>
<p class="calibre3">The Gunner Captain came that evening. In
Dormer’s smaller Mess, it needed only a hint
to the youngsters to clear out for a few minutes.
Dormer admired the good humour with which
the other approached him. It was obviously the
only thing to do.</p>
<p class="calibre3"><span id="Page_213" title="[213]"></span>Over drinks he asked, modelling himself on
the other’s attitude:</p>
<p class="calibre3">“So that business blew over, did it?”</p>
<p class="calibre3">“It did, thank goodness. Awfully decent of
you to take it as you did. I hated letting you
down.”</p>
<p class="calibre3">“Don’t mention it. I saw how you were
placed.”</p>
<p class="calibre3">“The Colonel very much appreciated the way
you spoke. I hope you had no trouble with
your chaps?”</p>
<p class="calibre3">“They were all right. I pitched them a yarn.
They didn’t believe it, of course. Some of them
were at the—er——”</p>
<p class="calibre3">“The bloomin’ Parliament. Don’t mind saying
it. It’s a dreadful shock to a regular like
our old man.”</p>
<p class="calibre3">“Naturally.”</p>
<p class="calibre3">“He spoke the plain unvarnished truth
when he said he was unused to all this demobbing.”</p>
<p class="calibre3">“Well, well, you can comfort him, I suppose, by
pointing out that it isn’t likely to occur again.”</p>
<p class="calibre3">“He’s a good old tough ’un. Splendid man
in action, that’s what makes one so sorry about
it. Otherwise, of course, one knows what the
men mean. It’s only natural.”</p>
<p class="calibre3">“Perfectly.”</p>
<p class="calibre3">“His trouble is not only the newness of it.
It’s his utter helplessness.”</p>
<p class="calibre3">“Quite so. Absolutely nothing to be done.<span id="Page_214" title="[214]"></span>
The—er—meeting was as orderly as possible. I
walked right through it. They simply ignored
me.”</p>
<p class="calibre3">“Oh yes, there’s no personal feeling. They
all paraded this morning complete and regular.”</p>
<p class="calibre3">“That’s the end of it, I hope.”</p>
<p class="calibre3">“I think so. They came up to the Mess—three
N.C.O.’s—a deputation, if you please.
They brought a copy of the resolution that was
passed.”</p>
<p class="calibre3">Neither could keep a straight face, but laughter
did not matter because it was simultaneous. The
Captain went on, finishing his drink:</p>
<p class="calibre3">“I believe the old man had a momentary
feeling that he ought to crime some one—but
our Adjutant—topping chap—met them in the
passage and gave them a soft answer, and cooked
up some sort of report, and sent it up. It
pacified ’em.”</p>
<p class="calibre3">“Did they need it?”</p>
<p class="calibre3">“Not really. ’Pon me word, never saw anything
more reasonable in my life, than what
they had written out. It’s too bad, hanging
’em up for months and months, while other
people get their jobs. They know what they
want so much better than anyone else.”</p>
<p class="calibre3">“It’s impossible to please every one.”</p>
<p class="calibre3">“Yes. But when you think of what the men
have done.”</p>
<p class="calibre3">Dormer did not reply. He was thinking of
the Infantry, with their whole possessions on<span id="Page_215" title="[215]"></span>
their backs, always in front in the advance, last
in the retreat. The Gunner took his leave.
Like everything else, either because of the
incident, or more probably without any relation
to it, the slow but steady progress of demobilization
went on, those men who had the more real
grievance, or the greater power of expression,
got drafted off. The composition of units was
always changing. Even where it did not, what
could “other ranks” do? To the last Dormer
felt his recurrent nightmare of the Headless Man
to be the last word on the subject. But it was
becoming fainter and fainter as the violence of
the first impression dimmed, keeping pace with
the actuality of the dispersal of that khaki nation
that lay spread across France, Germany, Italy,
the Balkans, and the East. The Headless Man
was fading out.</p>
<p class="calibre3">It was mid-April, the first fine weather of the
year, when his own turn came. Of course the
Mess gave him a little dinner, for although nothing
on earth, not even four years of War, could make
him a soldier, his length of service, varied experience,
and neat adaptability had made him invaluable;
again no one had ever found it possible to
quarrel with him; further, his preoccupation
with games had made him perhaps the most
sought-for person in the Brigade.</p>
<p class="calibre3">Had it not been for these reasons, there was
little else to which he had a farewell to say;<span id="Page_216" title="[216]"></span>
casualty, change, and now demobilization removed
friends, then chance acquaintances, until there
was no one with whom he was in the slightest
degree intimate. He might almost have been
some attached officer staying in the Mess, instead
of its President, for all he knew of the officers
composing it. There was nothing in the village
that lay on the edge of the battlefield that he
wanted to see again. It was not a place where
he had trained or fought, it was not even the
place at which the news of the Armistice had
reached him. It was just a place where the
Brigade of which his battalion had formed a
part had been dumped, so as to be out of the
way, but sufficiently within reach of rail, for
the gradual attrition of demobilization to work
smoothly. An unkind person might have wondered
if the mild festival that took place in the
estaminet of that obscure commune was not so
much a farewell dinner to old Dormer, as an
eagerly sought opportunity for a little extra food
and drink that might help to pass the empty
days. Slightly bleary-eyed in the morning,
Dormer boarded the train, waved his hand to
the little group of officers on the platform, and
sat down to smoke until he might arrive at
Dunkirk.</p>
<p class="calibre3">On a mild April evening, he paced the port
side of the deck of the steamer that was taking
him home. He was aware that he might have
to spend a night in dispersal station, but it did<span id="Page_217" title="[217]"></span>
not matter in the least. The real end of the
business to such an essential Englishman as
Dormer was here and now, watching the calm
leaden sea-space widen between him and the
pier-head of Calais. Prophets might talk about
the obliteration of England’s island defences,
but the sentiment that the Channel evoked was
untouched. After years of effort and sacrifice,
Dormer remained a stranger in France. He
might know parts of it tolerably well, speak its
language fairly, fight beside its soldiers, could
feel a good deal of intelligent admiration for
its people and institutions, but nothing would
ever make him French. It would perhaps have
been easier to assimilate him into Germany. But
on the whole, in spite of his unprovocative manner,
he was difficult to assimilate, a marked national
type. Lengthier developments and slower, more
permanent revolutions were in his inherited
mental make-up, than in that of any of the other
belligerents. In a Europe where such thrones
as were left were tottering and crashing, nothing
violent was in his mind, or in the minds of ninety
per cent of those men who covered the lower
deck, singing together, with precisely the same
lugubrious humour, as in the days of defeat, of
stalemate, or of victory:</p>
<div class="poetry-container">
<div class="poetry">
<div class="first">“Old soldiers never die,</div>
<div class="verse">They only fade away.”</div>
</div>
</div>
<p class="calibre3">He turned to look at them, packed like sardines,<span id="Page_218" title="[218]"></span>
so that even the sea breeze could hardly dissipate
the clouds of cigarette smoke, just as no disaster
and no triumph could alter their island characteristics,
however much talk there might be about
town life sapping the race. As he looked at
them, herded and stalled like animals, but cheerful
in their queer way as no animal can ever be, he
remembered that somewhere among all those
thousands that were being poured back into
England day by day (unless of course he were
buried in one of those graveyards that marked
so clearly the hundred miles from Ypres to St.
Quentin) was a private soldier, whom he had
been told to discover and bring to justice for
the Crime at Vanderlynden’s, as Kavanagh had
called it. He had never even got the fellow’s
name and number, and he did not care. He
never wanted the job, nothing but his punctilious
New Army spirit, that had made him take the
War as seriously as if it had been business, had
kept him at it. Now he had done with it, the
man would never be found. But in Dormer’s
mind would always remain that phantom that
he had pursued for so many months—years even,
over all those miles, in and out of so many units
and formations. It had come to stand for all
that mass whose minds were as drab as their
uniform, so inarticulate, so decent and likable
in their humility and good temper. Theirs was
the true Republicanism, and no written constitution
could add anything to it. He had not<span id="Page_219" title="[219]"></span>
thought of that affair, during all these last months
that had seen so many Empires fall, so many
nations set upon their feet, but he thought of it
now.</p>
<p class="calibre3">He turned once again and surveyed that coastline,
somewhere behind which he had made that
pilgrimage; there it lay, newly freed Belgium
on the left, on the right the chalky downs that
ran from Gris-Nez far out of sight, down to
Arras. Between the two, on those marshes so
like any of South-Eastern England, had taken
place that Crime at Vanderlynden’s, that typified
the whole War. There, on those flat valleys of
the Yser and the Lys, the English army had come
to rest after its first few weeks of romantic march
and counter-march. There had the long struggle
of endurance been the longest and least spectacular.
It was there that the English Effort, as they
called it, had played its real part, far more than
on the greater battlefields farther south, or away
on other continents. The Crime at Vanderlynden’s
showed the whole thing in miniature.
The English had been welcomed as Allies,
resented as intruders, but never had they become
homogeneous with the soil and its natives, nor
could they ever leave any lasting mark on the
body or spirit of the place. They were still
incomprehensible to Vanderlynden’s, and Vanderlynden’s
to them. Dormer was of all men
most unwilling and perhaps unable to seek for
ultimate results of the phenomena that passed<span id="Page_220" title="[220]"></span>
before his eyes. To him, at that moment, it
seemed that the English Effort was fading out,
leaving nothing but graveyards. And when he
found this moving him, his horror of the expression
of any emotion asserted itself, and he elbowed
his way down the companion, to get a drink.</p>
<p class="calibre3">When he came up again, that low shore had
passed out of sight, but ahead was visible the
moderately white cliffs of England, beyond which
lay his occupation and his home, his true mental
environment, and native aspiration. He experienced
now in all its fullness the feeling that had
been with him with such tragic brevity from time
to time during those years. This last passage
of the Channel was, this time, real escape. The
Crime at Vanderlynden’s was behind him. He
had got away from it at last.</p>
<p>THE END.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0002.vue?3943f58d